/**
 * English: the source dictionary. Swedish (sv.ts) is typed against it. The firm's client is "client"; the client's
 * own customer is "customer" (design 12, appendix B).
 */

export const en = {
  // ── The application ────────────────────────────────────────────────────────────────────────────────────────
  'app.name': 'Olivia',
  'app.tagline': 'the outsourcing department’s workspace',
  'app.fictional': 'fictional',

  'common.loading': 'Loading…',
  'common.save': 'Save',
  'common.saved': 'Saved.',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.none': 'None',
  'common.reason': 'Reason',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.error': 'Something went wrong: {message}',
  'common.unreachable': 'The Olivia server could not be reached. Check that it is running, and try again.',
  'common.notFound.title': 'Not found',
  'common.notFound.body': 'There is nothing here, or you have no access to it. Both look the same, on purpose.',
  'common.showingOf': 'showing {n} of {total}',
  'later.title': 'Not built yet',
  'later.body': 'This part of Olivia is not built yet. Until it is, this page shows nothing, rather than something invented.',
  'later.badge': 'later',
  'later.group': 'Coming later ({n})',
  'common.retry': 'Try again',


  // ── Errors from the server (errors.<CODE>) ─────────────────────────────────────────────────────────────────
  'errors.INTERNAL': 'Something went wrong. Try again, and tell your firm if it keeps happening.',
  'errors.UNAVAILABLE': 'Olivia could not check that just now. Try again in a moment.',
  'errors.SIGN_IN': 'Sign in first.',
  'errors.SESSION_ENDED': 'Your session has ended. Sign in again.',
  'errors.SIGN_IN_INPUT': 'Enter your email address and password.',
  'errors.SIGN_IN_FAILED': 'That email address and password do not match an account.',
  'errors.LOCKED': 'Too many failed sign-ins for this account from your network. Try again in 15 minutes.',
  'errors.TOO_MANY': 'Too many attempts from this address. Try again later.',
  'errors.SAME_ORIGIN': 'The request was refused because it did not come from this site.',
  'errors.FORBIDDEN': 'You may not do this.',
  'errors.NOT_FOUND': 'Not found.',
  'errors.CONFLICT': 'This exists already.',
  'errors.INVALID': 'That is not valid.',
  'errors.READ_ONLY': 'The portfolio is read-only. Open a client to change something.',
  'errors.LINK_INVALID': 'This link is not valid, or it has expired or been used.',
  'errors.PASSWORD_MISSING': 'Enter a password.',
  'errors.PASSWORD_SHORT': 'Use a password of at least 12 characters.',
  'errors.PASSWORD_LONG': 'Use a password of at most 72 bytes (about 72 letters).',
  'errors.DAILY_SPEND_CAP': 'Today’s AI budget is used up, or too little of it is left. It resets at midnight UTC.',
  'errors.MONTHLY_SPEND_CAP': 'This client’s monthly AI budget is used up, or too little of it is left.',

  // ── The shell ──────────────────────────────────────────────────────────────────────────────────────────────
  'shell.openMenu': 'Open the menu',
  'shell.closeMenu': 'Close the menu',
  'shell.mainNav': 'Main navigation',
  'shell.mode.label': 'Work or Expert',
  'shell.mode.work': 'Work',
  'shell.mode.expert': 'Expert',
  'shell.language': 'Language',
  'shell.signedInAs': 'Signed in as {name}',
  'shell.signOut': 'Sign out',
  'shell.about': 'About Olivia',
  'shell.privacy': 'Privacy notice',
  'shell.method': 'How Olivia decides',
  'shell.skip': 'Skip to the page',

  'switcher.label': 'Client',
  'switcher.choose': 'Choose a client',
  'switcher.search': 'Search clients…',
  'switcher.all': 'All clients (portfolio)',
  'switcher.recent': 'Recent',
  'switcher.allOfYours': 'All {n} of your clients',
  'switcher.allOfYours.one': 'Your {n} client',
  'switcher.none': 'You are not a member of any client yet.',
  'switcher.noMatch': 'No client matches.',

  // ── Navigation (nav/nav-config.ts) ─────────────────────────────────────────────────────────────────────────
  'nav.section.waiting': 'Waiting on you',
  'nav.section.clients': 'Your clients',
  'nav.section.firm': 'The firm',
  'nav.section.client': 'This client',
  'nav.section.registers': 'Registers',
  'nav.section.records': 'Records',
  'nav.section.settings': 'Profile and access',

  'nav.myWork': 'My work, all clients',
  'nav.myWork.answers': 'What is waiting for me in every client?',
  'nav.portfolio': 'Portfolio',
  'nav.portfolio.answers': 'Where do I start today?',
  'nav.cycleAll': 'Annual cycle, all clients',
  'nav.cycleAll.answers': 'What is due across my clients?',
  'nav.changesAll': 'Regulatory changes',
  'nav.changesAll.answers': 'Which new rules reach which clients?',
  'nav.deliverablesAll': 'Deliverables to review',
  'nav.deliverablesAll.answers': 'Which reports wait for my review?',
  'nav.people': 'People',
  'nav.people.answers': 'Who works here, and with which roles?',
  'nav.clients': 'Clients',
  'nav.clients.answers': 'Which clients does the firm have, and who leads them?',
  'nav.library': 'Library',
  'nav.library.answers': 'Which templates and service kinds does the firm use?',
  'nav.settings': 'Settings',
  'nav.settings.answers': 'How is this installation set up?',
  'nav.spend': 'AI spend',
  'nav.spend.answers': 'What does the firm spend on AI?',
  'nav.about': 'About',
  'nav.about.answers': 'What is Olivia, and who made it?',
  'nav.method': 'How Olivia decides',
  'nav.method.answers': 'By which rules does Olivia decide, and how can I check them?',

  'nav.work.week': 'Your week and month',
  'nav.work.week.answers': 'What matters for this client now?',
  'nav.work.happened': 'What happened',
  'nav.work.happened.answers': 'What changed here lately?',
  'nav.work.forYou': 'For you',
  'nav.work.forYou.answers': 'What does it mean for my work?',
  'nav.work.forClient': 'For the client',
  'nav.work.forClient.answers': 'What does it mean for the client?',
  'nav.work.compared': 'Compared with today',
  'nav.work.compared.answers': 'How do new rules differ from what applies now?',
  'nav.work.deadlines': 'Deadlines',
  'nav.work.deadlines.answers': 'What is due, and when?',
  'nav.work.todo': 'To do',
  'nav.work.todo.answers': 'What do we need to do?',

  'nav.overview': 'Overview',
  'nav.overview.answers': 'How is this client doing?',
  'nav.services': 'Services',
  'nav.services.answers': 'What does the firm do for this client, and who does what?',
  'nav.cycle': 'Annual cycle',
  'nav.cycle.answers': 'What is this client’s year?',
  'nav.changes': 'Regulatory changes',
  'nav.changes.answers': 'Which new rules is this client working through?',
  'nav.actions': 'Actions',
  'nav.actions.answers': 'Which findings are open, and who owns them?',
  'nav.moreRegisters': 'More registers',
  'nav.moreRegisters.answers': 'Incidents, indicators, policies, training and the rest.',
  'nav.risk': 'Risk assessment',
  'nav.risk.answers': 'What are this client’s risks and where are they against appetite?',
  'nav.data': 'Data in',
  'nav.data.answers': 'Which figures has the client sent, and are they promoted?',
  'nav.documents': 'Documents',
  'nav.documents.answers': 'Which files belong to this client?',
  'nav.deliverables': 'Deliverables',
  'nav.deliverables.answers': 'Which reports went, or will go, to the client?',
  'nav.aiDrafts': 'AI drafts',
  'nav.aiDrafts.answers': 'Which drafts did a model help with?',
  'nav.profile': 'Client profile',
  'nav.profile.answers': 'Who is this client and what is its perimeter?',
  'nav.members': 'Members and AI',
  'nav.members.answers': 'Who may see this client, and may its data go to a model?',
  'nav.customerData': 'Customer data (basis)',
  'nav.customerData.answers': 'On what basis may the firm hold this client’s customer records?',
  'nav.audit': 'Audit trail',
  'nav.audit.answers': 'Who did what here, and does the record verify?',

  // ── Roles ──────────────────────────────────────────────────────────────────────────────────────────────────
  'role.lead': 'lead',
  'role.reviewer': 'reviewer',
  'role.analyst': 'analyst',
  'role.reader': 'reader',
  'role.customer_data': 'customer-data role',
  'firmRole.firm_admin': 'firm administrator',
  'firmRole.regulatory_desk': 'regulatory desk',
  'firmRole.partner': 'partner',
  'firmRole.consultant': 'consultant',
  'firmRole.auditor': 'firm auditor',
  'status.onboarding': 'onboarding',
  'status.active': 'active',
  'status.paused': 'paused',
  'status.offboarding': 'offboarding',
  'status.closed': 'closed',
  'status.purged': 'purged',

  // ── Signing in, invitations and resets ─────────────────────────────────────────────────────────────────────
  'signIn.title': 'Sign in',
  'signIn.email': 'Email',
  'signIn.password': 'Password',
  'signIn.submit': 'Sign in',
  'signIn.busy': 'Signing in…',
  'signIn.invited': 'Invited? Open the link you were sent.',
  'signIn.forgot': 'Forgot your password? Ask a firm administrator. A reset needs two administrators, and you are told who.',
  'signIn.picture': 'Olivia, the outsourcing department’s guide',

  'invite.title': 'You are invited to Olivia',
  'invite.by': 'Invited by {name}.',
  'invite.firmRoles': 'Firm roles: {roles}.',
  'invite.grants': 'In clients:',
  'invite.grant': '{role} at {client}',
  'invite.expires': 'The link expires {date}.',
  'invite.name': 'Your name',
  'invite.password': 'Password',
  'invite.passwordHint': '12 characters or more',
  'invite.terms': 'I accept the terms and the privacy notice',
  'invite.create': 'Create my account',
  'invite.resetTitle': 'Choose a new password',
  'invite.resetFor': 'For {email}.',
  'invite.resetSubmit': 'Set my password',
  'invite.noToken': 'This page opens from an invitation or reset link.',
  'invite.checking': 'Checking the link…',

  'resetNotice.title': 'Your password was reset',
  'resetNotice.body': 'Your password was reset on {date} at the request of {requester}, confirmed by {confirmer}. All your earlier sessions were ended. If you did not ask for this, tell your firm.',
  'resetNotice.host': 'Your password was reset on {date} by the host operator. All your earlier sessions were ended. If you did not ask for this, tell your firm.',
  'resetNotice.ack': 'I have read this',

  // ── The portfolio home: the four questions across all clients (design 10.3) ────────────────────────────────
  'portfolio.title': 'Portfolio',
  'portfolio.summary': '{n} clients you work on',
  'portfolio.summary.one': '{n} client you work on',
  'portfolio.q1': 'What needs you',
  'portfolio.q2': 'What has changed',
  'portfolio.q3': 'Is the work on track',
  'portfolio.q4': 'What Olivia holds',
  'portfolio.q4.clients': '{n} clients you are a member of.',
  'portfolio.q4.clients.one': '{n} client you are a member of.',
  'portfolio.q4.spend': 'Your AI spend today: {usd}.',
  'portfolio.q4.spendFirm': 'The firm’s AI spend today: {usd} (totals only, never per client).',
  'portfolio.index': 'Where everything is',
  'portfolio.noClients': 'You are not a member of any client yet. A client’s lead, or a partner creating one, adds you.',

  // ── A client ───────────────────────────────────────────────────────────────────────────────────────────────
  'client.youHere': 'You here: {roles}',
  'client.work.title': 'Your week and month',
  'client.work.q1': 'What happened',
  'client.work.q2': 'What it means for you',
  'client.work.q3': 'What it means for the client',
  'client.work.q4': 'Compared with what applies today',
  'client.work.q5': 'Deadlines',
  'client.work.q6': 'What we need to do',

  'members.title': 'Members and AI',
  'members.list': 'Members',
  'members.person': 'Person',
  'members.role': 'Role',
  'members.grantedBy': 'given by {name}',
  'members.choosePerson': 'Choose a person',
  'members.submit': 'Give the role',
  'members.revoke': 'Revoke',
  'members.revokeReason': 'Why is the role revoked?',
  'members.leadOnly': 'Only a lead of this client gives and revokes roles here, never to themselves.',
  'members.invite.title': 'Invite someone new to this client',
  'members.invite.note': 'A lead invites into their own client, and a firm administrator or another lead of this client confirms it: the link is shown once, to whoever confirms, to pass on.',
  'members.customerDataNote': 'The customer-data role needs a confirmed customer-data basis for this client, and only a lead gives it.',

  'ai.title': 'AI for client data',
  'ai.off': 'Off: this client’s data goes to no model.',
  'ai.on': 'On: this client’s data goes to Claude through the Anthropic API.',
  'ai.decided': 'Decided by {name} on {date}.',
  'ai.basis': 'Basis (for example, the agreement and clause that allow it)',
  'ai.reviewOn': 'Review by',
  'ai.reviewPassed': 'The review date ({date}) has passed: no client data goes to AI until a lead reviews the allowance and sets a new date.',
  'ai.cap': 'Monthly cap in US dollars (optional)',
  'ai.switchOn': 'Switch on',
  'ai.switchOff': 'Switch off',
  'ai.ceilingOff': 'This installation sends client data to no model, so it cannot be switched on here.',
  'ai.leadOnly': 'Only a lead of this client decides this.',
  'ai.month': 'This month: {usd}.',
  'ai.contact': 'Authorised by, at the client',
  'ai.contactNone': 'Choose a contact',
  'ai.document': 'The authorisation document',
  'ai.documentNone': 'Choose a document',
  'ai.location': 'Processing location',
  'ai.transfer': 'Transfer basis',
  'ai.waiting': 'Waiting for a second member of this client to confirm. Until then no client data goes to AI.',
  'ai.confirm': 'Confirm',
  'ai.confirmed': 'Confirmed by {name} on {date}.',
  'ai.authorised': 'Authorised by {name} at the client ({document}). Processed in: {location}. Transfer basis: {transfer}.',
  'ai.secondMember': 'A second member of this client, a lead or a reviewer who did not decide it, confirms it. Any later change waits for a confirmation again.',
  'spend.month': 'Month: {month} (UTC)',
  'spend.yourTitle': 'Your spend',
  'spend.youToday': 'You today: {usd}',
  'spend.youMonth': 'You this month: {usd}',
  'spend.yourCap': 'Your daily cap: {usd}',
  'spend.firmTitle': 'The firm',
  'spend.firmToday': 'Firm today: {usd}',
  'spend.firmMonth': 'Firm this month: {usd}',
  'spend.byEngine': 'This month by engine: Anthropic {anthropic} · OpenRouter {openrouter} (public text only)',
  'spend.firmCap': 'Firm daily cap: {usd}',
  'spend.firmOnly': 'Firm totals are shown to partners and firm administrators only, never per client.',
  'ai.monthOfCap': 'This month: {usd} of {cap}.',
  'ai.customer': 'Customer-level data needs, in addition, the installation’s own ceiling, the client’s confirmed basis and the customer-data role. This installation sends it nowhere.',

  'audit.title': 'Audit trail',
  'audit.verified': 'The chain verifies.',
  'audit.broken': 'The chain breaks at position {at}.',
  'audit.verifyNow': 'Ask the worker to verify now',
  'audit.queued': 'Queued for the worker.',
  'audit.alreadyQueued': 'Already queued this hour.',
  'audit.at': 'When',
  'audit.actor': 'Who',
  'audit.action': 'What',
  'audit.record': 'Record',
  'audit.systemActor': 'the system',
  'audit.empty': 'Nothing recorded yet.',
  'audit.latest': 'The latest {n} entries.',

  // ── The firm ───────────────────────────────────────────────────────────────────────────────────────────────
  'people.title': 'People',
  'people.name': 'Name',
  'people.email': 'Email',
  'people.firmRoles': 'Firm roles',
  'people.clients': 'Clients (roles)',
  'people.disabled': 'switched off',
  'people.requestReset': 'Request a password reset',
  'people.resetNote': 'A reset needs a second firm administrator to confirm it. All the person’s sessions then end, and they are told at their next sign-in.',
  'people.resetRequested': 'Requested. It waits for a second administrator.',

  'invites.title': 'Invite a person',
  'invites.email': 'Email',
  'invites.name': 'Name (they may change it)',
  'invites.firmRoles': 'Firm roles',
  'invites.grants': 'Roles in clients',
  'invites.addGrant': 'Add a client role',
  'invites.removeGrant': 'Remove',
  'invites.client': 'Client',
  'invites.submit': 'Create the invitation',
  'invites.link': 'Give this link to the person yourself. It is shown once and expires {date}:',
  'invites.waits': 'Every invitation waits for a second person before any link exists: a second firm administrator, or, for roles in clients only, a lead of each of those clients. The link is shown once, to whoever confirms it.',
  'invites.pending': 'Waiting',
  'invites.none': 'Nothing is waiting.',
  'invites.kind.invite': 'Invitation',
  'invites.kind.reset': 'Password reset',
  'invites.kind.grant': 'Client roles for an existing person',
  'invites.askedBy': 'asked by {name}',
  'invites.forYou': 'Waiting for your confirmation',
  'invites.forOther': 'Waiting for a second person',
  'invites.forNamed': 'Waiting for {names} to confirm it',
  'invites.forNobody': 'Nobody else may confirm it yet: the firm needs a second administrator, or the client another lead.',
  'invites.linkGiven': 'Link issued',
  'invites.confirm': 'Confirm',
  'invites.refuse': 'Refuse',
  'invites.refuseReason': 'Why is it refused?',
  'invites.confirmedLink': 'Confirmed. Give this link to the person yourself; it is shown once:',
  'invites.confirmedGrant': 'Confirmed. The roles are given.',

  'clients.title': 'Clients',
  'clients.note': 'Firm administrators see this list. A client’s content needs membership, and its customer data a further role.',
  'clients.code': 'Code',
  'clients.name': 'Name',
  'clients.status': 'Status',
  'clients.leads': 'Lead',
  'clients.members': 'Members',
  'clients.ai': 'AI for client data',
  'clients.customerData': 'Customer data',
  'clients.aiOn': 'on',
  'clients.aiOff': 'off',
  'clients.basisLive': 'basis live',
  'clients.noBasis': 'none',
  'clients.youMember': 'you are a member',
  'clients.new': 'New client',
  'clients.fictional': 'Fictional (demonstration data)',
  'clients.firstLead': 'First lead',
  'clients.create': 'Create the client',
  'clients.created': 'Created.',
  'clients.partnerNote': 'A partner who creates a client becomes its first lead. A firm administrator names another person.',

  'settings.title': 'Settings',
  'settings.firmName': 'Firm name',
  'settings.orgNumber': 'Organisation number',
  'settings.accent': 'Brand colour (#RRGGBB)',
  'settings.language': 'Default language',
  'settings.disclaimer': 'Export disclaimer',
  'settings.installation': 'Installation, set by the host operator',
  'settings.clientEngines': 'Client data may go to',
  'settings.customerEngines': 'Customer-level data may go to',
  'settings.idle': 'Sessions end after {n} idle minutes and {h} hours in all.',
  'settings.noModel': 'no model',
  'settings.claude': 'Claude (Anthropic API)',
  'settings.adminOnly': 'Only a firm administrator changes these.',

  // ── About and privacy (design 10.3) ────────────────────────────────────────────────────────────────────────
  'about.title': 'About Olivia',
  'about.body': 'Olivia is the workspace of an outsourcing department for compliance, risk and financial-crime prevention.',
  'about.createdBy': 'Created by Daniel Bardun.',
  'about.version': 'Version {version}',
  'about.builtOn': 'Built on ideas and code from ANTON, Horizon, ARIS, Gap Assessor and ALEXANDER (each copied file names its origin).',
  'about.developed': 'Developed by Daniel Bardun, following the ANTON and Alexander frameworks and knowledge setup.',
  'about.licensedUnder': 'Licensed under the',
  'about.licenceName': 'European Union Public Licence (EUPL) v1.2',
  'about.fonts': 'Fonts: Instrument Sans and Instrument Serif (SIL Open Font License).',
  'about.method': 'How Olivia decides: every rule, with a worked example that the tests run through the code.',
  // Other supporting licences (web/src/lib/supporting-licences.ts): a closed section directly under the credit and licence.
  'about.supporting.title': 'Other supporting licences',
  'about.supporting.lucide': 'Icons from Lucide',
  'about.supporting.isc': 'ISC licence',
  'about.supporting.version': 'Version {version}',
  'about.supporting.commit': 'Commit {commit}',
  'about.supporting.text': 'Licence text',
  // figure's aria-label; the read-out words are the small word in its corner, never data (keep's "kept N" is its own count).

  // ── The Method page (IP2b; the family synthesis §1.4 B2, PLATFORM-PARITY 1.0 §1.3, §4) ─────────────────────
  'method.title': 'How Olivia decides',
  'method.intro': 'Code computes every level, band, priority, group and date in Olivia from the rules below; a person decides, and a model only proposes. Each rule has a card with a worked example. The tests run every example through the code and compare the result, so a card cannot drift from what runs.',
  'method.contents': 'On this page',
  'method.rules': 'Rules',
  'method.area.engine': 'The maturity engine',
  'method.area.business': 'The business profile',
  'method.area.pictures': 'The heat map and our role',
  'method.area.findings': 'Findings and the plan',
  'method.card.decides': 'What it decides',
  'method.card.formula': 'How',
  'method.card.inputs': 'Inputs',
  'method.card.example': 'Worked example',
  'method.card.exampleRun': 'The tests run this example through the code and compare the result.',
  'method.card.exampleData': 'The example’s data',
  'method.card.case': 'Case {n}',
  'method.card.input': 'What goes in',
  'method.card.output': 'What the code returns',
  'method.card.sources': 'Sources',
  'method.card.code': 'Code',
  'method.card.sql': 'Its twin in the database: {fn}',
  'method.card.file': 'The rule file',
  'method.card.fileLine': '{path}, version {version}, SHA-256 {sha}',
  'method.card.showFile': 'Show the rule file',
  'method.card.status.baseline': 'Olivia’s proposal: usable for the firm’s internal work, not yet adopted by a regulatory desk.',
  'method.card.status.other': 'Status of the file: {status}',
  'method.card.noFile': 'This rule has no file of its own: its values are in the code.',
  'method.prompts': 'Prompts',
  'method.prompts.none': 'Olivia sends no prompt to any model in this version. When it does, each prompt will be a file in the repository, shown here with its SHA-256, and used for a real client only after two people of the firm have adopted it.',
  'method.prompts.files': 'Prompt files in this version: {list}',
  'method.never': 'What a model never does here',
  'method.never.1': 'A model never decides a number: levels, bands, priorities, due dates and reach come from these rules or from a named person.',
  'method.never.2': 'A model never sees more than one client in one prompt.',
  'method.never.3': 'Client data goes only to Claude, through the Anthropic API, and only when the installation and the client’s workspace both allow it.',
  'method.never.4': 'A public model (GLM, through OpenRouter) sees only public data: nothing said inside a client reaches it.',
  'method.never.5': 'A model’s proposal is never a decision: a person accepts or rejects it, with a checked quote where it rests on a document.',
  'method.later': 'Rules without a card yet',
  'method.later.body': 'The code also applies these rules. Their cards are not written yet; until they are, the code named beside each is where the rule is.',
  'method.computed': 'How was this computed?',
  'method.heatCell': 'Why this cell?',

  'privacy.title': 'Privacy notice',
  'privacy.draft': 'DRAFT, not yet reviewed by a lawyer.',
  'privacy.who.h': 'Who is responsible',
  'privacy.who': '[[FIRM NAME]] runs this Olivia installation. For client data it acts as processor for each client, which is the controller, under a data processing agreement. For staff accounts it is the controller.',
  'privacy.what.h': 'What is held',
  'privacy.what': 'Staff accounts (name, email, sign-in records); for each client, its profile, registers, documents and aggregate figures. Data about a client’s own customers is held only where that client has recorded a legal basis and purpose for it, only for the customers the firm’s work touched and only the details that work needs; identifying details are encrypted with a key of that client’s own, and every reading of them is logged. Nothing that reveals a suspicion report is held. Documents uploaded for a client may still contain personal data about its customers or staff, although each upload states whether it does; that statement is the uploader’s.',
  'privacy.where.h': 'Where it is',
  'privacy.where': '[[HOSTING]], in the EU. Backups: [[BACKUP LOCATION]].',
  'privacy.ai.h': 'AI',
  'privacy.ai': 'A client’s data, including its documents, goes to Anthropic (Claude, through its API) only where that client has allowed it; data about the client’s customers only where, in addition, the client’s recorded basis allows it. Only public regulatory material, never anything about a client, goes to OpenRouter, routed only to Inceptron with zero data retention and no data collection.',
  'privacy.retention.h': 'Retention',
  'privacy.retention': 'Data about a client’s customers: [[PERIOD, proposed 24 months after the work closes]], then deleted. Other client data: [[PERIOD]] after the client relationship ends, then deleted. Staff accounts: [[PERIOD]].',
  'privacy.rights.h': 'Your rights',
  'privacy.rights': 'If you are a customer of one of our clients, ask that client, which answers your request; we help it. Otherwise: [[CONTACT]].',

  // ── O1 screens (generated from one table, so both languages keep the same keys) ─────────────────────────
  'common.noCustomerNames': 'Do not name the client’s customers here. A customer belongs in the customer-level records, where it is protected.',
  'common.remove': 'Remove',
  'client.fictionalNote': 'A fictional client, for demonstration only.',
  'errors.CUSTOMER_LEVEL_NOT_YET': 'Customer-level documents are not accepted yet: the registers and the controls that protect them come later. This upload is refused, and nothing of it is kept.',
  'errors.TEMPLATE_NOT_APPLICABLE': 'Only a baseline or adopted template can be applied; a draft waits for the regulatory desk.',
  'errors.PERFORMER_NEEDED': 'Name the client contact who performs “{title}”.',
  'errors.FILE_TOO_LARGE': 'The file is too large: a file may be at most {mb} MB.',
  'errors.FILE_MISSING': 'The stored file is missing or no longer matches its record. Tell your firm.',
  'switcher.overdue': '{n} overdue',
  'switcher.overdue.one': '{n} overdue',
  'switcher.failed': 'Your clients could not be listed just now.',
  'nav.counter.needsYou': '{n} waiting for you',
  'nav.counter.actionsOpen': '{n} open actions',
  'nav.counter.actionsOpen.one': '{n} open action',
  'nav.counter.cycleOverdue': '{n} overdue cycle items',
  'nav.counter.cycleOverdue.one': '{n} overdue cycle item',
  'state.overdue': 'overdue',
  'state.unowned': 'unowned',
  'state.inReview': 'in review',
  'state.inProgress': 'in progress',
  'state.inPreparation': 'in preparation',
  'state.due': 'due',
  'state.done': 'done',
  'state.skipped': 'skipped',
  'state.cancelled': 'cancelled',
  'state.waiting': 'waiting on the client',
  'state.performerLeft': 'performer has left',
  'state.waitingOnLeft': 'waits on someone who has left',
  'contact.hasLeft': '{name} (has left)',
  'rule.yearly': 'yearly',
  'rule.everyNYears': 'every {n} years',
  'rule.twiceYearly': 'twice a year',
  'rule.quarterly': 'quarterly',
  'rule.monthly': 'monthly',
  'rule.everyNMonths': 'every {n} months',
  'rule.timesYear': '{n} times a year',
  'rule.weekly': 'weekly',
  'rule.daily': 'daily',
  'rule.other': 'by its own rule',
  'rule.lastWorkingDay': 'last weekday',
  'rule.nthWorkingDay': 'weekday {n}',
  'rule.onDate': '{day} {month}',
  'rule.dayOfMonth': 'on day {day} of the month',
  'anchor.at': 'at {anchor}',
  'anchor.after': '{n} days after {anchor}',
  'anchor.after.one': '{n} day after {anchor}',
  'anchor.before': '{n} days before {anchor}',
  'anchor.before.one': '{n} day before {anchor}',
  'anchor.financial_year_end': 'the financial year end',
  'anchor.agm': 'the annual general meeting',
  'anchor.licence_date': 'the licence date',
  'anchor.service_start': 'the start of the service',
  'anchor.calendar': 'the calendar',
  'roll.none': 'stays on its date',
  'roll.previous_business_day': 'moves to the weekday before',
  'roll.next_business_day': 'moves to the weekday after',
  'when.title': 'When it is due',
  'when.kind': 'When',
  'when.kind.fixed': 'On a date each year',
  'when.kind.anchored': 'Some days before or after a date of the client',
  'when.kind.month_day': 'On a day of chosen months',
  'when.kind.weekday': 'On a weekday of chosen months',
  'when.day': 'Day',
  'when.month': 'Month',
  'when.days': 'Days',
  'when.side': 'Before or after',
  'when.before': 'before',
  'when.on': 'on the day',
  'when.after': 'after',
  'when.anchor': 'The client’s date',
  'when.dayOfMonth': 'Day of the month (1–28)',
  'when.dayOfMonthHint': 'For the end of a month, choose its last weekday instead.',
  'when.position': 'Which weekday',
  'when.position.nth': 'weekday {n} of the month',
  'when.position.last': 'the last weekday of the month',
  'when.months': 'Months',
  'when.reads': 'Reads: {rule}.',
  'when.firstDates': 'First dates: {dates}',
  'yearEnd.title': 'Financial year end',
  'yearEnd.assumedShort': 'These dates rest on a financial year end of {date} that nobody has confirmed.',
  'yearEnd.assumedLine': '{date}: assumed, not confirmed',
  'yearEnd.confirmedLine': '{date}: confirmed by {name} on {on}',
  'yearEnd.confirm': 'Confirm {date} as the client’s year end',
  'yearEnd.confirmedTick': 'Confirmed: this is the client’s financial year end (from its articles of association or its annual report)',
  'yearEnd.seriesAssumed': 'These dates rest on a financial year end of {date} that nobody has confirmed.',
  'yearEnd.itemAssumed': 'This date rests on the client’s financial year end, which nobody has confirmed.',
  'yearEnd.confirmOnProfile': 'Confirm it on the profile',
  'yearEnd.changeOnProfile': 'Change it on the profile',
  'yearEnd.templateQuestion': 'The client’s financial year end is assumed to be {date}; nobody has confirmed it. {n} of the items ticked below are dated from it.',
  'yearEnd.templateQuestion.one': 'The client’s financial year end is assumed to be {date}; nobody has confirmed it. {n} of the items ticked below is dated from it.',
  'yearEnd.applyAssumed': 'Apply the dates on the assumed year end ({date}); they will say that it is not confirmed',
  'yearEnd.homeLine': 'The financial year end ({date}) is assumed, not confirmed, and dates of the annual cycle rest on it.',
  'cadence.regulation': 'Regulation',
  'cadence.market_practice': 'Market practice (public source)',
  'cadence.proposal': 'Olivia’s proposal',
  'cadence.advisense_stated': 'Market practice (public source)',
  'cadence.firm_practice': 'Olivia’s proposal',
  'cadence.mark.regulation': 'R',
  'cadence.mark.market_practice': 'M',
  'cadence.mark.proposal': 'P',
  'cadence.mark.advisense_stated': 'M',
  'cadence.mark.firm_practice': 'P',
  'cadence.custom': 'custom series',
  'cadence.legend': 'Where each cadence comes from: R regulation · M market practice (public source) · P Olivia’s proposal, until your regulatory desk adopts the template. The source is on the item.',
  'provenance.duty': 'Duty',
  'provenance.noDuty': 'No law is cited for this item.',
  'provenance.requirement': 'requirement {code}',
  'provenance.cadence': 'Cadence',
  'provenance.page': 'the source page, read {date}',
  'provenance.day': 'Day',
  'provenance.day.proposal': 'Olivia’s proposal; the lead moves it for this client',
  'provenance.day.regulation': 'Set by the rule itself',
  'provenance.notAdopted': 'Not yet adopted by your regulatory desk.',
  'provenance.adoptedBy': 'Adopted by {name}, {date}.',
  'provenance.more': 'Where it comes from',
  'provenance.englishOnly': 'This version of the template has this text in English only.',
  'line.first': 'first line',
  'line.second': 'second line',
  'line.third': 'third line',
  'line.management': 'management',
  'line.support': 'support',
  'responsibility.firm': 'The firm performs it',
  'responsibility.client_firm_reviews': 'Client performs, firm reviews',
  'responsibility.client_tracked': 'Client performs, tracked only',
  'delivery.outsourced': 'outsourced',
  'delivery.co_sourced': 'co-sourced',
  'delivery.interim': 'interim',
  'delivery.on_demand': 'on demand',
  'presence.home': 'home',
  'presence.subsidiary': 'subsidiary',
  'presence.branch': 'branch',
  'presence.cross_border': 'cross-border',
  'contactRole.board_chair': 'board chair',
  'contactRole.ceo': 'CEO',
  'contactRole.board_member': 'board member',
  'contactRole.aml_compliance_manager': 'AML compliance manager, member of the management body (AMLR Art. 11(1))',
  'contactRole.appointed_aml_officer': 'AML compliance officer (AMLR Art. 11(2))',
  'contactRole.compliance_officer': 'head of compliance (the compliance function)',
  'contactRole.risk_officer': 'risk officer',
  'contactRole.internal_audit_head': 'head of internal audit',
  'contactRole.dpo': 'data protection officer',
  'contactRole.security_officer': 'information security officer',
  'contactRole.cfo': 'CFO',
  'contactRole.data_owner': 'data owner',
  'contactRole.other': 'other',
  'activity.annual_plan': 'annual plan',
  'activity.annual_report': 'annual report',
  'activity.bwra_review': 'business-wide risk assessment review',
  'activity.risk_assessment_update': 'risk assessment update',
  'activity.policy_review': 'policy review',
  'activity.procedure_review': 'procedure review',
  'activity.board_report': 'board report',
  'activity.management_report': 'management report',
  'activity.monitoring_activity': 'monitoring and controls',
  'activity.kyc_refresh_sample': 'KYC sample check',
  'activity.screening_review': 'screening review',
  'activity.sanctions_review': 'sanctions review',
  'activity.training_board': 'board training',
  'activity.training_staff': 'staff training',
  'activity.training_plan': 'training plan',
  'activity.kri_report': 'risk indicator report',
  'activity.regulatory_return': 'regulatory return',
  'activity.regulatory_monitoring_report': 'regulatory-monitoring report',
  'activity.independent_audit': 'independent audit',
  'activity.appetite_review': 'risk appetite review',
  'activity.atlas_cycle': 'risk assessment cycle',
  'activity.third_party_follow_up': 'third-party follow-up',
  'activity.mock_inspection': 'mock inspection',
  'activity.capital_planning': 'capital planning',
  'activity.model_validation': 'model validation',
  'activity.board_papers': 'board papers',
  'activity.board_minutes': 'board minutes',
  'activity.audit_engagement': 'audit engagement',
  'activity.quality_assessment': 'quality assessment',
  'activity.privacy_review': 'privacy review',
  'activity.security_review': 'security review',
  'activity.other': 'other',
  'table.occurrence': 'cycle items',
  'table.series': 'series',
  'table.step': 'checklists',
  'table.action': 'actions',
  'table.document': 'documents',
  'table.documentLink': 'document links',
  'table.comment': 'comments',
  'table.profile': 'client profile',
  'table.institutionType': 'institution types',
  'table.jurisdiction': 'jurisdictions',
  'table.contact': 'contacts',
  'table.service': 'services',
  'table.basis': 'customer-data basis',
  'table.aiAllowance': 'AI allowance',
  'table.member': 'members',
  'severity.low': 'low',
  'severity.medium': 'medium',
  'severity.high': 'high',
  'severity.critical': 'critical',
  'priority.P1': 'P1',
  'priority.P2': 'P2',
  'priority.P3': 'P3',
  'priority.P4': 'P4',
  'heat.cell.A': 'at or above target',
  'heat.cell.B': '1 level below target',
  'heat.cell.C': '2 levels below target',
  'heat.cell.D': '3 or more levels below target',
  'heat.cell.not_determined': 'not determined: evidence to obtain',
  'heat.cell.not_assessed': 'not assessed: none of its practices is in the scope of this analysis',
  'heat.cell.not_applicable': 'not applicable to this client',
  'heat.marker.breach': 'possible breach',
  'heat.marker.partly': 'partly met',
  'heat.marker.capped': 'capped: level 4 needs an independent assessment',
  'source.monitoring': 'monitoring',
  'source.cycle_review': 'review in the annual cycle',
  'source.gap_assessment': 'gap assessment',
  'source.internal_audit': 'internal audit',
  'source.external_audit': 'external audit',
  'source.supervisor': 'supervisor',
  'source.mock_inspection': 'mock inspection',
  'source.incident': 'incident',
  'source.regulatory_change': 'regulatory change',
  'source.risk_assessment': 'risk assessment',
  'source.third_party_review': 'third-party review',
  'source.product_assessment': 'product assessment',
  'source.other': 'other',
  'actionStatus.open': 'open',
  'actionStatus.reported_done': 'reported done by the client',
  'actionStatus.in_progress': 'in progress',
  'actionStatus.remediated': 'remediated',
  'actionStatus.verified': 'verified',
  'actionStatus.closed': 'closed',
  'actionStatus.cancelled': 'cancelled',
  'moved.due_on': 'due date',
  'moved.status': 'status',
  'moved.owner_person_id': 'owner',
  'moved.owner_contact_id': 'owner at the client',
  'moved.severity': 'severity',
  'docKind.evidence': 'evidence',
  'docKind.summary': 'summary',
  'docKind.policy': 'policy',
  'docKind.report': 'report',
  'docKind.board_paper': 'board paper',
  'docKind.contract': 'contract',
  'docKind.instruction': 'instruction',
  'docKind.dpia': 'data protection impact assessment',
  'docKind.correspondence': 'correspondence',
  'docKind.other': 'other',
  'linkPurpose.evidence': 'evidence',
  'linkPurpose.source': 'source',
  'linkPurpose.output': 'output',
  'linkPurpose.attachment': 'attachment',
  'subject.occurrence': 'cycle item (CY-…)',
  'subject.action': 'action (ACT-…)',
  'subject.series': 'series (SER-…)',
  'subject.service': 'service (SRV-…)',
  'subject.basis': 'customer-data basis (CDB-…)',
  'subject.profile': 'client profile',
  'purpose.sample_check': 'sample checks',
  'purpose.kyc_review': 'KYC review',
  'purpose.screening_review': 'screening-hit review',
  'purpose.alert_review': 'alert review',
  'purpose.subject_request': 'data-subject requests',
  'category.identity': 'identity',
  'category.kyc_cdd': 'KYC/CDD',
  'category.screening': 'screening hits',
  'category.transaction': 'transaction excerpts',
  'field.customer_ref': 'customer reference',
  'field.kind': 'natural or legal person',
  'field.name': 'name',
  'field.national_id': 'identity or organisation number',
  'field.date_of_birth': 'date of birth',
  'field.address': 'address',
  'field.nationality': 'nationality',
  'field.id_document': 'identity-document number',
  'field.risk_class': 'risk class',
  'field.pep': 'PEP flag',
  'field.purpose_nature': 'purpose and nature of the relationship',
  'field.beneficial_owners': 'beneficial owners',
  'field.source_of_funds': 'source of funds or wealth',
  'field.cdd_dates': 'due-diligence dates',
  'field.next_review_on': 'next review date',
  'field.checks': 'checks made',
  'field.exceptions': 'exceptions',
  'field.note': 'note',
  'field.list_kind': 'list',
  'field.hit_detail': 'hit detail',
  'field.disposition': 'disposition',
  'field.offence_stage': 'stage (allegation to conviction)',
  'field.reasoning': 'reasoning',
  'field.date': 'date',
  'field.amount': 'amount',
  'field.currency': 'currency',
  'field.counterparty_name': 'counterparty name',
  'field.counterparty_account': 'counterparty account',
  'field.country': 'country',
  'needs.review': 'Review',
  'needs.prepare': 'Prepare',
  'needs.verify': 'Verify',
  'needs.act': 'Act',
  'needs.confirm_basis': 'Confirm a basis',
  'needs.follow_up': 'Follow up',
  'needs.close': 'Close',
  'needs.contact_left': 'Contact has left',
  'needs.follow_up_left': 'Nobody follows up',
  'needs.returned': 'Sent back',
  'needs.returnedBy': 'sent back by {name} on {date}: {note}',
  'needs.ownedBy': 'owned by {name} at the client',
  'needs.verifiedBy': 'verified by {name}',
  'needs.contactLeft': 'has left and is still named; name who takes over',
  'needs.followUpLeft': '{name} no longer works on this client; name who follows it up',
  'needs.followUpNobody': 'nobody is named to follow it up; name who does',
  'needs.nameSuccessor': 'Contacts',
  'needs.confirm_skip': 'Confirm a skip',
  'needs.confirm_request': 'Confirm',
  'needs.skipAskedBy': 'skip asked by {name}: {reason}',
  'needs.requestAskedBy': '{kind}, asked by {name} on {date}',
  'needs.toConfirm': 'To confirm',
  'needs.due': 'due {date}',
  'needs.preparedBy': 'prepared by {name}',
  'needs.remediatedBy': 'remediated by {name}',
  'needs.recordedBy': 'recorded by {name}',
  'help.review': 'Could review',
  'help.verify': 'Could verify',
  'help.take': 'Nobody named',
  'help.close': 'Could close',
  'help.follow_up_left': 'Nobody follows up',
  'portfolio.allWork': 'All your work →',
  'portfolio.q1.none': 'Nothing waits for you in any of your clients.',
  'portfolio.q1.showing': 'showing {n} of {total}, overdue first',
  'portfolio.q1.counts': 'Counted: reviews you may do, items you prepare once their preparation has started, remediations you may verify, your actions due within 30 days, and customer-data bases waiting for a second person.',
  'portfolio.q2.changes': 'Changes by people',
  'portfolio.q2.actions': 'Actions opened',
  'portfolio.q2.done': 'Cycle items done',
  'portfolio.q2.documents': 'Documents added',
  'portfolio.q2.note': 'Read from each client’s own audit chain; what the worker made by itself is not counted. Incidents and indicator breaches, and new regulation reaching each client, come later.',
  'portfolio.q3.items': 'Cycle items',
  'portfolio.q3.highOpen': 'High or critical open',
  'portfolio.q3.actionsOverdue': 'Actions overdue',
  'portfolio.q3.ofOpen': '{n} of {total}',
  'portfolio.q3.since': 'oldest since {date}',
  'portfolio.q3.note': 'Cycle items due in the window, plus every overdue one. Overdue, unowned and waiting are worked out from the records each time, never stored; an overdue item that waits on the client counts as both. Waiting on the client counts the cycle items and the actions that wait, so it can be higher than the cycle page shows.',
  'portfolio.q3.policies': 'Lapsed policies come with the policy register.',
  'portfolio.q4.records': 'Active series: {series} · open cycle items: {items} · open actions: {actions} · documents: {documents}.',
  'portfolio.q4.templates': 'Templates: {baseline} Olivia’s proposals not yet adopted, {adopted} adopted by your regulatory desk · service kinds: {kinds}.',
  'portfolio.q4.worker': 'The worker was last seen {at}.',
  'portfolio.q4.noWorker': 'The worker has not been seen: nothing new is dated until it runs.',
  'portfolio.q4.chains': 'Audit chains verified: {verified} of {checked} checked, of {of} clients (last {at}).',
  'portfolio.q4.chainsNone': 'No audit chain has been checked yet (of {of} clients); the worker checks every night.',
  'portfolio.q4.corpus': 'The regulatory corpus, its readings and what reaches each client.',
  'portfolio.q4.radar': 'The Radar and how fresh its sources are.',
  'cycleAll.subtitle': 'All {n} of your clients',
  'cycleAll.subtitle.one': 'Your {n} client',
  'cycleAll.allClients': 'All clients',
  'cycleAll.function': 'Function',
  'cycleAll.allFunctions': 'All functions',
  'cycleAll.laterViews': 'The year view across clients and the people view (capacity per week) come later.',
  'home.window': 'Period',
  'home.deadlineWindow': 'How far ahead',
  'home.last7': 'Last 7 days',
  'home.last30': 'Last 30 days',
  'home.days': '{n} days',
  'home.all': 'All',
  'home.more': 'More',
  'home.owner': 'owner: {name}',
  'home.ownerContact': '{name} (client contact)',
  'home.forYou.phase': 'What new rules and Radar items mean for your work functions comes with the Radar, which is not built yet.',
  'home.forClient.phase': 'Reach, readiness and health, computed by rule and never by AI, come with the regulatory corpus, which is not built yet.',
  'home.compared.phase': 'How coming rules differ from what applies today comes with the regulatory corpus, which is not built yet.',
  'home.happened.register': 'In the registers',
  'home.happened.none': 'No changes by people in this period.',
  'home.happened.changes': '{n} changes by people:',
  'home.happened.changes.one': '{n} change by people:',
  'home.happened.byPeople': 'Read from this client’s audit chain; what the worker dated by itself is not counted.',
  'home.happened.audit': 'The whole audit trail →',
  'home.happened.outside': 'New regulation and Radar items that reach this client.',
  'home.happened.data': 'Data the client sent, and its promotion.',
  'home.deadlines.cycle': 'Annual cycle',
  'home.deadlines.actions': 'Actions',
  'home.deadlines.noCycle': 'No open cycle item is due within {n} days, and none is overdue.',
  'home.deadlines.noActions': 'No open action is due within {n} days.',
  'home.deadlines.regulatory': 'Regulatory dates that reach this client, with their precision.',
  'home.todo.yours': 'Yours',
  'home.todo.yoursNone': 'Nothing is yours to do here now.',
  'home.todo.couldHelp': 'You could help',
  'home.todo.couldHelpNone': 'Nothing here waits for a second person you could be.',
  'home.todo.otherThan': 'needs someone other than {name}',
  'home.todo.waiting': 'Waiting on the client',
  'home.todo.waitingNone': 'Nothing is waiting on the client.',
  'home.todo.requests': 'The requests register (what the firm has asked the client for) comes later.',
  'home.todo.absences': 'Recorded absences',
  'home.todo.absencesNone': 'Nothing lapsed, and every service has its cycle.',
  'home.todo.lapsed': 'lapsed',
  'home.todo.contactLeft': 'Contact has left',
  'home.todo.contactLeftLine': '{name} has left and is still named in {n} places',
  'home.todo.contactLeftLine.one': '{name} has left and is still named in {n} place',
  'home.todo.yearEndAssumed': 'Year end assumed',
  'home.todo.serviceWithoutCycle': '{service}: no cycle items yet',
  'overview.onboarding': 'This client is still being set up.',
  'overview.continueSetUp': 'Continue the set-up →',
  'overview.outsideAppetite': 'Outside appetite',
  'overview.outsideAppetite.phase': 'Threat paths and residual risk come with the risk assessment, which is not built yet.',
  'overview.overdue': 'Overdue',
  'overview.overdueNone': 'Nothing is overdue.',
  'overview.overdueCounts': 'cycle items: {cycle} · actions: {actions}',
  'overview.openCycle': 'Open the cycle',
  'overview.openActions': 'Open the actions',
  'overview.nextThree': 'Next three things',
  'overview.nextNone': 'No open cycle item is dated yet.',
  'overview.health': 'Health',
  'overview.health.phase': 'The health strip (governance, risk assessment, monitoring, training and the rest) comes with the other registers.',
  'overview.services': 'Services',
  'overview.servicesAll': 'Services and who does what →',
  'overview.noServices': 'No service is recorded yet, so no cycle runs.',
  'overview.serviceNext': 'Next: {title}, {date}',
  'overview.serviceNoItems': 'No open cycle item.',
  'overview.serviceOverdue': '{n} overdue',
  'overview.serviceOverdue.one': '{n} overdue',
  'overview.waiting': 'Waiting on the client',
  'overview.waitingLine': '{n} items since {date} ({who})',
  'overview.waitingLine.one': '{n} item since {date} ({who})',
  'overview.more': 'Further',
  'overview.upcoming': '{n} cycle items in the next 90 days',
  'overview.upcoming.one': '{n} cycle item in the next 90 days',
  'overview.recentChanges': 'Recent changes',
  'overview.drift': 'Open drift triggers',
  'cycle.addSeries': 'Add a series',
  'cycle.applyTemplate': 'Apply a template',
  'cycle.template': 'Template: {name} (version {version})',
  'cycle.noTemplate': 'No template is applied to this client yet.',
  'cycle.leadOnly': 'Only a lead of this client changes its annual cycle.',
  'cycle.view.label': 'View',
  'cycle.view.list': 'List',
  'cycle.view.month': 'Month',
  'cycle.view.year': 'Year',
  'cycle.filter.window': 'Due within',
  'cycle.filter.windowAll': 'All dated items',
  'cycle.filter.state': 'State',
  'cycle.filter.state.open': 'Open',
  'cycle.filter.state.overdue': 'Overdue',
  'cycle.filter.state.unowned': 'Unowned',
  'cycle.filter.state.waiting': 'Waiting on the client',
  'cycle.filter.state.closed': 'Done or skipped',
  'cycle.filter.state.all': 'All',
  'cycle.filter.person': 'Person',
  'cycle.filter.everyone': 'Everyone',
  'cycle.filter.me': 'Me',
  'cycle.filter.service': 'Service',
  'cycle.filter.allServices': 'All services',
  'cycle.counts': 'Overdue: {overdue} · unowned: {unowned} · waiting on the client: {waiting} ·',
  'cycle.countsOfShown': '(counted among those shown)',
  'cycle.empty': 'No cycle item matches this view.',
  'cycle.noSeries': 'No series yet: apply a template or add a series.',
  'cycle.overdueFirst': 'Overdue ({n}), shown first',
  'cycle.week': 'Week {week} ({from} – {to})',
  'cycle.thisWeek': 'this week',
  'cycle.col.due': 'Due',
  'cycle.col.client': 'Client',
  'cycle.col.code': 'Code',
  'cycle.col.item': 'Item',
  'cycle.col.who': 'Who does it',
  'cycle.col.state': 'State',
  'cycle.col.series': 'Series',
  'cycle.col.source': 'Source',
  'cycle.col.next': 'Next',
  'cycle.nobodyNamed': 'nobody named',
  'cycle.who.tracked': 'the client ({performer}), tracked only',
  'cycle.who.clientReviews': 'the client ({performer}) · {reviewer} reviews',
  'cycle.waitingSince': 'since {date} ({who})',
  'cycle.movedFrom': 'moved from {date}',
  'cycle.noneNext': 'none planned',
  'cycle.prevMonth': 'Previous month',
  'cycle.nextMonth': 'Next month',
  'cycle.inMonth': '{n} items this month',
  'cycle.inMonth.one': '{n} item this month',
  'cycle.markerLegend': 'Markers',
  'cycle.markerLegendNote': 'The state is also written on each item.',
  'occurrence.due': 'due {date} · preparation from {from}',
  'occurrence.series': 'Series',
  'occurrence.service': 'Service',
  'occurrence.kind': 'Kind',
  'occurrence.who': 'Who does it',
  'occurrence.responsibility': 'Who performs it',
  'occurrence.cadence': 'Where the cadence comes from',
  'occurrence.review': 'Review',
  'occurrence.reviewRequired': 'A second person reviews it',
  'occurrence.reviewNone': 'No review needed',
  'occurrence.evidence': 'Evidence',
  'occurrence.evidenceRequired': 'Evidence must be linked before it is done',
  'occurrence.evidenceNone': 'No evidence required',
  'occurrence.byOn': '{name}, {date}',
  'occurrence.skippedReason': 'Skipped because',
  'occurrence.acts': 'What can be done now',
  'occurrence.start': 'Start',
  'occurrence.started': 'Started.',
  'occurrence.prepare': 'Mark as prepared',
  'occurrence.recordDelivery': 'Record what the client delivered',
  'occurrence.prepareNote': 'Note (optional)',
  'occurrence.prepared': 'Prepared. It now waits for a review by someone else.',
  'occurrence.done': 'Mark as done',
  'occurrence.doneTracked': 'Mark as done (tracked only)',
  'occurrence.doneNote': 'Done.',
  'occurrence.approve': 'Approve',
  'occurrence.reviewNoteOptional': 'Review note (optional)',
  'occurrence.approved': 'Approved and done.',
  'occurrence.sendBack': 'Send back',
  'occurrence.sendBackNote': 'Why does it go back?',
  'occurrence.sentBack': 'Sent back to preparation.',
  'occurrence.withdraw': 'Withdraw my preparation',
  'occurrence.withdrawn': 'Withdrawn.',
  'occurrence.skip': 'Skip',
  'occurrence.findings': 'Findings from this item',
  'occurrence.findingsNone': 'No finding has been raised from this item.',
  'occurrence.raiseFinding': 'Raise a finding',
  'skip.ask': 'Ask to skip',
  'skip.basis': 'On what basis',
  'skip.basis.client': 'The client agreed',
  'skip.basis.board': 'It will be reported to the board',
  'skip.agreedBy': 'Who at the client agreed',
  'skip.agreedOn': 'When',
  'skip.boardReport': 'In which report to the board',
  'skip.boardReportHint': 'For example: the annual compliance report 2026',
  'skip.twoPeople': 'This item needs a review or evidence, so skipping it takes two people: a reviewer other than you confirms it.',
  'skip.askSubmit': 'Ask to skip it',
  'skip.asked': 'Asked. A reviewer other than you confirms or declines it.',
  'skip.confirm': 'Confirm the skip',
  'skip.withdraw': 'Withdraw or decline the skip',
  'skip.withdrawNote': 'Note (optional)',
  'skip.withdrawn': 'The request to skip is withdrawn.',
  'skip.basisLine.board': 'to be reported to the board in {report}',
  'skip.basisLine.client': 'agreed by {name} at the client on {date}',
  'skip.askedLine': 'asked by {name} on {when}: {reason} ({basis})',
  'skip.factAsked': 'Skip asked, waiting for a reviewer',
  'skip.factSkipped': 'Skip asked',
  'skip.factConfirmed': 'Skip confirmed by',
  'occurrence.move': 'Move this item',
  'occurrence.moveTo': 'New due date',
  'occurrence.moveReason': 'Why it moves',
  'occurrence.moveNote': 'Only this item moves; its series keeps its rule. Who moved it, when and why are kept with it and in its history.',
  'occurrence.moveUntil': 'At most to {date}: an item moves inside its own period, before the next item of its series, and never past the planning horizon or the end of its service.',
  'occurrence.moveSubmit': 'Move it',
  'occurrence.movedDone': 'Moved.',
  'occurrence.movedFact': 'Moved',
  'occurrence.movedLine': 'from {date}, by {name} on {when}: {reason}',
  'occurrence.movedBackLine': 'back to the date of its rule ({date}), by {name} on {when}: {reason}',
  'occurrence.skipReason': 'Why is it skipped?',
  'occurrence.skipped': 'Skipped.',
  'occurrence.youPrepared': 'You prepared it. A reviewer other than you reviews it.',
  'occurrence.awaitingReview': 'Waiting for a review by a reviewer other than {name}.',
  'occurrence.notThePreparer': 'You may review: you are not the preparer ({name}).',
  'occurrence.needsEvidence': 'This item needs its evidence linked before it can be done.',
  'occurrence.readerOnly': 'You read this client; you do not act in it.',
  'occurrence.databaseDecides': 'Who acted is recorded by the database from your session; the same person never prepares and reviews one item.',
  'occurrence.waitingTitle': 'Waiting on the client',
  'occurrence.assignments': 'Who is expected to act',
  'occurrence.preparer': 'Preparer',
  'occurrence.reviewer': 'Reviewer',
  'occurrence.assignNote': 'An assignment names who should act; who actually acted is recorded when they do. A reviewer needs the reviewer role here.',
  'occurrence.checklist': 'Checklist',
  'occurrence.checklistNone': 'No checklist lines.',
  'occurrence.stepAdd': 'New line',
  'occurrence.stepSubmit': 'Add',
  'occurrence.stepRemove': 'Remove',
  'occurrence.stepDoneBy': 'ticked by {name}',
  'occurrence.firstLineNote': 'First-line work: until customer-level records are built, the evidence is a summary of the sample (counts by risk tier, exceptions by kind), never the customer files, and its uploader states that it holds no customer-level data.',
  'series.status': 'Status',
  'series.status.active': 'active',
  'series.status.paused': 'paused',
  'series.status.retired': 'retired',
  'series.rule': 'How often',
  'series.from': 'From',
  'series.roll': 'On a weekend',
  'series.prepDays': 'Days of preparation',
  'series.hours': 'Estimated hours',
  'series.hoursHint': 'An estimate for planning, never a time record.',
  'series.noEstimate': 'no estimate',
  'series.basis': 'Why it exists',
  'series.next': 'Next',
  'series.lastDone': 'Last done',
  'series.notYet': 'not yet',
  'series.ruleRaw': 'Rule (RFC 5545)',
  'series.ruleHint': 'For example FREQ=YEARLY;BYMONTH=11;BYMONTHDAY=30. A changed rule cancels only planned future items, with the reason.',
  'series.expertRule': 'Change the rule (expert)',
  'series.change': 'Change who does it',
  'series.saved': 'Saved. {n} planned items were brought in line; the worker dates the rest.',
  'series.saved.one': 'Saved. {n} planned item was brought in line; the worker dates the rest.',
  'series.performer': 'Client contact who performs it',
  'series.recorder': 'Member who records the delivery',
  'series.appliesFromNext': 'A change of who does it applies from the next planned item; items already started keep what they had.',
  'series.pause': 'Pause',
  'series.resume': 'Resume',
  'series.retire': 'Retire',
  'series.upcoming': 'Its next items',
  'series.noneUpcoming': 'No item of this series is dated.',
  'series.chooseService': 'Choose a service',
  'series.title': 'Title',
  'series.line': 'Line',
  'series.ruleReads': 'The rule reads: {rule}.',
  'series.create': 'Add the series',
  'series.whenTitle': 'When it is due',
  'series.whenNote': 'A new date applies to the planned items from today; items already started keep theirs. To move one item only, open that item.',
  'series.whenSave': 'Save the date',
  'series.whenNoPlain': 'This series’ rule has no plain form. It can be changed under the expert option below.',
  'series.followCalendar': 'Follow the calendar instead of {anchor}: a rule written here no longer moves with that date',
  'series.ruleSave': 'Save the rule',
  'series.daySetForClient': 'the day set for this client',
  'series.expertRuleNew': 'Write the rule yourself (expert)',
  'template.label.baseline': 'Olivia’s proposal, not yet adopted by your regulatory desk',
  'template.label.adopted': 'Adopted by the regulatory desk',
  'template.label.adoptedBy': 'Adopted by {name}',
  'template.label.draft': 'Draft: cannot be applied until the desk confirms it',
  'template.label.retired': 'Retired',
  'template.intro': 'A template creates series only for the services this client has. Items offered unticked stay out unless you tick them. Each item says who does it and where its cadence comes from.',
  'template.none': 'No template in the library.',
  'template.choose': 'Template',
  'template.otherType': 'for another client type',
  'template.noneFits': 'No template fits this client’s institution type and size; check the profile first.',
  'template.notApplicable': 'This template is a draft and cannot be applied.',
  'template.preparerAll': 'Preparer for every item below',
  'template.reviewerAll': 'Reviewer for every item below',
  'template.unownedNote': 'An item without a preparer shows as unowned once it is due within 30 days.',
  'template.include': 'Apply',
  'template.includeItem': 'Apply “{item}”',
  'template.firstDates': 'Cadence and first dates',
  'template.appliedAs': 'applied as series {code}',
  'template.notOffered': 'not offered: no such service',
  'template.offeredUnticked': 'offered unticked',
  'template.sourceNote': 'Source note',
  'template.performerDefault': 'the service’s responsible contact',
  'template.defaultFor': 'The library’s default for the delivery model “{model}”.',
  'template.clientAct': 'The client’s own act under every delivery model: the client performs it, and the firm records it with its evidence.',
  'template.byModel': '{model}: {who}',
  'template.apply': 'Apply the ticked items',
  'template.changeDate': 'Another date for this client',
  'template.dateChanged': 'Date changed for this client',
  'template.dateFor': 'The date of “{item}”',
  'template.dateBack': 'Back to the proposal',
  'template.applied': 'Applied: series created {created}, left out {left}, applied already {already}. The worker dates 18 months of items shortly.',
  'services.intro': 'What the firm does for this client, how, and who at the client keeps the responsibility. The services decide which template items apply and which cards the homes show.',
  'services.none': 'No service is recorded for this client yet.',
  'services.service': 'Service',
  'services.how': 'How',
  'services.responsible': 'Responsible at the client',
  'services.firmLead': 'Firm lead',
  'services.series': 'Active series',
  'services.trackedOnly': 'tracked only',
  'services.trackedOnlyNote': 'A tracked-only service: the work is done in other tools, and its dates and documents are held here.',
  'services.since': 'since {date}',
  'services.endedOn': 'ended {date}',
  'services.ended': 'ended',
  'services.noOwner': 'not named',
  'services.responsibleLeft': 'The responsible person has left',
  'services.responsibleMissing': 'Nobody at the client is named as responsible',
  'services.line': 'from {from} · firm lead {lead} · responsible at the client: {owner}',
  'services.kind': 'Service kind',
  'services.chooseKind': 'Choose a service kind',
  'services.delivery': 'Delivery model',
  'services.lineDefault': 'The kind’s default: {line}',
  'services.lineFromKind': 'As the kind says',
  'services.startOn': 'Starts',
  'services.description': 'Description',
  'services.contractRef': 'Contract reference',
  'services.interimRole': 'Title held at the client (interim)',
  'services.independenceNote': 'Independence note',
  'services.add': 'Add the service',
  'services.addOpen': 'Add a service',
  'services.added': 'Service added.',
  'services.leadOnly': 'Only a lead of this client records its services.',
  'services.edit': 'Change',
  'services.period': 'Period',
  'services.commercial': 'Commercial basis and planned hours',
  'services.whoDoesWhat': 'Who does what',
  'services.retained.title': 'Stays with the client',
  'services.retained.lead': 'Whoever does the rest of the work, the client cannot outsource these tasks. The decisions on single customers stay in the client’s own casework.',
  'services.retained.inCycle': 'In this client’s annual cycle as its own acts, which the firm records with their evidence:',
  'services.retained.missing': 'Not in this client’s annual cycle yet: {items}. A lead adds them by applying a template that has them.',
  'services.retained.none': 'The approvals that recur every year are not in this client’s annual cycle.',
  'services.retained.source': 'Source',
  'services.cadence': 'Cadence',
  'services.noSeries': 'No series runs for this service yet.',
  'services.notApplied': 'Not applied from the template: {items}',
  'services.effort': 'Estimated: {hours} h a month on average over the next year (items without an estimate: {missing}). Estimates, not recorded time.',
  'services.effortNone': 'No estimated hours recorded (items over the next year without one: {missing}).',
  'services.whoNote': 'Each client line names the client contact who performs it. A change applies from the next planned item and is recorded in the audit trail.',
  'services.end': 'End the service',
  'services.endNote': 'Ending a service pauses its series with the reason; nothing is deleted.',
  'services.endOn': 'Ends on',
  'services.endedPaused': 'Ended. {n} series are paused.',
  'services.endedPaused.one': 'Ended. {n} series is paused.',
  'services.openCycle': 'Its items in the annual cycle',
  'services.exportLater': 'Task-division export: later',
  'independence.person': '{name} is assigned on {lines} for this client ({services}).',
  'independence.firm': 'The firm runs internal audit ({audit}) and also first- or second-line work ({others}) for this client: record an independence note on the internal-audit service.',
  'independence.firmNoted': 'The firm runs internal audit ({audit}) and also first- or second-line work ({others}) for this client; an independence note is recorded.',
  'independence.neverRefuses': 'A warning, never a refusal.',
  'profile.edit': 'Change the profile',
  'profile.notYet': 'No profile is recorded yet.',
  'profile.identity': 'Identity',
  'profile.legalName': 'Legal name',
  'profile.orgNumber': 'Organisation number',
  'profile.lei': 'LEI',
  'profile.leiHint': '20 characters',
  'profile.perimeter': 'Perimeter',
  'profile.institutionTypes': 'Institution types',
  'profile.addType': 'Add a type',
  'profile.chooseType': 'Choose a type',
  'profile.removeType': 'Remove this type',
  'profile.typeNeeded': 'At least one institution type is needed.',
  'profile.sizeBand': 'Size band',
  'profile.notRecorded': 'not recorded',
  'profile.supervisors': 'Supervisors',
  'profile.commaSeparated': 'separated by commas',
  'profile.jurisdictions': 'Jurisdictions',
  'profile.jurisdiction': 'Jurisdiction',
  'profile.chooseJurisdiction': 'Choose a jurisdiction',
  'profile.presence': 'Presence',
  'profile.addJurisdiction': 'Add a jurisdiction',
  'profile.crossBorderNote': 'A cross-border presence is watched, not applied.',
  'profile.oneHome': 'Only one jurisdiction can be the home.',
  'profile.jurisdictionWatched': '{code} (cross-border, watched)',
  'profile.noPerimeter': 'No perimeter recorded yet',
  'profile.licences': 'Licences',
  'profile.licenceKind': 'Licence',
  'profile.licenceSince': 'Since',
  'profile.addLicence': 'Add a licence',
  'profile.anchors': 'Anchors',
  'profile.fyeMonth': 'Financial year ends: month',
  'profile.fyeDay': 'and day',
  'profile.agmMonth': 'AGM month',
  'profile.licenceDate': 'Licence date',
  'profile.anchorsNote': 'Items anchored on these dates move with them: a change here re-dates their planned items.',
  'profile.anchorsLine': 'year end {fye} · AGM month {agm} · licence {licence}',
  'profile.business': 'Business',
  'profile.employees': 'Employees',
  'profile.customers': 'Customers (count band)',
  'profile.description': 'Description',
  'profile.products': 'Products',
  'profile.channels': 'Channels',
  'profile.keySystems': 'Key systems',
  'profile.featuresLater': 'What the business does in reach terms (payments, remote onboarding and the like) is recorded with the regulatory corpus, which is not built yet.',
  'profile.savedReresolved': 'Saved. {n} anchored series moved with the new dates; the worker re-dates their planned items.',
  'profile.savedReresolved.one': 'Saved. {n} anchored series moved with the new dates; the worker re-dates its planned items.',
  'profile.leadOnly': 'Only a lead of this client changes its profile.',
  'profile.servicesLink': 'Services and who does what →',
  'profile.history': 'History of the profile, services and contacts',
  'contacts.title': 'Contacts at the client',
  'contacts.none': 'No contact is recorded yet.',
  'contacts.name': 'Name',
  'contacts.titleField': 'Title',
  'contacts.role': 'Role at the client',
  'contacts.reach': 'Email and phone',
  'contacts.email': 'Email',
  'contacts.phone': 'Phone',
  'contacts.board': 'board member',
  'contacts.retired': 'no longer a contact',
  'contacts.stillNamed': 'still named in {n} places',
  'contacts.stillNamed.one': 'still named in {n} place',
  'contacts.whereNamed': 'Where, and who takes over',
  'contacts.stillNamedIntro': '{name} is no longer a contact, and is still named here:',
  'contacts.staysAsRecorded': 'These record what happened, and stay as they are:',
  'contacts.successor': 'Who takes over',
  'contacts.nameSuccessor': 'Name who takes over',
  'contacts.leadNamesSuccessor': 'A lead of this client names who takes over.',
  'contacts.successorNamed': 'Saved: who takes over is named.',
  'contacts.named.service': 'responsible for the service',
  'contacts.named.series': 'performs the recurring activity',
  'contacts.named.action': 'owns the action',
  'contacts.named.occurrence': 'performs the item already started',
  'contacts.named.waiting': 'the firm waits on them for',
  'contacts.named.basis': 'gave the instruction of the basis',
  'contacts.add': 'Add a contact',
  'contacts.edit': 'Change',
  'contacts.retire': 'No longer a contact',
  'contacts.minimal': 'Keep it to what the work needs: name, role and a way to reach them.',
  'actions.title': 'Actions and findings',
  'actions.new': 'New action',
  'actions.create': 'Record the action',
  'actions.titleField': 'Title',
  'actions.detail': 'Detail',
  'actions.source': 'Source',
  'actions.sourceRef': 'Source reference',
  'actions.sourceOccurrence': 'Cycle item it came from',
  'actions.sourceOccurrenceHint': 'A code such as CY-2026-041, optional',
  'actions.severity': 'Severity',
  'actions.severityBasis': 'Basis for the severity',
  'actions.severityBasisHint': 'A rule or a reason a reader can check, for example “firm rule: a control gap on high-risk customers is high”.',
  'actions.owner': 'Owner',
  'actions.ownerMembers': 'Members of the firm',
  'actions.ownerContacts': 'Contacts at the client',
  'actions.due': 'Due',
  'actions.noDue': 'no due date',
  'actions.status': 'Status',
  'actions.filter.status': 'Show',
  'actions.filter.status.open': 'Open',
  'actions.filter.status.closed': 'Closed or cancelled',
  'actions.filter.status.all': 'All',
  'actions.filter.status.to_close': 'Verified, to close',
  'actions.filter.overdueOnly': 'Overdue only',
  'actions.showing': 'showing {n} of {total} · in this register: open {open}, verified and to close {toClose}, closed {closed}',
  'actions.followedUpBy': 'followed up by {name}',
  'actions.followUpLeft': '{name}, who no longer works on this client',
  'actions.awaitingClose': 'verified; a lead or reviewer closes it',
  'actions.followUp': 'Followed up at the firm by',
  'actions.followUpHint': 'The member who chases the client, and who sees it under What needs you.',
  'actions.followUpMe': 'Me (the person who records it)',
  'actions.verificationRefused': 'Verification refused',
  'actions.filter.all': 'All',
  'actions.moved': 'Moved',
  'actions.movedAny': 'Any',
  'actions.movedDays': 'in the last {n} days',
  'actions.movedNote': '“Moved” means the due date, the status, the owner or the severity changed in the window, read from the audit chain. Any other edit does not count.',
  'actions.movedCol': 'What moved',
  'actions.none': 'No action matches.',
  'actions.noneMoved': 'No action moved in the last {n} days.',
  'actions.awaitingVerification': 'waiting for verification',
  'actions.pipeline': 'Where the action stands',
  'actions.created': 'Recorded',
  'actions.remediated': 'Remediated',
  'actions.verified': 'Verified',
  'actions.cancelledReason': 'Cancelled because',
  'actions.edit': 'Change',
  'actions.start': 'Start',
  'actions.started': 'Started.',
  'actions.remediate': 'Record the remediation',
  'actions.remediationNote': 'What was done?',
  'actions.remediatedNote': 'Recorded. A reviewer other than you verifies it.',
  'actions.verify': 'Verify',
  'actions.verificationNote': 'Verification note (optional)',
  'actions.verifiedNote': 'Verified.',
  'actions.refuse': 'Not verified',
  'actions.refuseNote': 'What is still missing?',
  'actions.refusedNote': 'Sent back to work.',
  'actions.close': 'Close',
  'actions.closedNote': 'Closed.',
  'actions.cancel': 'Cancel the action',
  'actions.cancelReason': 'Why is it cancelled?',
  'actions.cancelledNote': 'Cancelled.',
  'actions.youRemediated': 'You recorded the remediation; a reviewer other than you verifies it.',
  'actions.reviewerVerifies': 'A member with the reviewer role verifies it.',
  'documents.upload': 'Upload a file',
  'documents.uploading': 'Uploading…',
  'documents.file': 'File',
  'documents.titleField': 'Title',
  'documents.kind': 'Kind',
  'documents.statement': 'What the file holds',
  'documents.statementNone': 'This file holds no customer-level data (no data about the client’s own customers, one by one).',
  'documents.statementCustomer': 'This file holds customer-level data.',
  'documents.statementNote': 'The statement is yours as the uploader; Olivia cannot check it, and says so.',
  'documents.firstLine': 'Evidence for first-line work must be a summary that holds no customer-level data.',
  'documents.thisClientOnly': 'Files are stored for this client only; nothing is shared with other clients.',
  'documents.none': 'No document is stored for this client yet.',
  'documents.noneOfKind': 'No document of this kind.',
  'documents.linkedTo': 'Linked to',
  'documents.uploaded': 'Uploaded',
  'documents.notLinked': 'not linked',
  'documents.versionOf': 'new version of {code}',
  'documents.retired': 'retired',
  'documents.statedNone': 'holds no customer-level data, stated by {name} at upload',
  'documents.statedBy': 'stated by {name} at upload, {date}',
  'documents.sha256': 'Fingerprint (SHA-256)',
  'documents.download': 'Download',
  'documents.newVersion': 'Upload a new version',
  'documents.retire': 'Retire',
  'documents.links': 'Where it is used',
  'documents.linkedBy': 'linked by {name}, {date}',
  'documents.linkSubject': 'Link it to',
  'documents.linkCode': 'Code',
  'documents.linkCodeHint': 'for example CY-2026-041',
  'documents.purpose': 'As',
  'documents.link': 'Link',
  'documents.linked': 'Linked.',
  'documents.evidence': 'Evidence and files',
  'documents.noneLinked': 'No document is linked yet.',
  'documents.unlink': 'Remove the link',
  'documents.uploadHere': 'Upload a file here',
  'documents.linkExisting': 'Link a document this client holds',
  'documents.chooseDocument': 'Document',
  'documents.choose': 'Choose a document',
  'bytes.b': '{n} B',
  'bytes.kb': '{n} KB',
  'bytes.mb': '{n} MB',
  'comments.title': 'Comments',
  'comments.none': 'No comments.',
  'comments.retracted': 'Retracted by its author.',
  'comments.retract': 'Retract',
  'comments.add': 'Comment',
  'comments.submit': 'Add the comment',
  'history.title': 'History',
  'history.none': 'Nothing recorded yet.',
  'waiting.since': 'since {date}: {who}, {what}',
  'waiting.clear': 'The client has answered',
  'waiting.markOpen': 'Mark as waiting on the client…',
  'waiting.mark': 'Mark as waiting',
  'waiting.contact': 'Contact asked',
  'waiting.chooseContact': 'Choose a contact',
  'waiting.what': 'What was asked',
  'waiting.askedOn': 'Asked on (today if empty)',
  'waiting.requestsLater': 'Waiting does not stop the clock: an overdue item stays overdue. Later, a wait will point to a request in the requests register.',
  'basis.title': 'Customer data (basis)',
  'basis.nothingStored': 'Nothing customer-level is stored in Olivia yet: the registers that hold it, with field encryption, logged reads and retention, come later. What is recorded here is the client’s basis for them.',
  'basis.youHold': 'You hold here: {roles}; {customer}.',
  'basis.withRole': 'including the customer-data role',
  'basis.withoutRole': 'not the customer-data role',
  'basis.none': 'No basis is recorded for this client. Without a confirmed one, no customer-level record can be held for it.',
  'basis.forService': 'for service {service}',
  'basis.state.live': 'live',
  'basis.state.awaiting': 'waiting for a second person to confirm it',
  'basis.state.retired': 'retired',
  'basis.state.notYetEffective': 'confirmed, not yet in effect',
  'basis.state.reviewPassed': 'review date passed: nothing new may be held under it',
  'basis.purposes': 'Purposes',
  'basis.categories': 'Categories and fields',
  'basis.notCovered': 'Not covered',
  'basis.suspicionNotAvailable': 'suspicion-related content (not available)',
  'basis.legalBasis': 'Legal basis',
  'basis.legalBasisHint': 'As the client, the controller, states it.',
  'basis.asTheClientStates': '“{text}” (as the client states it)',
  'basis.purposeStatement': 'Purpose',
  'basis.instruction': 'Instruction',
  'basis.instructionHint': 'The data processing agreement clause or the written instruction, stored as a document first.',
  'basis.instructionFrom': '{doc} from {who}',
  'basis.dpia': 'DPIA',
  'basis.dpiaDocument': 'The client’s DPIA (a document)',
  'basis.dpiaNone': 'none: record the client’s reason below',
  'basis.dpiaNote': 'The client’s recorded reason that no DPIA is needed',
  'basis.retention': 'Retention',
  'basis.retentionLine': '{category}: {months} months after the work closes',
  'basis.retentionFor': 'Retention for {category} (months)',
  'basis.retentionHint': '1 to 60 months after the work closes; proposed 24.',
  'basis.ai': 'AI',
  'basis.aiAllowed': 'may go to Claude, under the four conditions below',
  'basis.aiNotAllowed': 'not allowed',
  'basis.aiCheckbox': 'The client allows customer-level data under this basis to go to Claude (Anthropic API)',
  'basis.period': 'In effect',
  'basis.periodLine': 'from {from}, review by {review}',
  'basis.recorded': 'Recorded',
  'basis.confirmedBy': 'confirmed by {name}, {date}',
  'basis.retired': 'Retired',
  'basis.termsFixed': 'Its terms are fixed from the moment they are recorded; a change is a new basis.',
  'basis.confirm': 'Confirm this basis',
  'basis.confirmed': 'Confirmed. The basis is live from its effective date.',
  'basis.youRecorded': 'You recorded it; a second member (a lead or a reviewer) confirms it.',
  'basis.retire': 'Retire',
  'basis.record': 'Record a basis',
  'basis.recordIntro': 'Record what the client has instructed and on what basis. Only the fields each task needs; nothing customer-level is stored before a second person confirms it.',
  'basis.recordSubmit': 'Record the basis',
  'basis.recordedNote': 'Recorded. It is not live until a second member confirms it.',
  'basis.secondPerson': 'Recording needs a lead; confirming needs a second member who is a lead or a reviewer.',
  'basis.leadRecords': 'A lead of this client records a basis; a second member confirms it.',
  'basis.service': 'Service it serves',
  'basis.controller': 'Who at the client gave the instruction',
  'basis.fields': 'Fields per category and purpose',
  'basis.fieldsNote': 'Tick only what each task needs. A sample check that needs the reference, the risk class and the checks never holds a name.',
  'basis.notAvailableNote': 'Suspicion-related content and the special categories of personal data have no field at all.',
  'basis.effectiveFrom': 'In effect from',
  'basis.reviewOn': 'Review by',
  'basis.reviewHint': 'At most a year after it takes effect.',
  'basis.needsDocument': 'Upload the instruction (and the DPIA, if there is one) as documents first.',
  'basis.holders': 'Who holds the customer-data role',
  'basis.noHolders': 'Nobody holds it.',
  'basis.roleRule': 'Membership is not enough: the customer-data role is a second grant, given only by a lead of this client, never to themselves, and only while a confirmed basis is live.',
  'basis.grantTo': 'Give the role to',
  'basis.grant': 'Give the role',
  'basis.grantNeedsLive': 'The role can be given once a basis is confirmed and live.',
  'basis.whereDataGoes': 'Where this client’s data may go',
  'basis.class.public': 'Public material',
  'basis.class.publicGoes': 'Public regulatory material only, through the pinned OpenRouter route or Claude.',
  'basis.class.client': 'Client data',
  'basis.class.clientClaude': 'To Claude through the Anthropic API (allowed by {name}).',
  'basis.class.clientNone': 'To no model: the client’s AI allowance is off, or the installation sends client data nowhere.',
  'basis.class.customer': 'Customer-level data',
  'basis.class.customerClaude': 'May go to Claude where the person also holds the customer-data role.',
  'basis.class.customerNone': 'To no model.',
  'basis.class.conditions': 'Installation ceiling: {ceiling} · client’s AI allowance: {allowance} · a live basis allows AI: {basis} · you hold the role: {role}',
  'basis.condYes': 'yes',
  'basis.condNo': 'no',
  'basis.class.suspicion': 'Suspicion-related content',
  'basis.class.suspicionNever': 'Not held in Olivia, and never sent to any model.',
  'onboarding.title': 'Setting up the client',
  'onboarding.steps': 'Steps of the set-up',
  'onboarding.phase.setUp': 'Set up',
  'onboarding.phase.start': 'Start',
  'onboarding.step.client': 'Client',
  'onboarding.step.perimeter': 'Perimeter',
  'onboarding.step.services': 'Services',
  'onboarding.step.cycle': 'Annual cycle',
  'onboarding.step.members': 'Members',
  'onboarding.step.customer': 'Customer data',
  'onboarding.step.review': 'Review and start',
  'onboarding.back': 'Back',
  'onboarding.next': 'Next',
  'onboarding.leadOnly': 'Only a lead of this client sets it up; you can read every step.',
  'onboarding.alreadyStarted': 'This client is started already ({status}); the steps stay here to look back at.',
  'onboarding.perimeterIntro': 'Who the client is and where it works. The perimeter decides which templates fit it, and later which rules reach it.',
  'onboarding.servicesIntro': 'Record the contacts first, then each service the firm runs for the client, with who at the client keeps the responsibility.',
  'onboarding.cycleIntro': 'Apply the client type’s template: only the items of the services chosen are offered, each with who does it and where its cadence comes from.',
  'onboarding.cycleNeedsServices': 'Record a service first: a template applies only to the services the client has.',
  'onboarding.membersIntro': 'Reviewed work needs two people: give a second member a working role, and the reviewer role to whoever reviews.',
  'onboarding.customerQuestion': 'Will the firm hold customer-level records for this client (for example the files checked in a sample)?',
  'onboarding.customerNo': 'No, not now',
  'onboarding.customerYes': 'Yes: record the basis',
  'onboarding.customerRecorded': 'Recorded: {codes}. A second member confirms it on',
  'onboarding.customerNote': 'A lead records the basis and a second member confirms it; nothing customer-level is stored before that, and in this version nothing at all.',
  'onboarding.done': 'done',
  'onboarding.open': 'still open',
  'onboarding.notNow': 'not now',
  'onboarding.reviewNote': 'Starting makes the client active. Steps still open can be finished later from the client’s own pages.',
  'onboarding.start': 'Start the client',
  'onboarding.startNeeds': 'A profile with an institution type and at least one service come first.',
  'onboarding.toClient': 'To the client',
  'library.subtitle': 'The firm’s templates, service kinds and work functions, loaded from the library files and locked by their fingerprints.',
  'library.templates': 'Annual-cycle templates',
  'library.noTemplates': 'No template is loaded. Run the migrations, which load the library.',
  'library.template': 'Template',
  'library.forTypes': 'For',
  'library.anyType': 'any client type',
  'library.status': 'Status',
  'library.items': 'Items',
  'library.deskNote': 'Olivia’s proposed templates can be applied from the first day. A regulatory-desk member adopts one after checking each reference against the consolidated texts; until then every cadence and day in it is Olivia’s proposal.',
  'library.serviceKinds': 'Service kinds',
  'library.functions': 'Work functions',
  'library.what': 'What the firm does',
  'library.workFunctions': 'Work functions',
  'library.oliviaCode': 'added by Olivia',
  'library.functionsNote': 'What a person does, not what they may do: functions grant nothing.',
  'library.description': 'Description',
  'library.baselineDecision': 'Why it may be applied before adoption',
  'library.adopted': 'Adopted',
  'library.retired': 'Retired',
  'library.sha': 'Fingerprint (SHA-256)',
  'library.adopt': 'Adopt for the firm',
  'library.adoptedNote': 'Adopted. Your name is recorded with it.',
  'library.retire': 'Retire',
  'library.deskOnly': 'A regulatory-desk member adopts or retires templates.',
  'people.subtitle': 'Firm roles govern the firm’s own things; a client’s content needs membership of that client.',
  'people.manage': 'Manage',
  'people.grantRole': 'Give the firm role',
  'people.chooseRole': 'Choose a firm role',
  'people.revokeRole': 'Revoke',
  'people.notYourself': 'Nobody changes their own firm roles.',
  'people.clientRoles': 'Roles in a client',
  'people.requestGrant': 'Ask for the role',
  'people.grantNote': 'Nothing is given until a second firm administrator, or that client’s lead, confirms it.',
  'people.grantRequested': 'Asked. It waits for a second person.',
  'people.disable': 'Switch the account off',
  'people.enable': 'Switch the account on',
  'clients.none': 'No client exists yet.',
  'clients.notMember': 'you are not a member: name, status and counts only',
  'clients.markReal': 'Correct: not fictional',
  'clients.markFictional': 'Correct: fictional',
  'clients.correctionReason': 'Why the label is corrected (kept in the firm’s audit trail)',
  'clients.adminOnly': 'Firm administrators and firm auditors see the list of clients. Your own clients are in the switcher.',
  'clients.createRole': 'A partner or a firm administrator creates a client.',
  'clients.codeHint': 'Lower-case letters, digits and hyphens; it becomes the address and never changes.',
  'clients.createdOther': 'Created as {code}. Its first lead continues the set-up inside the client.',
  'members.granted': 'The role is given.',
  'members.needsSecond': 'Reviewed work needs a second person here: one working member cannot review their own work.',
  'ai.customerCeilingOn': 'Customer-level data may go to Claude only where, in addition, the client’s confirmed basis allows it and the person holds the customer-data role.',
  'invites.grantNote': 'Roles in clients may be confirmed by a lead of each of those clients; firm roles only by a second firm administrator.',
  'state.paused': 'series paused',
  'cycle.monthCut': 'Showing {n} of the {total} items due this month. Narrow the filters to see the rest.',
  'cycle.yearCut': 'Showing {n} of the {total} items due in these twelve months. Choose a service to see the rest.',
  'series.reviewStays': 'Once a series needs a review or evidence, it keeps needing them: one person never switches off the second pair of eyes. To change what the series is, retire it with the reason and add a new one.',
  'people.adminRequested': 'Asked. The role is given only when a second firm administrator confirms it.',
  'invites.kind.adminGrant': 'The firm administrator role for an existing person',

  // ── The audit trail in words, and the notes on a cycle item (expert review O-C) ─────────────────────────────────
  'auditAction.insert': 'Added',
  'auditAction.update': 'Changed',
  'auditAction.delete': 'Removed',
  'auditAction.other': 'Recorded a change',
  'auditAction.job': 'Scheduled work by the system',
  'auditAction.action.create': 'Recorded an action',
  'auditAction.action.update': 'Changed an action',
  'auditAction.action.start': 'Started work on an action',
  'auditAction.action.remediate': 'Recorded the remediation',
  'auditAction.action.verify': 'Verified the remediation',
  'auditAction.action.verification_refused': 'Did not accept the remediation',
  'auditAction.action.close': 'Closed an action',
  'auditAction.action.cancel': 'Cancelled an action',
  'auditAction.action.waiting_on_client': 'Marked as waiting on the client',
  'auditAction.action.waiting_cleared': 'Recorded that the client answered',
  'auditAction.ai_allowance.decide': 'Decided the AI allowance',
  'auditAction.ai_allowance.confirm': 'Confirmed the AI allowance',
  'auditAction.basis.record': 'Recorded a customer-data basis',
  'auditAction.basis.confirm': 'Confirmed a customer-data basis',
  'auditAction.basis.retire': 'Retired a customer-data basis',
  'auditAction.basis.replace': 'Replaced a customer-data basis',
  'auditAction.comment.add': 'Added a comment',
  'auditAction.comment.retract': 'Withdrew a comment',
  'auditAction.contact.add': 'Added a contact',
  'auditAction.contact.update': 'Changed a contact',
  'auditAction.contact.retire': 'Marked a contact as no longer a contact',
  'auditAction.cycle.add_series': 'Added a recurring activity',
  'auditAction.cycle.apply_template': 'Applied a template',
  'auditAction.cycle.change_series': 'Changed a recurring activity',
  'auditAction.cycle.start': 'Started a cycle item',
  'auditAction.cycle.prepare': 'Prepared a cycle item',
  'auditAction.cycle.review_approve': 'Approved the review',
  'auditAction.cycle.review_send_back': 'Sent the item back',
  'auditAction.cycle.withdraw': 'Withdrew the preparation',
  'auditAction.cycle.done': 'Marked a cycle item as done',
  'auditAction.cycle.skip': 'Skipped a cycle item',
  'auditAction.cycle.assign': 'Assigned a cycle item',
  'auditAction.cycle.waiting_on_client': 'Marked as waiting on the client',
  'auditAction.cycle.waiting_cleared': 'Recorded that the client answered',
  'auditAction.cycle.step_add': 'Added a checklist step',
  'auditAction.cycle.step_tick': 'Ticked a checklist step',
  'auditAction.cycle.step_untick': 'Unticked a checklist step',
  'auditAction.cycle.step_remove': 'Removed a checklist step',
  'auditAction.cycle.move': 'Moved a cycle item',
  'auditAction.cycle.skip_request': 'Asked to skip a cycle item',
  'auditAction.cycle.skip_confirm': 'Confirmed skipping a cycle item',
  'auditAction.cycle.skip_withdraw': 'Withdrew or declined a request to skip',
  'auditAction.document.upload': 'Uploaded a document',
  'auditAction.document.update': 'Changed a document’s title or kind',
  'auditAction.document.retire': 'Retired a document',
  'auditAction.document.link': 'Linked a document',
  'auditAction.document.unlink': 'Removed a document link',
  'auditAction.profile.update': 'Changed the client profile',
  'auditAction.profile.confirm_year_end': 'Confirmed the financial year end',
  'auditAction.contact.replace': 'Named who takes over from a contact',
  'auditAction.service.add': 'Added a service',
  'auditAction.service.update': 'Changed a service',
  'auditAction.service.end': 'Ended a service',
  'auditAction.workspace.create': 'Created the client',
  'auditAction.workspace.status': 'Changed the client’s status',
  'auditAction.workspace.fictional': 'Corrected whether the client is fictional',
  'auditAction.workspace_member.grant': 'Gave a role in the client',
  'auditAction.workspace_member.revoke': 'Took back a role in the client',
  'auditAction.invite.create': 'Invited a person',
  'auditAction.invite.confirm': 'Confirmed an invitation',
  'auditAction.invite.accept': 'Accepted an invitation',
  'auditAction.invite.revoke': 'Withdrew an invitation',
  'auditAction.grant.request': 'Asked for a role to be given',
  'auditAction.firm_role.grant': 'Gave a firm role',
  'auditAction.firm_role.revoke': 'Took back a firm role',
  'auditAction.firm_admin.request': 'Asked for a firm administrator to be made',
  'auditAction.person.disable': 'Switched off an account',
  'auditAction.person.enable': 'Switched an account on again',
  'auditAction.reset.request': 'Asked for a password reset',
  'auditAction.reset.accept': 'Set a new password',
  'auditAction.reset.host': 'Reset a password on the server',
  'auditAction.reset.notice_ack': 'Read the notice of a password reset',
  'auditAction.admin.create': 'Created the first administrator',
  'auditAction.session.open': 'Signed in',
  'auditAction.session.sign_out': 'Signed out',
  'auditAction.session.end': 'A session ended',
  'auditAction.session.touch': 'A session was used',
  'auditAction.session.sweep': 'Ended idle sessions',
  'auditAction.settings.update': 'Changed the firm’s settings',
  'auditAction.installation.sync': 'Read the installation’s settings',
  'auditAction.library.sync': 'Loaded the firm library',
  'auditAction.template.adopt': 'Adopted a template',
  'auditAction.template.retire': 'Retired a template',
  'auditAction.spend.reserve': 'Reserved AI budget',
  'auditAction.spend.settle': 'Settled the cost of an AI call',
  'auditAction.spend.release': 'Released unused AI budget',
  'auditAction.spend.sweep': 'Released stale AI reservations',
  'auditAction.job.enqueue': 'Asked the system for work',
  'auditAction.job.wheel.materialise': 'Made the planned cycle items',
  'auditAction.job.wheel.daily': 'Updated the annual cycle for the day',
  'auditAction.job.audit.verify': 'Checked the event log',
  'auditAction.job.audit.verify_firm': 'Checked the firm’s event log',
  'auditAction.job.session.sweep': 'Ended idle sessions',
  'auditAction.job.spend.sweep': 'Released stale AI reservations',
  'auditAction.seed.workspace': 'Set up the demonstration',
  'auditAction.seed.client': 'Set up a demonstration client',
  'auditAction.seed.people': 'Set up the demonstration’s people',
  'auditAction.seed.activity': 'Recorded the demonstration’s work',
  'auditAction.seed.baseline': 'Recorded the documents of the demonstration’s current-state analysis',
  'record.occurrence': 'Cycle item',
  'record.series': 'Recurring activity',
  'record.action': 'Action',
  'record.service': 'Service',
  'record.document': 'Document',
  'record.basis': 'Customer-data basis',
  'record.profile': 'Client profile',
  'record.contact': 'Contact',
  'record.institutionType': 'Institution type',
  'record.jurisdiction': 'Jurisdiction',
  'record.step': 'Checklist step',
  'record.documentLink': 'Document link',
  'record.comment': 'Comment',
  'record.aiAllowance': 'AI allowance',
  'record.aiCall': 'AI call',
  'record.member': 'Member',
  'record.spend': 'AI spending',
  'record.other': 'Record',
  'record.on': 'on {record}',
  'audit.changes': 'What changed',
  'audit.unknownPerson': 'a person not listed here',
  'audit.unknownRecord': 'a record not listed here',
  'audit.structuredChanged': 'changed (see the record)',
  'audit.roundEnded': '{act} by {name}: {note}',
  'audit.roundEndedNoNote': '{act} by {name}',
  'audit.since': 'since {date}',
  'auditField.status': 'status',
  'auditField.title': 'title',
  'auditField.createdBy': 'created by',
  'auditField.dueOn': 'due date',
  'auditField.startsOn': 'preparation starts',
  'auditField.responsibility': 'who performs it',
  'auditField.performer': 'performed at the client by',
  'auditField.reviewRequired': 'needs a review',
  'auditField.evidenceRequired': 'needs evidence',
  'auditField.preparer': 'assigned to prepare',
  'auditField.reviewer': 'assigned to review',
  'auditField.preparedBy': 'prepared by',
  'auditField.preparedAt': 'prepared on',
  'auditField.reviewedBy': 'reviewed by',
  'auditField.reviewedAt': 'reviewed on',
  'auditField.prepareNote': 'preparer’s note',
  'auditField.reviewNote': 'reviewer’s note',
  'auditField.noteBeforeSplit': 'note (who wrote it was not recorded)',
  'auditField.earlierRounds': 'earlier rounds',
  'auditField.doneAt': 'done on',
  'auditField.skippedBy': 'skipped by',
  'auditField.skippedAt': 'skipped on',
  'auditField.skippedReason': 'reason for skipping',
  'auditField.movedFrom': 'the date of its rule',
  'auditField.movedBy': 'moved by',
  'auditField.movedAt': 'moved on',
  'auditField.movedReason': 'why it moved',
  'auditField.skipRequestedBy': 'skip asked by',
  'auditField.skipRequestedAt': 'skip asked on',
  'auditField.skipRequestReason': 'why it should be skipped',
  'auditField.skipAgreedContact': 'agreed at the client by',
  'auditField.skipAgreedOn': 'agreed on',
  'auditField.skipBoardReport': 'to be reported to the board in',
  'auditField.yearEndConfirmedBy': 'year end confirmed by',
  'auditField.yearEndConfirmedAt': 'year end confirmed on',
  'auditField.cancelledBy': 'cancelled by',
  'auditField.cancelledAt': 'cancelled on',
  'auditField.cancelledReason': 'reason for cancelling',
  'auditField.waitingOn': 'waiting on',
  'auditField.waitingAskedOn': 'asked on',
  'auditField.waitingFor': 'what was asked',
  'auditField.waitingClearedAt': 'the client answered on',
  'auditField.activityKind': 'kind of activity',
  'auditField.function': 'function',
  'auditField.line': 'line of defence',
  'auditField.anchor': 'anchored on',
  'auditField.offsetDays': 'days from the anchor',
  'auditField.rule': 'when it falls',
  'auditField.starts': 'starts',
  'auditField.ends': 'ends',
  'auditField.roll': 'on a weekend',
  'auditField.prepDays': 'days of preparation',
  'auditField.reviewDays': 'days of review',
  'auditField.service': 'service',
  'auditField.estimatedHours': 'estimated hours',
  'auditField.basis': 'why it exists',
  'auditField.cadenceSource': 'where the cadence comes from',
  'auditField.sourceNote': 'source note',
  'auditField.statusReason': 'reason',
  'auditField.step': 'step',
  'auditField.tickedBy': 'ticked by',
  'auditField.tickedAt': 'ticked on',
  'auditField.removedBy': 'removed by',
  'auditField.removedAt': 'removed on',
  'auditField.removedReason': 'reason for removing',
  'auditField.detail': 'detail',
  'auditField.sourceKind': 'source',
  'auditField.sourceRef': 'source reference',
  'auditField.sourceDocument': 'source document',
  'auditField.sourceOccurrence': 'cycle item',
  'auditField.severity': 'severity',
  'auditField.severityBasis': 'basis for the severity',
  'auditField.owner': 'owner',
  'auditField.ownerContact': 'owner at the client',
  'auditField.followUp': 'followed up by',
  'auditField.remediatedBy': 'remediated by',
  'auditField.remediatedAt': 'remediated on',
  'auditField.remediationNote': 'what was done',
  'auditField.verifiedBy': 'verified by',
  'auditField.verifiedAt': 'verified on',
  'auditField.verificationNote': 'verification note',
  'auditField.closedBy': 'closed by',
  'auditField.closedAt': 'closed on',
  'auditField.serviceKind': 'kind of service',
  'auditField.deliveryModel': 'delivery model',
  'auditField.description': 'description',
  'auditField.endedReason': 'reason for ending',
  'auditField.contractRef': 'contract reference',
  'auditField.lead': 'lead',
  'auditField.clientOwner': 'responsible at the client',
  'auditField.interimRole': 'role held at the client',
  'auditField.independenceNote': 'note on independence',
  'auditField.documentKind': 'kind of document',
  'auditField.size': 'size',
  'auditField.originalName': 'file name',
  'auditField.versionOf': 'earlier version',
  'auditField.customerContent': 'customer-level data',
  'auditField.uploadedBy': 'uploaded by',
  'auditField.uploadedAt': 'uploaded on',
  'auditField.retiredBy': 'retired by',
  'auditField.retiredAt': 'retired on',
  'auditField.retiredReason': 'reason for retiring',
  'auditField.document': 'document',
  'auditField.linkPurpose': 'linked as',
  'auditField.comment': 'comment',
  'auditField.author': 'author',
  'auditField.retractedAt': 'withdrawn on',
  'auditField.legalName': 'legal name',
  'auditField.organisationNumber': 'organisation number',
  'auditField.lei': 'LEI code',
  'auditField.sizeBand': 'size',
  'auditField.supervisors': 'supervisors',
  'auditField.licences': 'licences',
  'auditField.financialYearEnd': 'financial year end',
  'auditField.agmMonth': 'month of the annual general meeting',
  'auditField.licenceDate': 'licence date',
  'auditField.employeesBand': 'employees',
  'auditField.customersBand': 'customers',
  'auditField.products': 'products',
  'auditField.channels': 'channels',
  'auditField.keySystems': 'key systems',
  'auditField.flags': 'characteristics',
  'auditField.name': 'name',
  'auditField.jobTitle': 'job title',
  'auditField.roleAtClient': 'role at the client',
  'auditField.email': 'email',
  'auditField.phone': 'phone',
  'auditField.isBoard': 'on the board',
  'auditField.contactRetiredBy': 'marked no longer a contact by',
  'auditField.contactRetiredAt': 'no longer a contact from',
  'auditField.institutionType': 'institution type',
  'auditField.addedBy': 'added by',
  'auditField.addedAt': 'added on',
  'auditField.jurisdiction': 'jurisdiction',
  'auditField.presence': 'presence',
  'auditField.purposes': 'purposes',
  'auditField.categories': 'categories',
  'auditField.fields': 'fields',
  'auditField.legalBasis': 'legal basis',
  'auditField.purposeStatement': 'purpose statement',
  'auditField.controllerContact': 'the controller’s contact',
  'auditField.instructionDocument': 'instruction',
  'auditField.dpiaDocument': 'data protection impact assessment',
  'auditField.dpiaNote': 'note on the impact assessment',
  'auditField.retention': 'retention',
  'auditField.aiAllowed': 'AI allowed',
  'auditField.recordedBy': 'recorded by',
  'auditField.recordedAt': 'recorded on',
  'auditField.confirmedBy': 'confirmed by',
  'auditField.authorisedBy': 'authorised by, at the client',
  'auditField.processingLocation': 'processing location',
  'auditField.transferBasis': 'transfer basis',
  'auditField.confirmedAt': 'confirmed on',
  'auditField.effectiveFrom': 'in force from',
  'auditField.reviewOn': 'review by',
  'auditField.engines': 'services allowed for client data',
  'auditField.allowanceBasis': 'basis for the allowance',
  'auditField.monthlyCap': 'monthly cap (USD)',
  'auditField.decidedBy': 'decided by',
  'auditField.decidedAt': 'decided on',
  'auditField.forPerson': 'for',
  'auditField.purpose': 'purpose',
  'auditField.engine': 'AI service',
  'auditField.model': 'model',
  'auditField.cost': 'cost (USD)',
  'auditField.reserved': 'reserved (USD)',
  'auditField.settledAt': 'settled on',
  'auditField.callStatus': 'outcome',
  'auditField.refusal': 'refusal',
  'auditField.person': 'person',
  'auditField.role': 'role',
  'auditField.grantedBy': 'given by',
  'auditField.grantedVia': 'given through',
  'auditField.grantedAt': 'given on',
  'auditField.revokedBy': 'taken back by',
  'auditField.revokedAt': 'taken back on',
  'auditField.revokeReason': 'reason for taking it back',
  'occurrenceStatus.planned': 'planned',
  'occurrenceStatus.in_progress': 'in progress',
  'occurrenceStatus.prepared': 'prepared',
  'occurrenceStatus.done': 'done',
  'occurrenceStatus.skipped': 'skipped',
  'occurrenceStatus.cancelled': 'cancelled',
  'customerContent.none': 'none',
  'customerContent.customer_level': 'holds customer-level data',
  'grantedVia.person': 'a lead’s grant',
  'grantedVia.workspace_create': 'the creation of the client',
  'grantedVia.invite': 'an invitation',
  'grantedVia.grant_request': 'an administrator’s request, confirmed by a second person',
  'grantedVia.seed': 'the demonstration',
  'occurrence.notesTitle': 'Preparation and review',
  'occurrence.notesNone': 'Not prepared yet.',
  'occurrence.notePrepared': 'Prepared by {name}, {date}',
  'occurrence.noteSentBack': 'Sent back by {name}, {date}',
  'occurrence.noteWithdrawn': 'Withdrawn by {name}, {date}',
  'occurrence.noteApproved': 'Approved by {name}, {date}',
  'occurrence.noteDone': 'Marked as done by {name}, {date}',
  'occurrence.noteNoText': 'No note.',
  'occurrence.noteBeforeSplit': 'A note from before Olivia recorded who wrote each note: who wrote it is not known.',
  'occurrence.act.sent_back': 'Sent back',
  'occurrence.act.withdrawn': 'Withdrawn',

  // ── Refusals in the reader's language (expert review O-D) ───────────────────────────────────────────────────────
  'errors.UNREACHABLE': 'The Olivia server could not be reached. Check your connection, and try again in a moment.',
  'errors.subject.rule': 'The rule',
  'errors.subject.item': '“{title}”',
  'errors.subject.series': 'The series {code}',
  'errors.TEXT_REQUIRED': 'Fill in the text that is asked for.',
  'errors.TEXT_TOO_SHORT': 'Write at least {min} characters.',
  'errors.TEXT_TOO_LONG': 'Write at most {max} characters.',
  'errors.REASON_REQUIRED': 'Give a reason.',
  'errors.UPLOAD_UNREADABLE': 'The upload could not be read. Try again, and tell your firm if it keeps happening.',
  'errors.FIRM_ROLE_NEEDED': 'This needs the firm role {roles}.',
  'errors.CLIENT_ROLE_NEEDED': 'This needs the role {roles} in this client.',
  'errors.WORKING_ROLE_NEEDED': 'This needs a working role in this client: lead, reviewer or analyst.',
  'errors.ASSIGN_NEEDS_LEAD': 'Only a lead assigns a cycle item to someone else; you may take one to prepare yourself.',
  'errors.LEAD_GRANTS_ROLES': 'Only a lead of this client gives its roles.',
  'errors.LEAD_REVOKES_ROLES': 'Only a lead of this client or a firm administrator takes its roles back.',
  'errors.FOUR_EYES_REVIEW': 'You prepared this item, so someone else reviews it.',
  'errors.FOUR_EYES_VERIFY': 'You recorded the remediation, so someone else verifies it.',
  'errors.FOUR_EYES_BASIS': 'You recorded this basis, so a second member confirms it.',
  'errors.REVIEW_NEEDED': 'This item needs a review by a second person before it is done.',
  'errors.OWN_ROLES': 'Nobody gives or takes back their own roles.',
  'errors.OWN_ACCOUNT': 'You cannot switch off your own account.',
  'errors.OWN_RESET': 'Ask another administrator to reset your password.',
  'errors.CANNOT_CONFIRM': 'You cannot confirm this request: a second person does.',
  'errors.CANNOT_WITHDRAW': 'You cannot withdraw this request.',
  'errors.ADMIN_NEEDS_SECOND': 'A firm administrator is made only when a second administrator confirms it. Ask for it instead.',
  'errors.INVITE_NEEDS_SECOND': 'A role from an invitation or a request is given only once a second person has confirmed it.',
  'errors.CYCLE_ITEM_STATE': 'A cycle item cannot go from {from} to {to}.',
  'errors.CYCLE_ITEM_CLOSED': 'This cycle item is closed, and it stays closed.',
  'errors.ITEM_HAS_NO_REVIEW': 'This item has no review.',
  'errors.SEND_BACK_REASON_REQUIRED': 'Say why the item goes back.',
  'errors.EVIDENCE_NEEDED': 'Link the evidence before the item is done.',
  'errors.LAST_EVIDENCE': 'This is the only evidence of a closed cycle item, so it stays.',
  'errors.FIRST_LINE_EVIDENCE': 'Evidence of first-line work must be a document stated to hold no customer-level data.',
  'errors.DOCUMENT_RETIRED': 'A retired document cannot be linked.',
  'errors.SERIES_REASON_REQUIRED': 'Pausing or retiring a series needs a reason.',
  'errors.SERIES_KEEPS_REVIEW': 'A series that needs a review or evidence keeps needing them. To change what it is, retire it with the reason and add a new series.',
  'errors.YEAR_END_NOT_CONFIRMED': 'The client’s financial year end ({yearEnd}) is assumed, not confirmed, and dates you chose rest on it. Confirm it, or tick that the dates may rest on the assumed one.',
  'errors.YEAR_END_CHANGED': 'The financial year end has changed meanwhile, to {yearEnd}. Read it again before you confirm it.',
  'errors.PROFILE_NEEDED': 'Record the client profile first.',
  'errors.RULE_DROPS_ANCHOR': 'This series follows {anchor}. A rule written by hand follows the calendar instead: tick “Follow the calendar instead” to say so.',
  'errors.CYCLE_ITEM_SAME_DATE': 'The item is due on that date already.',
  'errors.CYCLE_ITEM_OVERDUE_NOT_MOVED': 'An overdue item is not moved: it stays overdue until it is done.',
  'errors.CYCLE_ITEM_MOVE_PAST': 'An item is moved to today or a later date.',
  'errors.CYCLE_ITEM_AFTER_SERVICE_END': 'An item is never due after its service ends.',
  'errors.CYCLE_ITEM_MOVE_PAST_NEXT': 'An item is moved to a date before the next item of its series, which its rule gives on {date}.',
  'errors.CYCLE_ITEM_MOVE_PAST_HORIZON': 'An item is moved at most to {date}, the end of the planning horizon.',
  'errors.FOLLOW_UP_NEEDED': 'An action a client contact owns names the member of the firm who follows it up.',
  'errors.CONTACT_REPLACES_ITSELF': 'Name another contact to take over.',
  'errors.SERVICE_RESPONSIBLE_NEEDED': 'Before the client starts, name who at the client is responsible for {services}.',
  'errors.SKIP_NEEDS_SECOND': 'This item needs a review or evidence, so skipping it takes two people: a lead asks, and a reviewer other than that lead confirms.',
  'errors.CLIENT_ACT_STAYS_CLIENTS': 'This is the client’s own act under every delivery model: the client performs it, and the firm records it.',
  'errors.FOUR_EYES_SKIP': 'You asked to skip this item, so someone else confirms it.',
  'errors.SKIP_REQUEST_PENDING': 'A request to skip this item is waiting already.',
  'errors.SOURCE_ITEM_NOT_FOUND': 'No cycle item of this client has the code {code}.',
  'errors.SERIES_SERVICE_ENDED': 'The service of this series has ended, so the series stays paused.',
  'errors.SERIES_RETIRED': 'This series is retired, and it stays retired.',
  'errors.SERVICE_ENDED': 'That service has ended; a new series needs a current service.',
  'errors.TEMPLATE_STATE': 'A template moves from draft or baseline to adopted, or to retired.',
  'errors.TEMPLATE_RETIRED': 'This template is retired, and it stays retired.',
  'errors.TEMPLATE_ITEM_NOT_OFFERED': '“{title}” belongs to a service this client does not have.',
  'errors.WHEEL_ANCHOR_MISSING': '{what}: no date is recorded for {anchor}, so the rule cannot be placed. Record the date first.',
  'errors.WHEEL_ANCHOR_NOT_YEARLY': '{what}: a rule tied to a date of the client’s year can only be yearly, or every few years.',
  'errors.WHEEL_CALENDAR_OFFSET': '{what}: a rule on the calendar takes no days before or after.',
  'errors.WHEEL_LEAP_DAY': '{what}: a date on 29 February cannot come back every year. Choose another day.',
  'errors.WHEEL_TOO_OFTEN': '{what}: the rule falls too often for an annual cycle.',
  'errors.WHEEL_BAD_DATE': '{what}: one of the dates does not exist.',
  'errors.WHEEL_RULE_UNREADABLE': '{what}: the annual cycle cannot read this rule.',
  'errors.ACTION_ONE_OWNER': 'An action has one owner: a person at the firm or a contact at the client, not both.',
  'errors.ACTION_STATE': 'An action cannot go from {from} to {to}.',
  'errors.ACTION_CLOSED': 'This action is closed or cancelled, and it stays so.',
  'errors.REMEDIATION_NOTE_REQUIRED': 'Say what was done to remediate.',
  'errors.VERIFICATION_REASON_REQUIRED': 'Say why the remediation is not accepted.',
  'errors.SEVERITY_BASIS_REQUIRED': 'A new severity needs its basis.',
  'errors.ASSIGNEE_NOT_MEMBER': 'The person assigned must be a working member of this client.',
  'errors.ASSIGNEE_NEEDS_ROLE': 'The person assigned must be a member of this client with the role {role}.',
  'errors.CONTACT_NOT_CURRENT': 'The contact named must be a current contact of this client.',
  'errors.CONTACT_RETIRED': 'This person is no longer a contact.',
  'errors.LAST_LEAD': 'A client keeps at least one lead.',
  'errors.CLIENT_CLOSED': 'This client is closed.',
  'errors.CLIENT_STATE': 'A client cannot go from {from} to {to} here.',
  'errors.SERVICE_OVERLAP': 'This client already has a service of that kind in that period.',
  'errors.INTERIM_ROLE_NEEDED': 'An interim placement names the role the consultant holds at the client.',
  'errors.SERVICE_LINE_NEEDED': 'Name the line of defence for this service.',
  'errors.SERVICE_KIND_RETIRED': 'That kind of service is retired from the firm’s library.',
  'errors.SERVICE_ENDED_ALREADY': 'This service has ended already.',
  'errors.CUSTOMER_ROLE_NEEDS_BASIS': 'The customer-data role needs a live customer-data basis for this client.',
  'errors.CUSTOMER_ROLE_BY_LEAD': 'The customer-data role is given by a lead of the client, never through an invitation or an administrator’s request.',
  'errors.ACCOUNT_OFF': 'That account is switched off.',
  'errors.PARTNER_FIRST_LEAD': 'Only a partner becomes the first lead of a client they create; name another person.',
  'errors.FIRST_LEAD_NEEDED': 'A new client needs its first lead.',
  'errors.EMAIL_INVALID': 'Enter a valid email address.',
  'errors.EMAIL_TAKEN': 'An account with this email address exists already. Give it roles instead of inviting it.',
  'errors.INVITE_NO_ROLE': 'An invitation gives at least one role.',
  'errors.GRANT_NOT_ALLOWED': 'You may not give roles in that client.',
  'errors.GRANT_TWICE': 'A role is listed twice.',
  'errors.ALREADY_ADMIN': 'That person is a firm administrator already.',
  'errors.ADMIN_REQUEST_WAITING': 'A request to make that person a firm administrator is waiting already.',
  'errors.SSO_NO_PASSWORD': 'An account that signs in through single sign-on has no password to reset.',
  'errors.INVITE_NO_LONGER_VALID': 'This invitation or request is no longer valid: the people or the client it depends on have changed.',
  'errors.TERMS_NOT_ACCEPTED': 'Accept the terms and the privacy notice first.',
  'errors.BASIS_DECIDED_ALREADY': 'This basis is confirmed or retired already.',
  'errors.BASIS_RETIRED_ALREADY': 'This basis is retired already.',
  'errors.BASIS_SERVICE_ENDED': 'A basis serves a current service.',
  'errors.BASIS_DOCUMENTS_RETIRED': 'The instruction and the impact assessment must be current documents.',
  'errors.BASIS_FIELDS_NEEDED': 'List the fields the basis allows for {category}.',
  'errors.BASIS_PURPOSE_FIELDS_NEEDED': 'List the fields of {category} for {purpose}.',
  'errors.BASIS_RETENTION_NEEDED': 'Give the retention for {category} in whole months, 1 to 60.',
  'errors.DOCUMENT_STORED_ALREADY': 'This file is stored already as {code}. Link that document instead.',
  'errors.DOCUMENT_RETIRED_ALREADY': 'This document is retired already.',
  'errors.FILE_EXECUTABLE': 'Programs and scripts are not stored. Upload a document instead.',
  'errors.FILE_SCRIPT': 'Script files are not stored.',
  'errors.FILE_SCRIPTED_PAGE': 'Web pages with scripts are not stored. Save the page as a PDF and upload that.',
  'errors.COMMENT_NOT_YOURS': 'Only the author withdraws a comment.',
  'errors.AI_ENGINE_NOT_ALLOWED': 'This installation does not send client data to {engines}.',

  // ── Onboarding and the current state (docs/ONBOARDING-CURRENT-STATE.md) ──────────────────────────────────────
  'documents.textForQuotes': 'Text for quotes',
  'textStatus.waiting': 'waiting for the system to read the text',
  'textStatus.extracted': 'text read: {chars} characters, {pages} pages',
  'textStatus.not_checkable': 'no text to check (a scan or an image: there is no text recognition)',
  'textStatus.failed': 'the text could not be read',
  // IP4b: decks, transcripts and e-mails
  'textStatus.slides': 'text read: {slides} slides, {notes} of them with speaker notes',
  'textStatus.cues': 'text read: {cues} timed passages, {speakers} speakers',
  'textStatus.email': 'text read: an e-mail with {attachments} attachments',
  'textStatus.emailOne': 'text read: an e-mail with 1 attachment',
  'quoteFind.title': 'Find a quote in the text',
  'quoteFind.field': 'Words from the document',
  'quoteFind.hint': 'The system looks for exactly these words in the text read from the file. Nothing is saved.',
  'quoteFind.check': 'Look for the words',
  'quoteFind.foundAt': 'Found {where}.',
  'quoteFind.foundInText': 'Found in the text.',
  'quoteFind.not_found': 'Not found in the text read from this file.',
  'quoteFind.not_checkable': 'This file has no text that can be checked.',
  'locator.page': 'on page {page}',
  'locator.sheet': 'on sheet {page}',
  'locator.slide': 'on slide {slide}',
  'locator.notes': 'in the speaker notes of slide {slide}',
  'locator.cue': 'at {time}',
  'locator.cueSpeaker': 'at {time}, said by {speaker}',
  'locator.headers': 'in the e-mail’s header lines',
  'locator.body': 'in the e-mail’s text',
  'attachments.title': 'Attachments in this e-mail',
  'attachments.note': 'An attachment becomes a document of its own only when you store it. Until then it is only listed here.',
  'attachments.none': 'The e-mail has no attachments.',
  'attachments.unnamed': 'attachment {n} (no name)',
  'attachments.inline': 'shown inside the e-mail',
  'attachments.storedAs': 'stored as {code}',
  'attachments.store': 'Store as its own document',
  'attachments.stored': 'The attachment is stored as its own document.',
  'documents.takenFrom': 'Taken from',
  'documents.fromAttachment': 'attachment {n} of {code}',
  'transcript.labelsFound': 'The file names {n} speakers. Choose who each one is: nothing is chosen for you.',
  'transcript.unmapped': 'Choose a participant for each speaker, or remove the line.',
  'transcript.removeSpeaker': 'Remove',
  'errors.ATTACHMENT_NOT_READ': 'The system has not read the e-mail yet, so its attachments are not listed. Try again in a moment.',
  'auditAction.document.store_attachment': 'Stored an e-mail’s attachment as a document',
  'auditField.fromEmail': 'taken from the e-mail',
  'auditField.fromAttachment': 'attachment number',
  'errors.QUOTE_NOT_FOUND': 'The quote is not in {document}. Copy the words exactly as they stand there.',
  'errors.QUOTE_TOO_LONG': 'A quote is at most {max} characters.',
  'auditAction.job.document.extract': 'Read the text of a document',
  'auditField.extractor': 'read with',
  'auditField.textStatus': 'text',
  'auditField.textReason': 'why there is no text',
  'auditField.chars': 'characters',
  'auditField.extractedAt': 'text read on',
  'textState.extracted': 'read',
  'textState.not_checkable': 'cannot be checked',
  'textState.failed': 'could not be read',
  'auditAction.document.extract': 'Asked the system to read a document’s text',
  'record.role': 'Role',
  'record.startCheck': 'Start check',
  'record.checkItem': 'Item of a start check',
  'record.inherited': 'Inherited position',
  'record.documentText': 'Text of a document',
  'auditField.reason': 'reason',
  'auditField.roleKind': 'role',
  'auditField.roleMode': 'how the firm takes the role',
  'auditField.firmPerson': 'firm member',
  'auditField.clientHolder': 'holder at the client',
  'auditField.delegatedTasks': 'tasks the firm performs',
  'auditField.roleBasis': 'legal basis',
  'auditField.roleBasisNote': 'note on the basis',
  'auditField.roleBasisDocument': 'document behind the basis',
  'auditField.appointmentDocument': 'appointment decision',
  'auditField.notifiedOn': 'notified on',
  'auditField.notificationDocument': 'notice',
  'auditField.evidenceDocument': 'evidence',
  'auditField.exceptionReason': 'reason for the exception',
  'auditField.exceptionRequestedBy': 'exception asked for by',
  'auditField.exceptionRequestedAt': 'exception asked for on',
  'auditField.exceptionConfirmedBy': 'exception confirmed by',
  'auditField.exceptionConfirmedAt': 'exception confirmed on',
  'auditField.locator': 'where in the document',
  'auditField.quote': 'quote',
  'auditField.quoteCheck': 'quote check',
  'auditField.asOf': 'as of',
  'auditField.openOrders': 'open supervisor orders',
  'auditField.openOrdersSource': 'source of the open orders',
  'auditField.openOrdersNote': 'note on the orders',
  'auditField.alertsUnhandled': 'alerts not handled',
  'auditField.alertsUnhandledSource': 'source of the alert count',
  'auditField.alertsOldestDays': 'oldest alert, in days',
  'auditField.alertsOldestDaysSource': 'source of the alert age',
  'auditField.reviewsOverdue': 'customer reviews overdue',
  'auditField.reviewsOverdueSource': 'source of the overdue reviews',
  'auditField.customersNotScreened': 'customers not screened',
  'auditField.customersNotScreenedSource': 'source of the screening count',
  'auditField.customersWithoutRiskClass': 'customers without a risk class',
  'auditField.customersWithoutRiskClassSource': 'source of the risk-class count',
  'auditField.openAuditFindings': 'open audit findings',
  'auditField.openAuditFindingsSource': 'source of the audit findings',
  'auditField.auditFindingsDocument': 'audit findings document',
  'auditField.knownBreaches': 'known breaches',
  'auditField.knownBreachesSource': 'source of the breaches',
  'auditField.knownBreachesNote': 'note on the breaches',
  'auditField.acknowledgedBy': 'acknowledged by',
  'auditField.acknowledgementDocument': 'acknowledgement',
  'auditField.acknowledgedOn': 'acknowledged on',
  'auditField.acknowledgementRecordedBy': 'acknowledgement recorded by',
  'auditField.acknowledgementRecordedAt': 'acknowledgement recorded on',
  'roleMode.supported': 'supported (the client holds it)',
  'roleMode.held': 'held by the firm',
  'roleMode.interim': 'covered by the firm ad interim',
  'checkState.open': 'open',
  'checkState.done': 'done',
  'checkState.not_applicable': 'not applicable',
  'checkState.exception': 'exception',
  'itemState.unchecked': 'not checked',
  'itemState.found': 'found',
  'itemState.missing': 'missing',
  'itemState.not_applicable': 'not applicable',
  'quoteCheck.found': 'found in the document',
  'quoteCheck.not_checkable': 'cannot be checked (no text)',
  'countSource.client_report': 'the client\'s report',
  'countSource.system_extract': 'a system extract',
  'countSource.estimate': 'an estimate',
  'auditAction.mandate.sync_checks': 'Made the start checks',
  'auditAction.role.add': 'Recorded a role',
  'auditAction.role.update': 'Changed a role',
  'auditAction.role.confirm_basis': 'Confirmed the legal basis of a role',
  'auditAction.role.retire': 'Ended a role',
  'auditAction.mandate_check.done': 'Recorded a start check as done',
  'auditAction.mandate_check.not_applicable': 'Recorded a start check as not applicable',
  'auditAction.mandate_check.exception_request': 'Asked for an exception to a start check',
  'auditAction.mandate_check.exception_confirm': 'Confirmed an exception to a start check',
  'auditAction.mandate_check.exception_withdraw': 'Withdrew or declined an exception',
  'auditAction.mandate_check.item': 'Recorded an item of a start check',
  'auditAction.inherited.record': 'Recorded the inherited position',
  'auditAction.inherited.acknowledge': 'Recorded the client\'s acknowledgement of the inherited position',
  'auditAction.library.refs_checked': 'Recorded that a library file\'s references were checked',
  'auditAction.library.adopt': 'Adopted a library file',
  'errors.FOUR_EYES_ADOPTION': 'You checked this file’s references, so another desk member adopts it.',
  'errors.LIBRARY_REFS_CHECKED_ALREADY': 'The references of this version are recorded as checked already.',
  'errors.LIBRARY_REFS_NOT_CHECKED': 'Record that the references were checked before the file is adopted.',
  'errors.DOCUMENT_NOT_USABLE': 'Name a current document of this client that holds no customer-level data.',
  'errors.ROLE_NOT_FOR_SERVICE': 'That role does not belong to a service of this kind.',
  'errors.HOME_JURISDICTION_NEEDED': 'Record the client’s home jurisdiction in the profile first: the client’s roles and its maps of practices depend on it.',
  'errors.MAP_NOT_FOR_JURISDICTION': 'No map of practices is written for {jurisdiction} yet, so this analysis cannot be planned. The plan shows which maps exist for which jurisdictions.',
  'errors.BASELINE_RECORD_UNVERIFIABLE': 'The frozen analysis cannot be checked against its snapshot, so its picture is not shown.',
  'errors.MAP_JURISDICTION_UNSTATED': 'This map of practices states no jurisdiction, so it joins only an analysis already planned on it.',
  'errors.PRACTICE_ALREADY_IN_SCOPE': 'This practice is in the analysis already.',
  'errors.ROLE_OUTSIDE_SERVICE': 'A role starts within the period of its service.',
  'errors.ROLE_RULE_MISSING': 'No rule says whether a provider may take this role in {jurisdiction} on {date}. The regulatory desk records one first.',
  'errors.ROLE_NOT_HOLDABLE_HERE': 'A provider may not take this role in {jurisdiction} from {date} in the way chosen: {mode}. The rule and its source are shown with the role.',
  'errors.ROLE_BASIS_NEEDED': 'The rule allows this way of taking the role ({mode}) only on a basis it accepts. Choose the basis and say why it holds.',
  'errors.ROLE_BASIS_NOT_NEEDED': 'This role needs no basis here.',
  'errors.ROLE_RETIRED': 'This role has ended, and it stays ended.',
  'errors.FOUR_EYES_ROLE_BASIS': 'You recorded this basis, so another reviewer confirms it.',
  'errors.TASK_NOT_OUTSOURCEABLE': 'The task {task} may not be handed to the firm under the rule in force.',
  'errors.CHECK_DECIDED': 'This start check is decided, and it stays so.',
  'errors.CHECK_EVIDENCE_NEEDED': 'A start check is done with its evidence.',
  'errors.CHECK_ITEMS_OPEN': 'Every item of this check is found or not applicable before it is done.',
  'errors.CHECK_CLOSES_WITH_ACKNOWLEDGEMENT': 'This check closes with the client’s acknowledgement of the inherited position.',
  'errors.INHERITED_ALWAYS_APPLIES': 'The inherited position always applies: it is acknowledged, or the client starts on an exception.',
  'errors.EXCEPTION_NOT_ASKED': 'An exception is confirmed only once a lead has asked for it.',
  'errors.FOUR_EYES_EXCEPTION': 'You asked for this exception, so another reviewer confirms it.',
  'errors.EXCEPTION_ASKED_ALREADY': 'An exception is waiting on this check already.',
  'errors.INHERITED_ACKNOWLEDGED': 'An acknowledged inherited position never changes.',
  'errors.INHERITED_ACKNOWLEDGED_BY_OWNER': 'The inherited position is acknowledged by the person at the client responsible for the service.',
  'errors.DATE_IN_FUTURE': 'The date is today or earlier.',
  'errors.MANDATE_CHECK_OPEN': 'Start checks are open. The client starts once each is done, not applicable or excepted by two people.',
  'errors.ROLE_NO_BASIS_TO_CONFIRM': 'This role has no basis waiting for a confirmation.',
  'errors.NO_EXCEPTION_REQUEST': 'No exception is waiting on this check.',
  'errors.QUOTE_NEEDS_DOCUMENT': 'A quote names the document it is taken from.',
  'errors.INHERITED_NOT_RECORDED': 'Record the inherited position first.',
  'nav.mandate': 'Mandate',
  'nav.mandate.answers': 'Which roles does the firm take for this client, and what must be in place before it starts?',
  'mandate.title': 'Mandate',
  'mandate.library.proposal': '{what}: Olivia’s proposal, not yet adopted by your regulatory desk.',
  'mandate.library.adopted': '{what}: adopted by {name} on {date}.',
  'mandate.noHome': 'Record the client’s home jurisdiction in the profile: the rules of each role depend on it.',
  'mandate.noServices': 'No services yet. Record the services first: the mandate is set per service.',
  'mandate.from': 'From',
  'mandate.lead': 'Lead at the firm',
  'mandate.responsible': 'Responsible at the client',
  'mandate.noResponsible': 'nobody named yet',
  'mandate.roles': 'Roles',
  'mandate.noRoleKinds': 'No role is defined for this kind of service.',
  'mandate.appointedBy': 'Appointed by',
  'mandate.allowed.never': 'not allowed',
  'mandate.allowed.with_basis': 'only on a recorded basis a reviewer confirms',
  'mandate.allowed.yes': 'allowed',
  'mandate.ruleNone': 'No rule says whether a provider may take this role in {jurisdiction} today; the regulatory desk records one first.',
  'mandate.ruleNext': 'From {date} another rule applies:',
  'mandate.sourcePage': 'the text, read on {date}',
  'mandate.tasksFirm': 'Tasks the firm performs',
  'mandate.tasksStay': 'Stay with the client',
  'mandate.oversight': 'The client’s oversight of the firm',
  'mandate.notice': 'Notice to the authority once appointed',
  'mandate.clientActs': 'The client’s own acts',
  'mandate.clientActs.count': '{n} of {total} in the annual cycle',
  'mandate.clientAct.inCycle': 'in the annual cycle ({code})',
  'mandate.clientAct.notInCycle': 'not in the annual cycle',
  'mandate.clientActs.apply': 'Apply the client type’s template',
  'mandate.addRole': 'Record the role',
  'mandate.form.modeNever': '{mode} (not allowed here)',
  'mandate.form.nobody': 'nobody',
  'mandate.form.basis': 'Basis the rule accepts',
  'mandate.form.basisNote': 'Why the basis holds',
  'mandate.form.basisNeedsReviewer': 'A reviewer who did not record the basis confirms it before the role counts.',
  'mandate.role.since': 'from {date}, {jurisdiction}',
  'mandate.role.basisConfirmed': 'confirmed by {name}',
  'mandate.role.confirmBasis': 'Confirm the basis',
  'mandate.role.retire': 'End the role',
  'mandate.role.appointment': 'Appointment decision (document)',
  'mandate.role.recordAppointment': 'Record the appointment',
  'mandate.role.recordNotice': 'Record the notice',
  'roleState.basis_not_confirmed': 'basis not yet confirmed by a reviewer',
  'roleState.appointment_not_recorded': 'appointment not recorded',
  'roleState.notice_not_recorded': 'notice not recorded',
  'roleState.rule_changes': 'the rule changes on {date}',
  'roleState.must_end_or_become_supported': 'the rule no longer allows this since {date}: end the role, or record it as supported',
  'roleState.tasks_rule_changes': 'from {date} the rule no longer lists: {tasks}',
  'mandate.checks': 'Start checks',
  'mandate.checks.make': 'Make the start checks',
  'mandate.checks.notMade': '{n} start checks the services call for are not made yet.',
  'mandate.checks.none': 'No start check applies to this service.',
  'mandate.checks.onDemand': 'A service on demand calls for no start checks.',
  'mandate.check.beforeStart': 'before the start',
  'mandate.check.after': 'after the start',
  'mandate.check.from': 'in force from {date}',
  'mandate.check.actor.firm': 'the firm checks',
  'mandate.check.actor.client_act': 'the client’s own act',
  'mandate.check.closesWith': 'Closes with',
  'mandate.check.doneBy': 'Done with {code}, by {name} on {date}',
  'mandate.check.notApplicableWhy': 'Not applicable: {reason}',
  'mandate.check.exceptionAsked': 'Exception asked for by {name}: {reason}',
  'mandate.check.exceptionConfirmed': 'Confirmed by {name}.',
  'mandate.check.items': '{found} of {total} found',
  'mandate.check.missing': 'missing or not checked: {items}',
  'mandate.check.showItems': 'Show the items',
  'mandate.check.inheritedNote': 'This check closes when the client acknowledges the inherited position.',
  'mandate.check.done': 'Record as done',
  'mandate.check.notApplicable': 'Not applicable',
  'mandate.check.askException': 'Ask for an exception',
  'mandate.check.confirmException': 'Confirm the exception',
  'mandate.check.withdrawException': 'Withdraw or decline the exception',
  'mandate.check.exceptionNote': 'A lead asks for an exception, with the reason; a reviewer who is not that lead confirms it.',
  'mandate.item.record': 'Record the item',
  'mandate.item.result': 'Result',
  'mandate.item.locator': 'Where (clause or page)',
  'mandate.item.quote': 'Quote',
  'mandate.item.quoteHint': 'At most 300 characters, copied from the document: it is checked against the document’s text.',
  'mandate.item.recordedBy': 'recorded by {name}',
  'inherited.title': 'Inherited position',
  'inherited.intro': 'What the firm takes over, in a short record that the person responsible at the client acknowledges with a signed letter or minute.',
  'inherited.noServices': 'No service runs a function for this client, so there is no position to take over.',
  'inherited.none': 'Not recorded yet.',
  'inherited.line': '{code} · what the firm takes over, as of {date}',
  'inherited.count.open_orders': 'Open supervisor orders',
  'inherited.count.alerts_unhandled': 'Alerts not handled',
  'inherited.count.alerts_oldest_days': 'Oldest unhandled alert, in days',
  'inherited.count.reviews_overdue': 'Customer reviews overdue',
  'inherited.count.customers_not_screened': 'Customers not screened against lists',
  'inherited.count.customers_without_risk_class': 'Customers without a risk class',
  'inherited.count.open_audit_findings': 'Open audit findings',
  'inherited.count.known_breaches': 'Known breaches',
  'inherited.unknown': 'the client could not say',
  'inherited.source': 'Source of the figure',
  'inherited.valueHint': 'Leave a figure empty when the client could not say: it is shown as unknown, never as zero.',
  'inherited.ordersNote': 'The orders, briefly',
  'inherited.breachesNote': 'The breaches, briefly',
  'inherited.record': 'Record the position',
  'inherited.edit': 'Change the position',
  'inherited.save': 'Save the position',
  'inherited.acknowledge': 'Record the acknowledgement',
  'inherited.ackHint': 'The person responsible at the client for this service.',
  'inherited.ackDocument': 'Signed letter or minute',
  'inherited.acknowledged': 'acknowledged by {name} on {date} ({code})',
  'inherited.notAcknowledged': 'not acknowledged yet',
  'inherited.backlogHint': 'Each backlog above zero offers an action. Nothing is raised by itself: the action exists once you submit it.',
  'inherited.raiseAction': 'Raise an action for: {what} ({n})',
  'inherited.actionTitle': 'Clear the inherited backlog: {what} ({n})',
  'onboarding.step.mandate': 'Mandate',
  'onboarding.step.inherited': 'Inherited position',
  'onboarding.mandateIntro': 'For each service: the roles the firm takes, with the rule in force for the client’s jurisdiction; the client’s own acts; and the start checks.',
  'onboarding.inheritedIntro': 'What the firm takes over when it starts, acknowledged by the client.',
  'onboarding.startChecksOpen': 'Start checks still open: {n}. The client starts once each is done, not applicable, or excepted by two people.',
  'onboarding.startChecksNone': 'No start check is open.',
  'library.files': 'Rule files',
  'library.file.roles': 'Roles and their rules per jurisdiction',
  'library.file.checks': 'Start checks',
  'library.noFiles': 'No rule file is loaded.',
  'library.refsChecked': 'References checked by {name} on {date}',
  'library.refsNotChecked': 'References not checked yet',
  'library.adoptedFile': 'Adopted by {name} on {date}',
  'library.notAdoptedFile': 'Olivia’s proposal, not yet adopted by your regulatory desk',
  'library.recordRefsChecked': 'Record that the references were checked',
  'library.adoptFile': 'Adopt this version',
  'library.adoptOther': 'You checked the references: another desk member adopts this version.',
  'library.filesNote': 'Two desk members: one checks the references against the consolidated texts, another adopts that version.',
  'people.roles': 'Roles for clients',
  'people.rolesAbove': '{n}, above the firm’s threshold of {max}',
  'people.rolesNote': 'Roles held, covered or supported for clients, counted over the clients you can see.',
  'settings.roleCapacity': 'Warn when one person takes more roles for clients than',
  'settings.roleCapacityHint': 'A warning on the people page, never a refusal.',
  'services.mandateLink': 'Roles, start checks and the inherited position',
  'nav.business': 'Business',
  'nav.business.answers': 'What does the client do, how much of it, and how exposed is it?',
  'onboarding.step.business': 'Business',
  'onboarding.businessIntro': 'The client’s business in figures: what it offers, how much, where, through which systems and models. Missing figures are allowed and shown as missing.',
  'library.file.business': 'Fields of the business profile',
  'library.file.inherent': 'Inherent-risk rule',
  'library.file.perimeter': 'Licence perimeter rule',
  'record.figure': 'Figure',
  'record.exposure': 'Exposure',
  'record.feature': 'Feature of the business',
  'record.system': 'System',
  'record.model': 'Model',
  'business.title': 'Business profile',
  'business.aggregatesOnly': 'Figures and counts only: nothing here names or identifies a customer.',
  'business.features': 'What the client offers',
  'business.noFeatures': 'No feature recorded yet.',
  'business.featureAdd': 'Add a feature',
  'business.featureRemove': 'No longer offered',
  'business.perimeter': 'Within the licence?',
  'business.perimeterNone': 'No feature outside what the client’s institution types are authorised for.',
  'business.perimeterQuestion': 'possibly outside the licence',
  'business.perimeterNote': 'A question for a person to look into, never a finding: the rule decides nothing.',
  'business.figures': 'Figures',
  'business.figuresIntro': 'Each figure is recorded by one member and confirmed by another. A figure the inherent-risk rule reads counts only once confirmed.',
  'business.readByRule': 'read by the inherent-risk rule',
  'business.notRecorded': 'not recorded',
  'business.periodYear': 'for the year to {date}',
  'business.periodPoint': 'at {date}',
  'business.periodEndYear': 'Last day of the twelve months',
  'business.periodEndPoint': 'Counted on',
  'business.valueIn.count': 'Number',
  'business.valueIn.eur': 'Amount in euro',
  'business.valueIn.pct': 'Per cent (0 to 100)',
  'business.valueIn.fte': 'Full-time equivalents',
  'business.unit.eur': 'EUR {value}',
  'business.unit.pct': '{value} %',
  'business.unit.fte': '{value} FTE',
  'business.recordFigure': 'Record the figure',
  'business.figureConfirmNote': 'Another member confirms the figure; changing it later clears the confirmation.',
  'business.confirmedBy': 'confirmed by {name} on {date}',
  'business.toConfirm': 'recorded by {name}, to be confirmed by another member',
  'business.confirm': 'Confirm',
  'business.retire': 'No longer holds',
  'business.inherent': 'Inherent risk per AML domain',
  'business.inherentIntro': 'Computed by the inherent-risk rule from the confirmed figures and the features, as of {date}. Olivia’s proposal; a person decides the band in force when the baseline is planned.',
  'business.band': 'band {band}',
  'business.notRated': 'not rated: band {band} assumed',
  'business.bandReason': 'Band {band}:',
  'business.baseBand': 'No condition of the rule raises this domain.',
  'business.missing': 'Missing: {fields}',
  'business.missingWhy.not_recorded': 'not recorded',
  'business.missingWhy.not_confirmed': 'not confirmed',
  'business.missingWhy.no_common_period': 'not for the same day as the figure it is compared with',
  'business.inherentNote': 'A domain that is not rated is treated as band 3 for its targets and priority, never lower.',
  'business.exposures': 'Exposures',
  'business.noExposures': 'No exposure recorded yet.',
  'business.addExposure': 'Record an exposure',
  'business.exposureCode': 'Code',
  'business.exposureCodeHint.country': 'Two letters, for example SE',
  'business.exposureCodeHint.currency': 'Three letters, for example EUR',
  'business.exposureCodeHint.channel': 'A short code, for example agents',
  'business.exposureCodeHint.counterparty_type': 'A short code, for example merchants',
  'business.systems': 'Key systems',
  'business.noSystems': 'No system recorded yet.',
  'business.addSystem': 'Record a system',
  'business.vendor': 'from {vendor}',
  'business.inHouse': 'built in-house',
  'business.vendorHint': 'Leave empty when built in-house.',
  'business.models': 'Financial-crime models',
  'business.modelsIntro': 'The client’s monitoring, screening and risk-rating logic, recorded whether or not the client calls it a model.',
  'business.noModels': 'No model recorded yet.',
  'business.addModel': 'Record a model',
  'business.editModel': 'Change',
  'business.saveModel': 'Save the model',
  'business.lastValidated': 'last validated on {date}',
  'business.neverValidated': 'never validated',
  'business.lastChanged': 'significantly changed on {date}',
  'business.vendorDefaults': 'vendor’s default settings assessed: {state}',
  'business.lastValidatedHint': 'Leave empty when it was never validated.',
  'business.modelConfirmNote': 'Another member confirms the model; changing it later clears the confirmation.',
  'exposureKind.country': 'country',
  'exposureKind.channel': 'channel',
  'exposureKind.counterparty_type': 'type of counterparty',
  'exposureKind.currency': 'currency',
  'exposureMeasure.customers': 'customers',
  'exposureMeasure.volume_share': 'share of the number of transactions',
  'exposureMeasure.value_share': 'share of the transaction value',
  'systemKind.core': 'core system',
  'systemKind.onboarding': 'onboarding',
  'systemKind.screening': 'screening against lists',
  'systemKind.monitoring': 'transaction monitoring',
  'systemKind.case_management': 'case management',
  'systemKind.reporting': 'reporting',
  'systemKind.other': 'other',
  'modelKind.transaction_monitoring': 'transaction monitoring',
  'modelKind.screening_customer': 'screening of customers',
  'modelKind.screening_payment': 'screening of payments',
  'modelKind.customer_risk_rating': 'customer risk rating',
  'modelKind.fraud': 'fraud detection',
  'modelKind.adverse_media': 'adverse media',
  'modelKind.ai_tool': 'AI tool',
  'modelKind.other': 'other',
  'vendorDefaults.yes': 'yes',
  'vendorDefaults.no': 'no',
  'vendorDefaults.unknown': 'unknown',
  'modelState.validation_due': 'never validated: a validation is due',
  'modelState.changed_since_validation': 'changed since its last validation',
  'modelState.not_confirmed': 'not confirmed',
  'modelState.vendor_defaults_not_assessed': 'vendor’s defaults not assessed',
  'auditField.businessField': 'field',
  'auditField.periodEnd': 'period end',
  'auditField.value': 'value',
  'auditField.countSource': 'source',
  'auditField.note': 'note',
  'auditField.exposureKind': 'kind of exposure',
  'auditField.exposureMeasure': 'measure',
  'auditField.exposureBasis': 'how it was counted',
  'auditField.feature': 'feature',
  'auditField.setBy': 'set by',
  'auditField.setAt': 'set on',
  'auditField.systemKind': 'kind of system',
  'auditField.vendor': 'vendor',
  'auditField.modelKind': 'kind of model',
  'auditField.versionLabel': 'version',
  'auditField.system': 'system',
  'auditField.inUseSince': 'in use since',
  'auditField.lastValidatedOn': 'last validated on',
  'auditField.lastValidationRef': 'validation report',
  'auditField.lastSignificantChangeOn': 'last significant change on',
  'auditField.changeNote': 'what changed',
  'auditField.openFindings': 'open findings',
  'auditField.openFindingsSource': 'source of the open findings',
  'auditField.vendorDefaultsAssessed': 'vendor’s defaults assessed',
  'auditAction.business.figure': 'Recorded a figure of the business profile',
  'auditAction.business.exposure': 'Recorded an exposure',
  'auditAction.business.confirm': 'Confirmed a record of the business profile',
  'auditAction.business.retire': 'Recorded that a business record no longer holds',
  'auditAction.feature.set': 'Recorded a feature of the business',
  'auditAction.feature.retire': 'Recorded that a feature is no longer offered',
  'auditAction.system.add': 'Recorded a system',
  'auditAction.system.update': 'Changed a system',
  'auditAction.model.add': 'Recorded a model',
  'auditAction.model.update': 'Changed a model',
  'errors.FOUR_EYES_BUSINESS': 'You recorded this, so another member confirms it.',
  'errors.BUSINESS_CONFIRMED_ALREADY': 'This is confirmed already; a change clears the confirmation.',
  'errors.BUSINESS_RETIRED': 'This record no longer holds, and it stays so. Record it again if needed.',
  'errors.BUSINESS_FIELD_UNKNOWN': 'The business profile has no such field.',
  'errors.FIGURE_WHOLE_NUMBER': 'A count is a whole number.',
  'errors.FIGURE_SHARE_TOO_HIGH': 'A share is at most 100 per cent.',
  'errors.MODEL_DATES_ORDER': 'A model is validated or changed after it came into use.',
  'errors.SYSTEM_NOT_CURRENT': 'Name a current system of this client.',
  'errors.FEATURE_SET_ALREADY': 'The client offers this already.',
  'library.file.mapAml': 'AML practices for payment and e-money institutions',
  'library.file.mapGovernance': 'Governance practices for payment and e-money institutions',
  'library.file.requests': 'Catalogue of document requests',
  'library.file.guides': 'Interview guides',
  'library.file.maturity': 'Maturity rule',
  'library.file.duty': 'Rule for duty conclusions',
  'library.file.instruments': 'Register of legal instruments',
  'library.pin': '{name}, version {version}',
  'library.maps': 'Practices for the current-state analysis',
  'library.noMaps': 'No map of practices is loaded.',
  'library.mapsCounts': 'With {requests} document requests and {questions} interview questions that point at the practices.',
  'library.mapLine': '{practices} practices, {shall} of them legal duties, {key} key controls · version {version}',
  'library.showPractices': 'Show the practices',
  'library.keyControl': 'key control',
  'library.weight.shall': 'legal duty',
  'library.weight.good_practice': 'good practice',
  'library.refFrom': 'from {date}',
  'library.refUntil': 'until {date}',
  'nav.currentState': 'Current state',
  'nav.currentState.answers': 'How mature is the client\'s compliance today, and what is being gathered to show it?',
  'onboarding.step.currentState': 'Current state',
  'onboarding.currentStateIntro': 'Plan the current-state analysis: the scope from the services, the as-of date, who prepares and who reviews, the draft request and the interviews. It does not hold up the start.',
  'onboarding.currentStateLead': 'A lead of this client plans the current-state analysis.',
  'record.assessment': 'Current-state analysis',
  'analysis.notFound': 'There is no analysis {code} for this client.',
  'analysis.notFound.list': 'All analyses of this client',
  'record.scope': 'Practice in scope',
  'record.band': 'Inherent-risk band',
  'record.request': 'Document request',
  'record.requestLine': 'Line of a request',
  'record.inquiry': 'Inquiry',
  'record.participant': 'Participant',
  'record.statement': 'Statement',
  'record.statementSource': 'Confidential source',
  'record.statementLink': 'Statement on a practice',
  'record.inconsistency': 'Inconsistency',
  'assessment.title': 'Current state',
  'assessment.intro': 'The current-state analysis: planned from the client\'s services, gathered from documents and interviews. The levels are computed later, from the evidence, by rule.',
  'assessment.list': 'Analyses',
  'assessment.none': 'No analysis planned yet.',
  'assessment.asOfLine': 'as of {date} · {n} practices in scope',
  'assessment.plan': 'Plan a current-state analysis',
  'assessment.create': 'Create the plan',
  'assessment.windowHint': 'The months before the as-of date the evidence may cover.',
  'assessment.reviewerHint': 'Only reviewers who lead none of this client\'s services and hold none of its roles are offered.',
  'assessment.reviewersNotOffered': 'Not offered as reviewer, because they lead a service or hold a role here: {names}.',
  'assessment.scopePreview': 'In scope from the client\'s services',
  'assessment.mapPractices': '{n} of {total} practices ({domains})',
  'assessment.mapNotServed': 'outside the firm\'s services for this client',
  'assessment.mapNotForTypes': 'not for this client\'s institution types',
  'assessment.mapWrittenFor': 'written for {written}',
  'assessment.mapNotForJurisdiction': "written for {written}, not for this client's home jurisdiction ({home})",
  'assessment.noHomeJurisdiction': "Record the client's home jurisdiction on the profile first: each map is written for the jurisdictions it names.",
  'assessment.bandsPreview': 'Inherent risk by AML domain:',
  'assessment.notRatedShort': '{band} assumed',
  'assessment.requestPreview': 'Draft request: {core} core items ticked, {supporting} supporting items offered.',
  'assessment.gather': 'Start gathering',
  'assessment.refresh': 'Bring the scope up to date',
  'assessment.withdraw': 'Withdraw the analysis',
  'assessment.bands': 'Inherent risk',
  'assessment.bandsIntro': 'As the inherent-risk rule computed it at planning, from the confirmed business profile. A person may override a band with a reason; a reviewer confirms the band in force.',
  'assessment.bandsNote': 'Used only for the default targets and the priority of gaps; a band never lowers a priority below what band 3 gives.',
  'assessment.overridden': 'overridden from {from} by {name}: {reason}',
  'assessment.bandConfirmed': 'confirmed by {name}',
  'assessment.bandToConfirm': 'a reviewer confirms the band in force',
  'assessment.bandMissing': '{n} figures missing or not confirmed',
  'assessment.override': 'Override the band',
  'assessment.confirmBand': 'Confirm the band',
  'assessment.scope': 'Scope',
  'assessment.scopeCount': '{n} of {total} practices in scope',
  'assessment.scopeDecided': '{name}: {reason}',
  'assessment.scopeConfirmed': 'confirmed by {name}',
  'assessment.scopeToConfirm': 'a reviewer confirms this for a legal duty',
  'assessment.takeOut': 'Take out of scope',
  'assessment.putBack': 'Put back in scope',
  'assessment.confirmScope': 'Confirm',
  'assessment.notApplying': '{n} practices do not apply to this client',
  'assessment.notApplying.one': '{n} practice does not apply to this client',
  'assessment.notOnSimplified': 'not for an entity on the simplified framework of DORA Art 16',
  'assessment.onlyForTypes': 'only for: {types}',
  'assessment.doesNotApply': 'only with {what}',
  'assessment.requests': 'Document requests',
  'assessment.noRequests': 'No request yet.',
  'assessment.requestLine': '{received} of {lines} received · sent {date}',
  'assessment.newRequest': 'New request',
  'assessment.interviewPlan': 'Interview plan',
  'assessment.interviewRole': '{role}: {n} questions',
  'assessment.inquiries': 'Interviews, walkthroughs and workshops',
  'assessment.noInquiries': 'None yet.',
  'assessment.statementsCount': '{n} statements',
  'assessment.newInquiry': 'New interview or walkthrough',
  'assessment.inconsistencies': 'Inconsistencies',
  'assessment.disposedBy': 'disposed of by {name}: {disposition}',
  'assessment.dispose': 'Dispose of it',
  'appliesIf.modelAny': 'a model in the inventory',
  'appliesIf.modelVendor': 'a vendor\'s model in the inventory',
  'request.send': 'Record as sent',
  'request.close': 'Close the request',
  'request.checklist': 'Download the checklist (Excel)',
  'request.letter': 'Download the letter (Word)',
  'request.lines': 'What is asked for',
  'request.addItem': 'Add from the catalogue',
  'request.addOwn': 'Add a line of our own',
  'request.attach': 'Link the received document',
  'request.markReceived': 'Mark as received',
  'request.withdrawLine': 'Withdraw the line',
  'request.noCustomers': 'Ask for no customer names, customer lists or details of a suspicion: counts are enough.',
  'requestTier.core': 'core',
  'requestTier.supporting': 'supporting',
  'inquiry.record': 'Record as held',
  'inquiry.close': 'Close',
  'inquiry.notesNote': 'The notes are a document of this client with no customer-level data. Participants may confirm them or send corrections.',
  'inquiry.participants': 'Participants',
  'inquiry.noParticipants': 'No participant yet.',
  'inquiry.participant': 'Participant',
  'inquiry.member': 'member of the firm',
  'inquiry.addParticipant': 'Add the participant',
  'inquiry.questions': 'Questions from the guide',
  'inquiry.noQuestions': 'No question chosen.',
  'inquiry.chooseQuestions': 'Choose the questions',
  'inquiry.nearSuspicion': 'near suspicion reports',
  'inquiry.confirmWith': 'Confirm with: {what}',
  'inquiry.statements': 'Statements',
  'inquiry.noStatements': 'No statement yet.',
  'inquiry.confidentialSource': 'A confidential source',
  'inquiry.sourceShownToYou': '(shown to you as interviewer or reviewer; others see the role only: {role})',
  'inquiry.retired': 'Retired: {reason}',
  'inquiry.retireStatement': 'Retire the statement',
  'inquiry.noQuestion': 'No particular question',
  'inquiry.speaker': 'Who said it',
  'inquiry.roleHint': 'Deliverables attribute statements by role only.',
  'inquiry.speaksTo': 'Practices it speaks to',
  'inquiry.confidentialTick': 'Confidential source: only the interviewer and the reviewer see who said it.',
  'inquiry.noSuspicionTick': 'I confirm the statement refers to no individual suspicion assessment or report.',
  'inquiry.noAllegationTick': 'I confirm the statement holds no allegation of misconduct against a named person; such an allegation belongs in the client\'s whistleblowing channel.',
  'inquiry.recordStatement': 'Record the statement',
  'assessmentKind.baseline': 'baseline',
  'assessmentKind.reassessment': 're-assessment',
  'assessmentKind.triggered': 'triggered assessment',
  'assessmentKind.correction': 'correction',
  'assessmentStatus.planning': 'planning',
  'assessmentStatus.gathering': 'gathering',
  'assessmentStatus.assessing': 'assessing',
  'assessmentStatus.in_review': 'in review',
  'assessmentStatus.frozen': 'frozen',
  'assessmentStatus.withdrawn': 'withdrawn',
  'scope.in_scope': 'in scope',
  'scope.not_applicable': 'not applicable',
  'scope.not_assessed': 'not assessed',
  'riskSource.business_profile': 'the business profile',
  'riskSource.atlas': 'the risk atlas',
  'requestStatus.draft': 'draft',
  'requestStatus.sent': 'sent',
  'requestStatus.closed': 'closed',
  'lineStatus.asked': 'asked',
  'lineStatus.received': 'received',
  'lineStatus.not_held': 'not held',
  'lineStatus.not_applicable': 'not applicable',
  'lineStatus.withdrawn': 'withdrawn',
  'inquiryKind.interview': 'interview',
  'inquiryKind.walkthrough': 'walkthrough',
  'inquiryKind.workshop': 'workshop',
  'inquiryKind.questionnaire': 'questionnaire',
  'inquiryKind.validation_meeting': 'validation meeting',
  'inquiryStatus.draft': 'planned',
  'inquiryStatus.recorded': 'recorded',
  'inquiryStatus.closed': 'closed',
  'participantConfirmation.none': 'not yet confirmed',
  'participantConfirmation.confirmed': 'confirmed by the participants',
  'participantConfirmation.corrections_received': 'corrections received',
  'statementKind.claim': 'claim',
  'statementKind.confirms_document': 'confirms a document',
  'statementKind.contradicts_document': 'contradicts a document',
  'statementKind.self_rating': 'self-rating',
  'statementKind.management_response': 'management response',
  'statementKind.root_cause': 'root cause',
  'inconsistencyState.open': 'open',
  'inconsistencyState.resolved_by_fact': 'resolved by a fact',
  'inconsistencyState.disposed': 'disposed of',
  'auditField.assessmentKind': 'kind of assessment',
  'auditField.windowMonths': 'window in months',
  'auditField.gatheringFrom': 'gathering from',
  'auditField.cycleItem': 'cycle item',
  'auditField.withdrawnReason': 'reason for withdrawing',
  'auditField.scope': 'scope',
  'auditField.scopeReason': 'reason',
  'auditField.domain': 'domain',
  'auditField.computedBand': 'computed band',
  'auditField.computedReasons': 'reasons of the computed band',
  'auditField.ruleVersion': 'rule version',
  'auditField.overrideBand': 'band set by a person',
  'auditField.overrideReason': 'reason for the band',
  'auditField.overrideBy': 'band set by',
  'auditField.overrideAt': 'band set on',
  'auditField.bandSource': 'source of the band',
  'auditField.assessment': 'analysis',
  'auditField.sentTo': 'sent to',
  'auditField.sentOn': 'sent on',
  'auditField.sentBy': 'recorded as sent by',
  'auditField.lineNo': 'line',
  'auditField.requestItem': 'catalogue item',
  'auditField.why': 'why',
  'auditField.askedOn': 'asked on',
  'auditField.answeredNote': 'what the client answered',
  'auditField.inquiryKind': 'kind',
  'auditField.heldOn': 'held on',
  'auditField.interviewer': 'interviewer',
  'auditField.secondListener': 'second listener',
  'auditField.guideQuestions': 'questions from the guide',
  'auditField.notesDocument': 'notes',
  'auditField.sharedOn': 'notes shared with the participants on',
  'auditField.participantConfirmation': 'participants\' confirmation',
  'auditField.confirmationDocument': 'confirmation',
  'auditField.participantContact': 'participant at the client',
  'auditField.participantPerson': 'participant at the firm',
  'auditField.roleTitle': 'role',
  'auditField.question': 'question',
  'auditField.speakerContact': 'speaker at the client',
  'auditField.speakerPerson': 'speaker at the firm',
  'auditField.speakerRole': 'speaker\'s role',
  'auditField.statementText': 'statement',
  'auditField.statementKind': 'kind of statement',
  'auditField.selfRating': 'self-rated level',
  'auditField.confidentialSource': 'confidential source',
  'auditField.noSuspicionConfirmed': 'refers to no suspicion, confirmed',
  'auditField.noAllegationConfirmed': 'holds no allegation, confirmed',
  'auditField.linkedBy': 'linked by',
  'auditField.linkedAt': 'linked on',
  'auditField.disposition': 'disposition',
  'auditField.disposedBy': 'disposed of by',
  'auditField.disposedAt': 'disposed of on',
  'auditAction.assessment.plan': 'Planned a current-state analysis',
  'auditAction.assessment.update': 'Changed the plan of an analysis',
  'auditAction.assessment.refresh': 'Brought the scope of an analysis up to date',
  'auditAction.assessment.gather': 'Started gathering for an analysis',
  'auditAction.assessment.withdraw': 'Withdrew an analysis',
  'auditAction.assessment.scope': 'Changed the scope of a practice',
  'auditAction.assessment.scope_confirm': 'Confirmed a scope decision',
  'auditAction.assessment.band_override': 'Set an inherent-risk band',
  'auditAction.assessment.band_confirm': 'Confirmed an inherent-risk band',
  'auditAction.request.create': 'Created a document request',
  'auditAction.request.update': 'Changed a document request',
  'auditAction.request.line_add': 'Added a line to a request',
  'auditAction.request.line': 'Recorded the answer to a line',
  'auditAction.request.line_document': 'Linked a received document to a line',
  'auditAction.request.send': 'Recorded a request as sent',
  'auditAction.request.close': 'Closed a request',
  'auditAction.inquiry.create': 'Planned an interview or walkthrough',
  'auditAction.inquiry.update': 'Changed an interview or walkthrough',
  'auditAction.inquiry.participant': 'Added a participant',
  'auditAction.inquiry.record': 'Recorded an interview as held',
  'auditAction.inquiry.close': 'Closed an interview',
  'auditAction.inquiry.statement': 'Recorded a statement',
  'auditAction.inquiry.statement_retire': 'Retired a statement',
  'auditAction.inconsistency.dispose': 'Disposed of an inconsistency',
  'errors.ASSESSMENT_PEOPLE_NEEDED': 'Name the preparer and the reviewer before gathering starts.',
  'errors.ASSESSMENT_DATES_FIXED': 'The as-of date and the window are set while planning.',
  // The engine's records (stage 1 of the engine).
  'record.evidence': 'Evidence item',
  'record.fact': 'Fact',
  'record.factEvidence': 'Evidence of a fact',
  'record.element': 'Required element',
  'record.sampleTest': 'Sample test',
  'record.kri': 'KRI',
  'record.kriReading': 'KRI reading',
  'record.practiceLink': 'Link to a practice',
  'record.escalation': 'Escalation',
  'record.target': 'Target level',
  'record.duty': 'Duty conclusion',
  'auditAction.assessment.assess': 'Started assessing an analysis',
  'auditAction.assessment.submit': 'Sent an analysis for review',
  'auditAction.assessment.send_back': 'Sent an analysis back to its preparer',
  // The freeze (033; addendum §8.13, §14 "Review and freeze")
  'auditAction.assessment.freeze': 'Froze an analysis',
  'auditAction.job.baseline.recompute': 'Recomputed the frozen analyses from their snapshots',
  'record.snapshot': 'Snapshot',
  'record.result': 'Frozen result',
  'auditField.snapshotKind': 'kind of snapshot',
  'snapshotKind.current_state': 'current-state analysis',
  'auditField.levelKind': 'result for',
  'levelKind.practice': 'a practice',
  'levelKind.capability': 'a capability',
  'levelKind.function': 'a function',
  'auditField.subject': 'subject',
  'auditField.exportKind': 'what was exported',
  'auditField.exportedBy': 'exported by',
  'auditField.exportedAt': 'exported at',
  'exportKind.request_letter': 'the request as a letter',
  'exportKind.request_checklist': 'the request as a checklist',
  'exportKind.missing_letter': 'what we still need, as a letter',
  'exportKind.picture': 'the organisation on one page',
  'auditField.computedLevel': 'computed level',
  'auditField.finalLevel': 'final level',
  'auditField.capReason': 'capped because',
  'capReason.self_assessed': 'assessed by the provider',
  'capReason.unknown': 'a higher gate could not be determined',
  'auditField.performedBy': 'performed by',
  'auditField.gap': 'gap to the target',
  'auditField.priority': 'priority',
  'auditField.quickWin': 'quick win',
  'auditField.capOnly': 'a gap of the provider\'s cap only',
  'auditField.wave': 'roadmap wave',
  'auditField.resultState': 'state',
  'resultState.assessed': 'assessed',
  'resultState.not_applicable': 'not applicable',
  'resultState.not_assessed': 'not assessed',
  'resultState.not_determined': 'not determined',
  'auditField.atOrAbove': 'at or above target',
  'auditField.total': 'of',
  'auditField.notDeterminedCount': 'not determined',
  'errors.FREEZE_NOT_IN_REVIEW': 'An analysis is frozen once it is in review.',
  'errors.FOUR_EYES_FREEZE': 'You sent this analysis for review, so another reviewer freezes it.',
  'errors.FREEZE_NOT_INDEPENDENT': 'You lead or hold a role on a service in scope, so another reviewer freezes it.',
  'errors.FREEZE_NOT_REVIEWED': 'Not yet: {n} practices are not marked reviewed by an independent reviewer who recorded none of their facts.',
  'errors.FREEZE_NOT_REVIEWED.one': 'Not yet: 1 practice is not marked reviewed by an independent reviewer who recorded none of its facts.',
  'errors.FREEZE_OVERRIDE_UNCONFIRMED': 'Not yet: {n} overrides wait for their confirmation.',
  'errors.FREEZE_OVERRIDE_UNCONFIRMED.one': 'Not yet: 1 override waits for its confirmation.',
  'errors.FREEZE_SCOPE_UNCONFIRMED': 'Not yet: {n} decisions to leave a legal duty out wait for their confirmation.',
  'errors.FREEZE_SCOPE_UNCONFIRMED.one': 'Not yet: 1 decision to leave a legal duty out waits for its confirmation.',
  'errors.FREEZE_TARGET_UNCONFIRMED': 'Not yet: {n} targets off the default wait for their confirmation.',
  'errors.FREEZE_TARGET_UNCONFIRMED.one': 'Not yet: 1 target off the default waits for its confirmation.',
  'errors.FREEZE_BAND_UNCONFIRMED': 'Not yet: {n} inherent risk bands wait for their confirmation.',
  'errors.FREEZE_BAND_UNCONFIRMED.one': 'Not yet: 1 inherent risk band waits for its confirmation.',
  'errors.FREEZE_EVIDENCE_UNREVIEWED': 'Not yet: {n} evidence items wait for their review by another member.',
  'errors.FREEZE_EVIDENCE_UNREVIEWED.one': 'Not yet: 1 evidence item waits for its review by another member.',
  'errors.FREEZE_QUOTE_NOT_FOUND': 'Not yet: {n} quotes are no longer found in their documents.',
  'errors.FREEZE_QUOTE_NOT_FOUND.one': 'Not yet: 1 quote is no longer found in its document.',
  'errors.FREEZE_INCONSISTENCY_OPEN': 'Not yet: {n} inconsistencies are open.',
  'errors.FREEZE_INCONSISTENCY_OPEN.one': 'Not yet: 1 inconsistency is open.',
  'errors.FREEZE_DUTY_UNCONFIRMED': 'Not yet: {n} duty conclusions are not decided and confirmed.',
  'errors.FREEZE_DUTY_UNCONFIRMED.one': 'Not yet: 1 duty conclusion is not decided and confirmed.',
  'errors.FREEZE_NOTICE_UNSENT': 'Not yet: {n} escalation notices are not recorded as sent.',
  'errors.FREEZE_NOTICE_UNSENT.one': 'Not yet: 1 escalation notice is not recorded as sent.',
  'freeze.title': 'Review and freeze',
  'freeze.before': 'Before freezing',
  'freeze.blocker.not_reviewed': 'Practices in scope marked reviewed',
  'freeze.blocker.override': 'Overrides confirmed',
  'freeze.blocker.scope': 'Practices with a legal duty left out of scope, confirmed',
  'freeze.blocker.target': 'Targets off the default, confirmed',
  'freeze.blocker.band': 'Inherent risk bands confirmed',
  'freeze.blocker.evidence': 'Evidence items the analysis rests on, reviewed by another member',
  'freeze.blocker.quotes': 'Quotes still found in their documents',
  'freeze.blocker.inconsistencies': 'Inconsistencies resolved',
  'freeze.blocker.duty': 'Duty conclusions decided and confirmed',
  'freeze.blocker.notices': 'Escalation notices recorded as sent',
  'freeze.waiting': '{n} waiting',
  'freeze.line': '{label}: {done} of {total}',
  'freeze.done': 'done',
  'freeze.freeze': 'Freeze',
  'freeze.frozenNow': 'The analysis is frozen.',
  'freeze.never': 'A frozen analysis never changes; a correction is a new analysis.',
  'freeze.whyNot.submitted': 'You sent this analysis for review, so another reviewer freezes it.',
  'freeze.whyNot.not_independent': 'You lead or hold a role on a service in scope, so another reviewer freezes it.',
  'freeze.whyNot.reviewer': 'A reviewer of this client freezes the analysis.',
  'freeze.whyNot.waits': 'Freezing opens once nothing above waits.',
  'freeze.internalOnly': 'Internal use only',
  'freeze.internalOnlyWarning': 'Some library files this analysis rests on are not yet adopted by the regulatory desk: {files}. It will be frozen for internal use only, and its report cannot be published until they are.',
  'freeze.allAdopted': 'Every library file this analysis rests on is adopted by the regulatory desk.',
  'freeze.frozenTitle': 'Frozen',
  'freeze.snapshot': 'Snapshot',
  'freeze.snapshotLine': '{code} · SHA-256 {hash}…',
  'freeze.matches': 'Recomputed from the snapshot: the results match.',
  'freeze.mismatch': 'Recomputed from the snapshot: {n} results do not match.',
  'freeze.mismatch.one': 'Recomputed from the snapshot: 1 result does not match.',
  'freeze.hashBroken': 'The snapshot no longer matches the hash stored with it.',
  'freeze.filesMissing': 'Library files the snapshot pins are missing: {files}.',
  'engine.frozenNotice': 'Frozen: these are the results stored at the freeze, recomputed by rule from its snapshot. No number here is set by a model.',
  'auditAction.evidence.add': 'Recorded an evidence item',
  'auditAction.evidence.review': 'Reviewed an evidence item',
  'auditAction.evidence.retire': 'Retired an evidence item',
  'auditAction.fact.record': 'Recorded a fact',
  'auditAction.fact.escalate': 'Recorded a missing basic control and escalated it',
  'auditAction.fact.confirm': 'Confirmed a fact',
  'auditAction.fact.retire': 'Withdrew a fact',
  'auditAction.element.record': 'Recorded a required element',
  'auditAction.practice.override': 'Moved a computed level',
  'auditAction.practice.override_confirm': 'Confirmed a moved level',
  'auditAction.practice.review': 'Marked a practice reviewed',
  'auditAction.sample.plan': 'Planned a sample test',
  'auditAction.sample.results': 'Recorded the results of a sample test',
  'auditAction.sample.review': 'Reviewed a sample test',
  'auditAction.sample.conclude': 'Concluded a sample test',
  'auditAction.sample.conclusion_confirm': 'Confirmed the conclusion of a sample test',
  'auditAction.kri.create': 'Added a KRI',
  'auditAction.kri.reading': 'Recorded a KRI reading',
  'auditAction.kri.reading_confirm': 'Confirmed a KRI reading',
  'auditAction.practice_link.add': 'Linked a record to a practice',
  'auditAction.practice_link.remove': 'Removed a link to a practice',
  'auditAction.escalation.notice': 'Recorded an escalation notice as sent',
  'auditAction.target.set': 'Set a target level',
  'auditAction.target.confirm': 'Confirmed a target level',
  'auditAction.duty.propose': 'Brought the duty proposals up to date',
  'auditAction.duty.decide': 'Decided a duty conclusion',
  'auditAction.duty.confirm': 'Confirmed a duty conclusion',
  'errors.ASSESSMENT_STATUS_NEEDED': 'This can be done only when the analysis is in another status.',
  'errors.ASSESSMENT_STATE': 'The analysis cannot move to that status from where it is.',
  'errors.SUBMIT_PRACTICES_OPEN': 'Every practice in scope needs its "performed" fact before review: {practices}.',
  'errors.SUBMIT_PREPARER_ONLY': 'The preparer of this analysis sends it for review.',
  'errors.OVERRIDE_NOT_ASSESSED': 'A level is moved only on a practice that is assessed, never on one that is not determined, not assessed or not applicable.',
  'errors.OVERRIDE_OUT_OF_RANGE': 'A level stays between 0 and 4.',
  'errors.OVERRIDE_ABOVE_CAP': 'The firm performs this practice, so no override lifts it above level {cap}: level 4 needs an independent assessment.',
  'errors.SAMPLE_ALL_NOT_POPULATION': 'Testing all means testing the whole population: the size is {population}.',
  'errors.EVIDENCE_SOURCE_KIND': 'Only a record, a document or a KRI series can come from a system extract.',
  'errors.NOT_INDEPENDENT_OF_SERVICE': 'You lead, or hold a role on, the service that brings this practice in, so another person does this.',
  'errors.FOUR_EYES_PRACTICE_REVIEW': 'You recorded facts or elements of this practice, so another reviewer marks it reviewed.',
  'errors.PRACTICE_REVIEWED_ALREADY': 'This practice is marked reviewed already.',
  'errors.PRACTICE_REVIEW_OUT_OF_SCOPE': 'Only a practice in scope is marked reviewed.',
  'errors.FOUR_EYES_OVERRIDE': 'You moved this level, so another reviewer confirms it.',
  'errors.OVERRIDE_NOTHING_TO_CONFIRM': 'Only a moved level waits for a confirmation.',
  'errors.KRI_RETIRED': 'This KRI is retired, and it stays retired.',
  'errors.READING_CONFIRMED_ALREADY': 'This reading is confirmed already.',
  'errors.FOUR_EYES_READING': 'You recorded this reading, so another member confirms it.',
  'errors.PRACTICE_UNKNOWN': 'The library has no practice {practice}.',
  'errors.SAMPLE_STATE': 'A sample test goes from planned to tested to reviewed.',
  'errors.SAMPLE_PLAN_FIXED': 'The plan of a sample test is fixed once its results are recorded.',
  'errors.SAMPLE_RESULTS_RECORDED': 'The results of this sample test are recorded already.',
  'errors.FOUR_EYES_SAMPLE': 'You recorded the results, so another reviewer reviews the test.',
  'errors.SAMPLE_RULE_CONCLUDED': 'The rule concluded this test, so no person writes its conclusion.',
  'errors.SAMPLE_NOTHING_TO_CONFIRM': 'Only a person\'s conclusion waits for a confirmation.',
  'errors.FOUR_EYES_SAMPLE_CONCLUSION': 'You wrote this conclusion, so another reviewer confirms it.',
  'errors.SAMPLE_METHOD_NEEDED': 'A size from a table names the sampling method.',
  'errors.SAMPLE_NO_ROW': 'The method gives no size for this control and population; enter the size with a reason.',
  'errors.SAMPLE_SIZE_OUT_OF_RANGE': 'Choose a size from {min} to {max}, as the method allows, with a reason.',
  'errors.SAMPLE_SIZE_NEEDED': 'Enter the size, with a reason.',
  'errors.EVIDENCE_RETIRED': 'This evidence item is retired, and it stays retired.',
  'errors.EVIDENCE_IN_USE': 'A fact or an element rests on this item, so it is not retired.',
  'errors.EVIDENCE_REVIEWED_ALREADY': 'This evidence item is reviewed already.',
  'errors.FOUR_EYES_EVIDENCE': 'You obtained this item, so another member reviews it.',
  'errors.EVIDENCE_REVIEWED_FIXED': 'A reviewed evidence item never changes; retire it and record another.',
  'errors.CUSTOMER_LEVEL_NOT_FOR_ASSESSMENT': 'A document with customer-level data is not evidence for the analysis.',
  'errors.EVIDENCE_STATEMENT_NOT_HERE': 'A statement is evidence for its own analysis, while it is current.',
  'errors.EVIDENCE_SELF_RATING_KIND': 'A self-rating is evidence of the kind self-rating, and only it.',
  'errors.EVIDENCE_CYCLE_NOT_DONE': 'A cycle item is evidence once it is done.',
  'errors.EVIDENCE_NOT_HERE': 'Evidence is linked within its own analysis.',
  'errors.FACT_PRACTICE_OUT_OF_SCOPE': 'Facts are recorded for a practice in scope.',
  'errors.FACT_CONFIRMED_ALREADY': 'This fact is confirmed already.',
  'errors.FOUR_EYES_FACT': 'You recorded this fact, so another member confirms it.',
  'errors.ELEMENT_UNKNOWN': 'This practice has no element {element}.',
  'errors.ELEMENT_NEEDS_QUOTE': 'A present element names its evidence, the quote and where it is.',
  'errors.QUOTE_NOT_CHECKABLE': 'The text of {document} cannot be read, so the quote cannot be checked.',
  'errors.FACT_NEEDS_EVIDENCE': 'A yes or a partial needs evidence of a class that admits it.',
  'errors.ESCALATION_NEEDED': 'A missing basic control is escalated in the same act: name the action\'s owner, its due date and who gets the notice.',
  'errors.ESCALATION_NOT_HERE': 'Only a no on a basic control that the practice escalates opens an escalation.',
  'errors.ESCALATION_ACTION_NEEDED': 'An escalation raises a P1 action with its owner and due date.',
  'errors.NOTICE_SENT_ALREADY': 'This escalation\'s notice is recorded as sent already.',
  'errors.NOTICE_DATE_RANGE': 'A notice is sent after the escalation and no later than today.',
  'errors.NOTICE_LETTER_NEEDED': 'A notice is recorded as sent with the letter.',
  'errors.TARGET_NOTHING_TO_CONFIRM': 'Only a target off the default waits for a confirmation.',
  'errors.FOUR_EYES_TARGET': 'You set this target, so another reviewer confirms it.',
  'errors.DUTY_NOTHING_TO_CONFIRM': 'Only a decided conclusion waits for a confirmation.',
  'errors.FOUR_EYES_DUTY': 'You decided this conclusion, so another reviewer confirms it.',
  'errors.DUTY_NOT_IN_FORCE': 'A duty is concluded under a rule in force at the as-of date; a coming rule is shown as readiness.',
  'auditField.submittedBy': 'sent for review by',
  'auditField.submittedAt': 'sent for review on',
  'auditField.frozenBy': 'frozen by',
  'auditField.frozenAt': 'frozen on',
  'auditField.internalOnly': 'for internal use only',
  'auditField.levelOverride': 'level moved by',
  'auditField.levelOverrideReason': 'reason for moving the level',
  'auditField.levelOverrideBy': 'level moved by',
  'auditField.levelOverrideAt': 'level moved on',
  'auditField.unit': 'unit',
  'auditField.kriDirection': 'worse when',
  'auditField.warningAt': 'warning at',
  'auditField.breachAt': 'breach at',
  'auditField.frequency': 'frequency',
  'auditField.kriPosition': 'against the thresholds',
  'auditField.readingSource': 'source of the reading',
  'auditField.practice': 'practice',
  'auditField.populationNote': 'what was tested',
  'auditField.populationCount': 'population',
  'auditField.periodFrom': 'period from',
  'auditField.periodTo': 'period to',
  'auditField.controlFrequency': 'control frequency',
  'auditField.controlNature': 'nature of the control',
  'auditField.samplingMethod': 'sampling method',
  'auditField.samplingMethodVersion': 'method version',
  'auditField.sizeSource': 'size from',
  'auditField.sizeReason': 'reason for the size',
  'auditField.selection': 'selection',
  'auditField.sampleSize': 'sample size',
  'auditField.tolerableExceptions': 'deviations accepted',
  'auditField.itemsTested': 'items tested',
  'auditField.itemsNotTested': 'items not tested',
  'auditField.exceptions': 'exceptions',
  'auditField.exceptionKinds': 'kinds of exception',
  'auditField.methodAdopted': 'method adopted',
  'auditField.ruleConclusion': 'conclusion by rule',
  'auditField.ruleReason': 'why the rule did not conclude',
  'auditField.personConclusion': 'a person\'s conclusion',
  'auditField.personReason': 'reason for the person\'s conclusion',
  'auditField.concludedBy': 'concluded by',
  'auditField.concludedAt': 'concluded on',
  'auditField.resultDocument': 'results',
  'auditField.testedBy': 'tested by',
  'auditField.testedAt': 'tested on',
  'auditField.evidenceKind': 'kind of evidence',
  'auditField.datedOn': 'dated',
  'auditField.coversFrom': 'covers from',
  'auditField.coversTo': 'covers to',
  'auditField.evidenceSource': 'how it was obtained',
  'auditField.preparedForAssessment': 'prepared for the analysis',
  'auditField.original': 'original',
  'auditField.approvalBody': 'approved by',
  'auditField.approvalRef': 'approval reference',
  'auditField.approvedOn': 'approved on',
  'auditField.validUntil': 'valid until',
  'auditField.testerKind': 'tested by (kind)',
  'auditField.testerPerson': 'tester at the firm',
  'auditField.testerContact': 'tester at the client',
  'auditField.receiptDocument': 'receipt',
  'auditField.obtainedBy': 'obtained by',
  'auditField.obtainedAt': 'obtained on',
  'auditField.fact': 'fact',
  'auditField.factValue': 'value',
  'auditField.checkedAt': 'checked on',
  'auditField.element': 'element',
  'auditField.elementState': 'state',
  'auditField.noticeTo': 'notice to',
  'auditField.noticeState': 'notice',
  'auditField.noticeSentOn': 'notice sent on',
  'auditField.noticeDocument': 'letter',
  'auditField.sentAt': 'recorded as sent on',
  'auditField.capability': 'capability',
  'auditField.targetLevel': 'target level',
  'auditField.targetReason': 'reason for the target',
  'auditField.reference': 'reference',
  'auditField.proposedValue': 'proposed by the rule',
  'auditField.proposedReasons': 'reasons of the proposal',
  'auditField.proposedAt': 'proposed on',
  'auditField.dutyValue': 'conclusion',
  'source.current_state': 'current-state analysis',
  'source.escalation': 'escalation',
  'kriDirection.higher_is_worse': 'higher',
  'kriDirection.lower_is_worse': 'lower',
  'kriFrequency.monthly': 'monthly',
  'kriFrequency.quarterly': 'quarterly',
  'kriFrequency.yearly': 'yearly',
  'kriPosition.within': 'within the thresholds',
  'kriPosition.warning': 'at warning',
  'kriPosition.breach': 'in breach',
  'kriPosition.no_threshold': 'no threshold set',
  'readingSource.manual': 'recorded by a member',
  'controlFrequency.annual': 'annual',
  'controlFrequency.semiannual': 'half-yearly',
  'controlFrequency.quarterly': 'quarterly',
  'controlFrequency.monthly': 'monthly',
  'controlFrequency.semimonthly': 'twice a month',
  'controlFrequency.weekly': 'weekly',
  'controlFrequency.daily': 'daily',
  'controlFrequency.multiple_daily': 'several times a day',
  'controlFrequency.event_driven': 'on each event',
  'controlFrequency.continuous': 'continuous',
  'controlNature.manual': 'manual',
  'controlNature.it_dependent_manual': 'manual, relying on a system',
  'controlNature.automated': 'automated',
  'sizeSource.table': 'the sampling method',
  'sizeSource.person': 'a person, with a reason',
  'selection.random_seeded': 'random, with a recorded seed',
  'selection.systematic': 'systematic',
  'selection.judgemental': 'by judgement',
  'selection.all': 'every item',
  'sampleStatus.planned': 'planned',
  'sampleStatus.tested': 'tested',
  'sampleStatus.reviewed': 'reviewed',
  'testConclusion.effective': 'effective',
  'testConclusion.not_effective': 'not effective',
  'testConclusion.not_concluded': 'not concluded',
  'notConcluded.no_adopted_method': 'no sampling method adopted',
  'notConcluded.person_size': 'a size a person entered',
  'notConcluded.no_acceptable_number': 'no number of deviations accepted',
  'notConcluded.judgemental': 'a selection by judgement',
  'evidenceKind.document': 'document',
  'evidenceKind.record': 'record',
  'evidenceKind.approval': 'approval',
  'evidenceKind.statement': 'statement',
  'evidenceKind.walkthrough': 'walkthrough',
  'evidenceKind.sample_test': 'sample test',
  'evidenceKind.kri_series': 'KRI series',
  'evidenceKind.prior_report': 'earlier report',
  'evidenceKind.valentin_report': 'Valentin report',
  'evidenceKind.self_rating': 'self-rating',
  'evidenceKind.cycle_evidence': 'the firm\'s own cycle item',
  'evidenceSource.client_prepared': 'prepared by the client',
  'evidenceSource.obtained_directly': 'obtained directly',
  'evidenceSource.independent_third_party': 'from an independent third party',
  'evidenceSource.system_extract': 'system extract',
  'approvalBody.management_body': 'the management body',
  'approvalBody.compliance_manager': 'the AML compliance manager',
  'approvalBody.compliance_officer': 'the AML compliance officer',
  'approvalBody.none': 'no body named',
  'testerKind.client_internal_audit': 'the client\'s internal audit',
  'testerKind.client_second_line': 'the client\'s second line',
  'testerKind.client_first_line': 'the client\'s first line',
  'testerKind.external_party': 'an external party',
  'testerKind.supervisor': 'the supervisor',
  'testerKind.valentin': 'Valentin',
  'testerKind.firm_member': 'a member of the firm',
  'fact.performed': 'performed',
  'fact.designed': 'designed',
  'fact.approved': 'approved',
  'fact.current': 'current',
  'fact.owner': 'owner named',
  'fact.operating': 'operating',
  'fact.sample': 'sample effective',
  'fact.tested': 'tested independently',
  'fact.measured': 'measured',
  'fact.within_threshold': 'within its threshold',
  'fact.reported': 'reported to the board',
  'factValue.yes': 'yes',
  'factValue.partial': 'partly',
  'factValue.no': 'no',
  'factValue.unknown': 'unknown',
  'elementState.present': 'present',
  'elementState.absent': 'absent',
  'elementState.not_applicable': 'not applicable',
  'noticeState.to_send': 'to send',
  'noticeState.sent': 'sent',
  'dutyValue.met': 'met',
  'dutyValue.partly_met': 'partly met',
  'dutyValue.not_met': 'not met',
  'dutyValue.not_determined': 'not determined',
  'errors.ASSESSMENT_AS_OF_RANGE': 'An analysis is as of a day within the coming year.',
  'errors.REVIEWER_NOT_INDEPENDENT': 'The reviewer may not lead any of this client\'s services or hold any of its roles in the twelve months before the as-of date.',
  'errors.ASSESSMENT_CLOSED': 'A withdrawn or frozen analysis never changes.',
  'errors.ASSESSMENT_NOT_OPEN': 'This analysis is not open for planning or gathering.',
  'errors.SCOPE_NOTHING_TO_CONFIRM': 'Only a practice taken out of scope waits for a confirmation.',
  'errors.FOUR_EYES_SCOPE': 'You made this scope decision, so another reviewer confirms it.',
  'errors.BAND_CONFIRMED_ALREADY': 'The band in force is confirmed already.',
  'errors.FOUR_EYES_BAND': 'You set this band, so another reviewer confirms it.',
  'errors.BANDS_REFRESH_PLANNING': 'The computed bands are brought up to date while planning.',
  'errors.REQUEST_CLOSED': 'A closed request stays closed.',
  'errors.REQUEST_CONTACT_NEEDED': 'A request is sent to a contact at the client: choose one first.',
  'errors.REQUEST_LINES_NEEDED': 'A request is sent with at least one line.',
  'errors.REQUEST_ITEM_UNKNOWN': 'The catalogue has no such item.',
  'errors.LINE_DOCUMENT_NEEDED': 'Link the received document to the line first.',
  'errors.REQUEST_ANSWER_NEEDED': 'Say what the client answered.',
  'errors.INQUIRY_CLOSED': 'A closed interview stays closed.',
  'errors.INQUIRY_NOT_HELD': 'An interview is recorded once it has been held.',
  'errors.INQUIRY_NOTES_NEEDED': 'Record the notes, or the reason it was not held.',
  'errors.INQUIRY_DATE_RANGE': 'An interview is held within the coming year.',
  'errors.INQUIRY_SHARED_DATE': 'Notes are shared after the interview and no later than today.',
  'errors.STATEMENT_QUESTION_NOT_IN_GUIDE': 'A statement answers one of the interview\'s questions.',
  'errors.NO_SUSPICION_TICK_NEEDED': 'Confirm that the statement refers to no individual suspicion or report.',
  'errors.SPEAKER_NOT_PARTICIPANT': 'The speaker is a participant of the interview: add them first.',
  'errors.STATEMENT_RETIRED': 'A retired statement is not linked.',
  'errors.DISPOSITION_NEEDED': 'Say how the inconsistency is disposed of.',
  'errors.INCONSISTENCY_CLOSED': 'A closed inconsistency stays closed.',
  'errors.SELF_RATING_LEVEL_NEEDED': 'A self-rating states its level, 0 to 4.',
  'sampling.title': 'Sampling methods',
  'sampling.intro': 'The ways a test of a control can be sized and evaluated. None is in force until one regulatory-desk member records that its source was checked and a second adopts it. Until a method is adopted, no sample test reaches a conclusion by rule: a person writes the conclusion with a reason, and a reviewer confirms it.',
  'sampling.shared': 'The entries are Valentin’s, word for word, so a sample test reads the same in both products; where a text says what Valentin does, it describes Valentin.',
  'sampling.none': 'No sampling method is loaded.',
  'sampling.method.table': 'Table',
  'sampling.method.formula': 'Computed formula',
  'sampling.method.rule': 'Rule',
  'sampling.method.reference': 'Text to read',
  'sampling.selection.statistical': 'statistical',
  'sampling.selection.nonstatistical': 'non-statistical',
  'sampling.example': 'Example',
  'sampling.readOnly': 'Sizes no sample',
  'sampling.canSize': 'Adopted: can size samples',
  'sampling.notAdopted': 'Not adopted: sizes nothing yet',
  'sampling.formulaSizes': 'At {confidence} confidence and a {rate} tolerable rate, computed:',
  'sampling.formulaSize': '{n} items, {a} deviations accepted',
  'sampling.sourceChecked': 'Source checked by {name} on {date}',
  'sampling.sourceNotChecked': 'Source not checked yet',
  'sampling.notAdoptedYet': 'not adopted yet',
  'sampling.showDetails': 'Show the source and the rows',
  'sampling.recordSourceChecked': 'Record that the source was checked',
  'sampling.adopt': 'Adopt this method',
  'sampling.adoptOther': 'You checked the source: another desk member adopts this method.',
  'sampling.source': 'Source',
  'sampling.edition': 'Edition',
  'sampling.readOn': 'Read on',
  'sampling.page': 'Page',
  'sampling.licence': 'Licence',
  'sampling.licence.public_domain': 'free of copyright',
  'sampling.licence.cc_by_4_0': 'reused under CC BY 4.0',
  'sampling.licence.eu_law': 'EU law',
  'sampling.licence.copyrighted_cite_only': 'under copyright: cited, not copied',
  'sampling.licence.firm_internal': 'the firm’s own',
  'sampling.secondStage': 'Second stage',
  'sampling.evaluation': 'Conclusion',
  'sampling.rule.deviations_le_acceptable': 'by rule: within the accepted deviations, effective',
  'sampling.rule.person': 'by a person',
  'sampling.reviewDue': 'Review due',
  'sampling.file': 'File',
  'sampling.rows': 'Sample sizes',
  'sampling.row': 'Applies to',
  'sampling.size': 'Size',
  'sampling.accepted': 'Deviations accepted',
  'sampling.rowSource': 'Where it is written',
  'sampling.itgc': 'Only while the IT general controls are effective',
  'sampling.size.items': '{n} items',
  'sampling.size.then': ', then ',
  'sampling.size.range': '{min} to {max} items, chosen with a reason',
  'sampling.size.all': 'every item',
  'sampling.size.percent': 'at least {rate} of the items, and at least {min}',
  'sampling.accepted.person': 'not stated: a person concludes',
  'sampling.caveats': 'Before adopting',
  'sampling.verification': 'Checked',
  'sampling.own': 'The firm’s own table',
  'sampling.ownNote': 'Download the template, replace every placeholder with the firm’s methodology, and add it to the library as a file. It is then checked and adopted by two desk members like the methods above; the library refuses the template while a placeholder is left in it.',
  'sampling.ownDownload': 'Download the template (JSON)',
  'request.overdue': 'Overdue',
  // The current-state analysis on screen (stage 2 of the engine).
  'level.0': 'Not in place',
  'level.1': 'Performed, ad hoc',
  'level.2': 'Defined',
  'level.3': 'Operating',
  'level.4': 'Tested and managed',
  'performer.client': 'the client',
  'performer.firm': 'the firm',
  'performer.firm_reviews': 'the client, with the firm reviewing',
  'elementStatus.ok': 'present, quote found',
  'elementStatus.absent': 'absent',
  'elementStatus.not_applicable': 'not applicable',
  'elementStatus.unverified': 'present, not yet shown in reviewed evidence',
  'elementStatus.unchecked': 'not recorded',
  'quoteCheck.not_found': 'not found in the document',
  'engine.gate.met': 'met',
  'engine.gate.not_met': 'not met',
  'engine.gate.unknown': 'unknown',
  'engine.readiness.met_already': 'met already',
  'engine.readiness.not_yet': 'not yet',
  'engine.wave.now': 'now (0–3 months)',
  'engine.wave.mid': '3–6 months',
  'engine.wave.later': '6–18 months',
  'engine.title': 'Current-state analysis',
  'engine.nav.label': 'Parts of the analysis',
  'engine.nav.plan': 'Plan and gathering',
  'engine.nav.analysis': 'Results and gaps',
  'engine.nav.evidence': 'Evidence and KRIs',
  'engine.nav.tests': 'Sample tests',
  'engine.linksGathering': 'Evidence can be recorded while gathering. The levels are computed once a lead starts assessing.',
  'engine.linksAssessing': 'The facts, levels, gaps and duty conclusions are recorded and reviewed here.',
  'engine.provisional': 'Provisional: computed by rule from what is recorded now ({rule}). Nothing is kept until the analysis is frozen, and no number here is set by a model.',
  'engine.count.withFacts': 'Practices with their "performed" fact',
  'engine.count.of': '{n} of {total}',
  'engine.count.notDetermined': 'Not determined',
  'engine.count.notAssessed': 'Not assessed',
  'engine.count.notApplicable': 'Not applicable',
  'engine.count.byProvider': 'Performed by the firm (assessed by the provider, at most level 3)',
  'engine.count.duty': 'Duty conclusions in force',
  'engine.count.dutyLine': '{total} proposed · {decided} decided · {confirmed} confirmed',
  'engine.count.evidence': 'Evidence items',
  'engine.count.evidenceLine': '{total} current · reviewed by a second person: {reviewed} of {total}',
  'engine.rounds': 'Earlier rounds sent back',
  'engine.round': 'Round {n}: sent for review by {submitter}, sent back by {returner} on {date}: {reason}',
  'engine.assess': 'Start assessing',
  'engine.submit': 'Send for review',
  'engine.submitNeeds': '{n} practices in scope still need their "performed" fact before review: {practices}.',
  'engine.submitByPreparer': 'The preparer sends the analysis for review.',
  'engine.submitNeeds.one': '1 practice in scope still needs its "performed" fact before review: {practices}.',
  'engine.sendBack': 'Send back to the preparer',
  'engine.sendBackReason': 'Why it is sent back',
  'engine.headline': 'Where the client stands',
  'engine.headlineIntro': 'Capabilities at or above their target, counted by rule. A level is never averaged.',
  'engine.functionLine': '{at} of {total} capabilities at or above target · {nd} not determined',
  'engine.noFunctions': 'No capability in scope yet.',
  'engine.capabilities': 'Capabilities and targets',
  'engine.capabilitiesIntro': 'A capability\'s level is the lowest level among its legal-duty practices. A target off the rule\'s default needs a reason and a reviewer who did not set it.',
  'engine.col.level': 'Level',
  'engine.col.gap': 'Gap',
  'engine.col.priority': 'Priority',
  'engine.col.review': 'Review',
  'engine.col.item': 'Item',
  'engine.col.dates': 'Dated',
  'engine.col.class': 'Evidence class',
  'engine.levelChip': 'Level {n} · {name}',
  'engine.notDetermined': 'Not determined',
  'engine.missing': 'missing: {practices}',
  'engine.goodPractice': '{n} of {total} good-practice items at or above this level',
  'engine.targetDefault': '{n} (the rule\'s default)',
  'engine.targetSet': '{n} (set by a person)',
  'engine.targetBy': '{name}: {reason}',
  'engine.targetRuleDefault': 'The rule\'s default',
  'engine.setTarget': 'Change the target',
  'engine.confirmTarget': 'Confirm the target',
  'engine.confirmedBy': 'confirmed by {name}',
  'engine.toConfirm': 'waits for a second person\'s confirmation',
  'engine.notConfirmed': 'not confirmed by a second person before the analysis closed',
  'engine.practices': 'Practices',
  'engine.byProvider': 'Assessed by the provider',
  'engine.movedUp': 'computed {n}, moved one level up',
  'engine.movedDown': 'computed {n}, moved one level down',
  'engine.cappedByUnknown': 'stops at an unknown',
  'engine.overrideToConfirm': 'moved level waits for a reviewer',
  'engine.gapLine': '{gap} below target {target}',
  'engine.priority': '{p}',
  'engine.quickWin': 'Quick win',
  'engine.escalated': 'Escalated',
  'engine.reviewedBy': 'reviewed by {name}',
  'engine.notReviewed': 'not reviewed yet',
  'engine.gaps': 'Gaps and priorities',
  'engine.gapsIntro': 'Gap, priority, quick win and wave are computed by rule. The due date and severity of each action raised from a gap are a person\'s.',
  'engine.gapsClient': 'Gaps',
  'engine.noGaps': 'No practice is below its target.',
  'engine.needsIndependent': 'Level 4 needs an independent assessment',
  'engine.capOnlyIntro': 'The firm performs these practices, so its own assessment stops at level 3. They need a review by someone independent of the firm, such as the client\'s internal audit; this is not a weakness of the client.',
  'engine.capOnlyNote': 'The only gap is the provider\'s cap: a review by someone independent of the firm closes it.',
  'engine.toObtain': 'Not determined: evidence to obtain',
  'engine.claimedNotEvidenced': 'claimed, not evidenced',
  'engine.notEvidenced': 'not evidenced',
  'engine.followUpAsked': '{item}: asked in request {request}, line {line} ({status})',
  'engine.followUpNotAsked': '{item}: not asked yet',
  'engine.followUpNone': 'No catalogue item serves this practice; ask for its evidence in a line of your own.',
  'engine.escalations': 'Escalations',
  'engine.escalationsIntro': 'A missing basic control is escalated in the act that records it, with a P1 action. The notice to the client is due one business day later.',
  'engine.escalationFact': '{fact}: no',
  'engine.noticeSent': 'notice sent {date} (letter {doc})',
  'engine.noticeDue': 'notice due by {date}',
  'engine.noticeOverdue': 'notice overdue since {date}',
  'engine.escalationLine': 'Opened {opened} by {by} · notice to {to}',
  'engine.recordNotice': 'Record the notice as sent',
  'engine.keyControl': 'key control',
  'engine.refToday': 'Today: {ref}',
  'engine.refComing': 'From {date}: {ref}',
  'engine.performer': 'Performed by',
  'engine.service': 'Service',
  'engine.gates': 'What the level rests on',
  'engine.gatesIntro': 'Each level needs every lower level met. An unknown is kept apart from a no.',
  'engine.gatesNotDetermined': 'Whether the practice is performed is not evidenced yet, so no level is given.',
  'engine.gatesNotAssessed': 'This practice is not assessed in this analysis.',
  'engine.gateLevel': 'Level {n}:',
  'engine.gate.fact': '{fact}: {value}',
  'engine.gate.element': 'element {element}: {state}',
  'engine.gate.actionOpen': 'open critical action {code}',
  'engine.gate.actionOverdue': 'overdue action {code}',
  'engine.facts': 'Facts',
  'engine.factsIntro': 'A yes or a partly needs evidence of a class that admits it; a no may rest on a note. A fact is replaced, never edited, and a member who did not record it confirms it.',
  'engine.factNotApplicable': 'does not apply to this practice:',
  'engine.recordedAs': 'recorded as {value}',
  'engine.notRecorded': 'not recorded',
  'engine.derived': 'derived',
  'engine.periods': 'records in {n} of {total} periods',
  'engine.periodsMissing': 'records in {n} of {total} periods; missing: {missing}',
  'engine.elementsPresent': '{n} of {total} elements present',
  'engine.withinLine': '{n} of {total} readings within the threshold',
  'engine.recordedBy': 'recorded by {name} on {date}',
  'engine.evidenceNotReviewed': 'not reviewed yet',
  'engine.confirmFact': 'Confirm',
  'engine.confirmOther': 'you recorded it, so another member confirms it',
  'engine.confirmNotYou': 'waits for a member outside this service',
  'engine.withdrawFact': 'Withdraw the fact',
  'engine.recordFact': 'Record a fact',
  'engine.recordAndEscalate': 'Record and escalate',
  'engine.factYesNeeds': 'Link evidence of a class that admits it, with the quote and where it is.',
  'engine.factNoNeeds': 'A no or an unknown may rest on a note.',
  'engine.factNote': 'Note',
  'engine.replaceReason': 'Why the recorded fact is replaced',
  'engine.replaceHint': 'It is recorded as {value} now; that fact is kept in the history.',
  'engine.evidenceLinks': 'Evidence',
  'engine.evidenceItem': 'Evidence item',
  'engine.quote': 'Quote (checked against the document)',
  'engine.locator': 'Where it is (page, section, row)',
  'engine.addLink': 'Link another item',
  'engine.escalationForm': 'Escalated at once',
  'engine.escalationExplain': 'This practice escalates a no on this fact: the action with priority P1 and the escalation are opened in the same act, and the lead then records the notice to the client.',
  'engine.actionTitle': 'The action',
  'engine.escalationBasis': 'Basis for the severity',
  'engine.escalationBasisDefault': 'P1 by rule maturity.v1: {practice}, {fact}: no, escalated at once',
  'engine.actionOwner': 'Who owns the action',
  'engine.ownerMembers': 'Members of the firm',
  'engine.ownerContacts': 'Contacts at the client',
  'engine.elements': 'Required elements',
  'engine.elementsIntro': 'An element is present only with a quote found in reviewed evidence.',
  'engine.requiredAt': 'required from level {n}',
  'engine.recordElement': 'Record this element',
  'engine.duty': 'Compliance with the rules',
  'engine.dutyIntro': 'The rule proposes a conclusion on each rule in force; a person decides it, and a reviewer outside the service confirms it. This is the firm\'s advisory assessment, not a legal determination.',
  'engine.duty.inForce': 'Rules in force at the as-of date',
  'engine.duty.noneInForce': 'No rule of this practice is in force at the as-of date.',
  'engine.duty.proposed': 'Proposed by the rule:',
  'engine.duty.decided': 'Decided by {name}:',
  'engine.duty.toConfirm': 'waits for a reviewer outside this service',
  'engine.duty.decide': 'Decide the conclusion',
  'engine.duty.confirm': 'Confirm the conclusion',
  'engine.duty.reasonNeeded': 'Reason (needed: the decision differs from the proposal)',
  'engine.duty.reasonOptional': 'Reason (optional)',
  'engine.duty.reasonMissing': 'Say why the decision differs from the rule\'s proposal.',
  'engine.duty.coming': 'Coming rules: readiness only',
  'engine.duty.comingIntro': 'A rule that applies later is never concluded on. It shows whether the client meets it already.',
  'engine.duty.comingFrom': 'From {date}',
  'engine.override': 'Moving the level',
  'engine.overrideIntro': 'A person may move the computed level by one step, with a reason; a reviewer who did not propose it confirms it.',
  'engine.overrideCapped': 'A person may move the computed level by one step, with a reason, and a reviewer confirms it. It never lifts the level above what the provider may assess (level 3).',
  'engine.overrideUp': 'Moved one level up: {reason}',
  'engine.overrideDown': 'Moved one level down: {reason}',
  'engine.overrideConfirmed': 'confirmed',
  'engine.noOverride': 'The computed level stands.',
  'engine.moveLevel': 'Move the level one step',
  'engine.removeOverride': 'Remove the move',
  'engine.confirmOverride': 'Confirm the move',
  'engine.overrideStep': 'Direction',
  'engine.oneUp': 'One level up',
  'engine.oneDown': 'One level down',
  'engine.reviewMark': 'Review mark',
  'engine.reviewedOn': 'reviewed by {name} on {date}',
  'engine.reviewNotYet': 'Not marked reviewed yet. A reviewer who recorded none of its facts or elements, and holds no role on its service, marks it.',
  'engine.reviewNotYouRecorded': 'Not marked reviewed yet. You recorded facts or elements here, so another reviewer marks it.',
  'engine.reviewNotYouService': 'Not marked reviewed yet. You lead, or hold a role on, the service that brings this practice in, so another reviewer marks it.',
  'engine.markReviewed': 'Mark it reviewed',
  'engine.noTestsHere': 'No sample test of this practice.',
  'engine.countsOnly': 'Counts only, not re-performed',
  'engine.toTests': 'To the sample tests',
  'engine.history': 'Earlier facts',
  'engine.historyLine': '{fact}: {value}, recorded by {by}, withdrawn {withdrawn}: {reason}',
  'engine.evidence': 'Evidence items',
  'engine.evidenceIntro': 'A member records an item with its attributes; another member reviews it, and only then does it count. Its class is computed from the attributes, never chosen.',
  'engine.noEvidence': 'No evidence item recorded yet.',
  'engine.preparedForAnalysis': 'prepared for this analysis',
  'engine.copy': 'a copy',
  'engine.approvalLine': 'approved by {body}, {ref}, on {date}',
  'engine.testerLine': 'tested by {kind}',
  'engine.retired': 'retired: {reason}',
  'engine.covers': 'covers {from} to {to}',
  'engine.classOnlyCorroborated': 'counts only when corroborated',
  'engine.classNote': 'Classes: E1 tested, E2 a record made in the normal course of business, E3 an approved governing document. A statement or a document prepared for the analysis is E4 only when an E1–E3 item backs the same fact, and otherwise E5, which never raises a level.',
  'engine.obtainedBy': 'obtained by {name} on {date}',
  'engine.reviewEvidence': 'Review',
  'engine.reviewOther': 'you obtained it, so another member reviews it',
  'engine.retireEvidence': 'Retire the item',
  'engine.addEvidence': 'Record an evidence item',
  'engine.document': 'Document',
  'engine.statement': 'Statement',
  'engine.confidentialSource': 'a confidential source',
  'engine.occurrence': 'The firm\'s cycle item (code)',
  'engine.occurrenceHint': 'A done item of the annual cycle, such as CY-2026-041.',
  'engine.approval': 'Approval',
  'engine.notApproved': 'not an approval',
  'engine.testerUnknown': 'not stated',
  'engine.valentinNote': 'A Valentin report does not yet count as an independent test: Olivia cannot check its receipt until it issues its own hand-off requests.',
  'engine.kris': 'Key risk indicators',
  'engine.krisIntro': 'The client\'s KRIs and their readings. Each reading is placed against the thresholds by rule, and confirmed by a member who did not record it.',
  'engine.noKris': 'No KRI recorded for this client.',
  'engine.kriLine': 'worse when {direction} · warning at {warning} · breach at {breach} {unit} · {frequency}',
  'engine.noReadings': 'No readings yet.',
  'engine.readingConfirmed': 'confirmed',
  'engine.confirmReading': 'Confirm the reading',
  'engine.readingOther': 'you recorded it, so another member confirms it',
  'engine.addReading': 'Record a reading',
  'engine.periodEnd': 'Period ending',
  'engine.value': 'Value',
  'engine.addKri': 'Add a KRI',
  'engine.kriName': 'Name',
  'engine.kriPractices': 'Practices it measures',
  'engine.kriPracticesHint': 'Choose one or more; hold Ctrl or Shift to choose several.',
  'engine.testsIntro': 'Until customer-level records can be held, a test records its counts only and cannot be re-performed by the reviewer. Every selected item not tested counts as a deviation.',
  'engine.noTests': 'No sample test planned yet.',
  'engine.noTestsClosed': 'No sample test was planned in this analysis.',
  'engine.testPlanLine': 'population {population} · {from} to {to} · {frequency}, {nature}',
  'engine.testSizeLine': 'size {size}, from {source} · {selection} · deviations accepted: {tolerable}',
  'engine.testMethod': 'sampling method: {name}, version {v}',
  'engine.testMethodUnnamed': 'a sampling method no longer in the library',
  'engine.testCounts': 'tested: {tested} of {size} · not tested: {notTested} · exceptions: {exceptions} · deviations: {deviations}',
  'engine.testedBy': 'tested by {name}',
  'engine.ruleConclusion': 'By rule:',
  'engine.personConclusion': 'By {name}:',
  'engine.recordResults': 'Record the results',
  'engine.reviewTest': 'Review the test',
  'engine.concludeTest': 'Write a conclusion',
  'engine.confirmConclusion': 'Confirm the conclusion',
  'engine.selectionPositions': 'Items to test, by position in the population: {positions}',
  'engine.selectionNone': 'Selection {selection}: the items are chosen without a seed.',
  'engine.kindsHint': 'Separate kinds with commas.',
  'engine.untestedCount': 'A selected item that was not tested counts as a deviation.',
  'engine.planTest': 'Plan a sample test',
  'engine.planTestIntro': 'The size comes from the sampling method by rule, or from a person with a reason. No test reaches a conclusion by rule until its method is adopted by the regulatory desk.',
  'engine.methodHint': 'A method not yet adopted sizes the test, but no conclusion is reached by rule.',
  'engine.sizeFromMethod': 'Leave empty unless the method gives a range; then choose within it, with a reason.',
  'engine.sizeByPerson': 'The size you choose, with the reason below.',
  'engine.tolerableHint': 'For a key control of a legal duty the rule accepts no deviation, whatever is entered.',
  'engine.seedNote': 'The database draws the seed of a random selection when the test is planned, so the items cannot follow the results.',
  // The current-state report and its checkpoint (034; addendum §6 step J, §11.1; main 7.7)
  'engine.nav.report': 'Report',
  'report.title': 'Current-state report',
  'report.linkHint': 'rendered from this snapshot, in English or Swedish',
  'report.notFrozen': 'The report is made once the analysis is frozen.',
  'report.standing': 'Can the report be published?',
  'report.internalWhy': 'Built on content not yet adopted by the regulatory desk: this report can be used inside the firm but cannot be published.',
  'report.unadopted': 'Not yet adopted: {files}.',
  'report.publishable': 'Every library file this analysis rests on is adopted by the regulatory desk: a report can be published once a reviewer has approved it.',
  'report.prepareTitle': 'A new report',
  'report.prepareIntro': 'The report is rendered by rule from the frozen snapshot, as a Word file and a PDF. Every figure in it comes from the snapshot; nothing in it is written by an AI model.',
  'report.language': 'Language',
  'report.prepare': 'Prepare the report',
  'report.prepared': 'The report is prepared.',
  'report.layout': 'Layout',
  'report.layout.standard': 'Standard layout',
  'report.layout.gap_report': 'Gap report',
  'report.layoutNote': 'Both layouts are rendered by rule from the same frozen snapshot. The gap report lays the analysis out as a gap analysis and prints no other scale than Olivia’s.',
  'report.gapLog.title': 'Gap log',
  'report.gapLog.intro': 'An Excel workbook of the frozen analysis, one row per reference of each practice, built from the snapshot alone. It is a working file for the firm: what leaves the firm is the report, after its checkpoint. Each download is recorded.',
  'report.gapLog.en': 'Gap log in English (XLSX)',
  'report.gapLog.sv': 'Gap log in Swedish (XLSX)',
  'report.listTitle': 'Reports',
  'report.none': 'No report has been prepared yet.',
  'report.files': 'Files',
  'report.receipt': 'Approval receipt',
  'report.filesNote': 'These are the files a reviewer approves, and that are published unchanged.',
  'report.returned': 'Sent back: {note}',
  'report.checkpoint': 'Checkpoint',
  'report.asked': 'Review asked for by {name} on {date}',
  'report.expires': 'expires {date}',
  'report.decided': 'decided by {name} on {date}',
  'report.bound': 'bound to sha256 {hash}',
  'report.askReview': 'Ask for review',
  'report.askedDone': 'Review asked for. The files were rendered again with the checkpoint in their footer.',
  'report.approve': 'Approve',
  'report.approved': 'Approved.',
  'report.sendBack': 'Send back with a note…',
  'report.sendBackNote': 'Why it is sent back',
  'report.sendBackSubmit': 'Send back',
  'report.sentBack': 'Sent back.',
  'report.publish': 'Publish',
  'report.published': 'Published, with its approval receipt.',
  'report.renderAgain': 'Render again',
  'report.rendered': 'Rendered again.',
  'report.renderExpires': 'Rendering it again returns the report to draft and expires its checkpoint.',
  'report.whyNot.reviewer': 'A reviewer of this client decides at the checkpoint.',
  'report.whyNot.four_eyes': 'You prepared this report or asked for its review, so another reviewer decides it.',
  'report.whyNot.expired': 'The checkpoint has expired; ask for review again.',
  'report.whyNotPublish': 'You prepared this report or asked for its review, so another reviewer publishes it.',
  'reportLanguage.en': 'English',
  'reportLanguage.sv': 'Swedish',
  'deliverableStatus.draft': 'draft',
  'deliverableStatus.in_review': 'in review',
  'deliverableStatus.approved': 'approved',
  'deliverableStatus.published': 'published',
  'checkpointStatus.pending': 'pending',
  'checkpointStatus.approved': 'approved',
  'checkpointStatus.rejected': 'sent back',
  'checkpointStatus.expired': 'expired',
  'record.deliverable': 'Report',
  'record.checkpoint': 'Checkpoint',
  'record.export': 'Export',
  'auditField.reportLanguage': 'language',
  'auditField.reportLayout': 'layout',
  'auditField.renderedBy': 'rendered by',
  'auditField.renderedAt': 'rendered at',
  'auditField.docxDocument': 'Word file',
  'auditField.pdfDocument': 'PDF file',
  'auditField.checkpoint': 'checkpoint',
  'auditField.returnedNote': 'note when sent back',
  'auditField.publishedBy': 'published by',
  'auditField.publishedAt': 'published at',
  'auditField.deliverable': 'report',
  'auditField.requestedBy': 'review asked for by',
  'auditField.requestedAt': 'review asked for at',
  'auditField.decisionNote': 'note to the decision',
  'auditField.expiresAt': 'expires at',
  'auditField.expiredAt': 'expired at',
  'auditAction.report.prepare': 'Prepared a current-state report',
  'auditAction.export.request_letter': 'Exported a request as a letter',
  'auditAction.export.request_checklist': 'Exported a request as a checklist',
  'auditAction.export.missing_letter': 'Exported what we still need as a letter',
  'auditAction.export.picture': 'Exported the organisation on one page',
  'auditAction.report.render': 'Rendered a report again',
  'auditAction.report.request_review': 'Asked for review of a report',
  'auditAction.report.approve': 'Approved a report at its checkpoint',
  'auditAction.report.reject': 'Sent a report back',
  'auditAction.report.publish': 'Published a report',
  'auditAction.checkpoint.expire': 'A checkpoint expired: the files no longer matched',
  'errors.REPORT_NOT_FROZEN': 'A report is made from a frozen analysis.',
  'errors.DELIVERABLE_PUBLISHED': 'A published report never changes; a correction is a new report.',
  'errors.LIBRARY_NOT_ADOPTED': 'Built on content not yet adopted by the regulatory desk: the report is for internal use and is not published.',
  'errors.REVIEW_NEEDS_DRAFT': 'Review is asked for a report that is a draft.',
  'errors.FOUR_EYES_CHECKPOINT': 'You prepared this report or asked for its review, so another reviewer decides it.',
  'errors.CHECKPOINT_EXPIRED': 'The checkpoint has expired; ask for review again.',
  'errors.REJECTION_NEEDS_NOTE': 'Say why the report is sent back.',
  'errors.FILES_CHANGED': 'The stored files no longer match the files approved; the checkpoint has expired. Ask for review again.',
  'errors.CHECKPOINT_DECIDED': 'This checkpoint is decided already.',
  'errors.PUBLISH_NOT_APPROVED': 'A report is published once a reviewer has approved it at its checkpoint.',
  'errors.FOUR_EYES_PUBLISH': 'You prepared this report or asked for its review, so another reviewer publishes it.',
  'errors.REPORT_RECOMPUTE_MISMATCH': 'The frozen analysis no longer matches its snapshot, so no report is made from it.',
  'errors.CHECKPOINT_NOT_PENDING': 'This report has no checkpoint waiting for a decision.',
  // The hand-over and the roadmap tracker (onboarding addendum §11.2, §11.4, §14).
  'engine.nav.handover': 'Hand-over',
  'engine.nav.roadmap': 'Roadmap tracker',
  'handover.title': 'Hand over to the work',
  'handover.notFrozen': 'The hand-over starts once the analysis is frozen.',
  'handover.byPerson': 'Nothing here is created by itself: each action, series and KRI is made by a person\'s act.',
  'handover.gaps': 'Gaps: an action for each',
  'handover.gapsIntro': 'One action for each gap, one gap at a time. The title, the owner and the due date are yours; the severity is proposed from the gap\'s priority, with its basis, and you may change both.',
  'handover.capOnly': 'Level 4 needs an independent assessment',
  'handover.capOnlyTitle': 'Independent assessment: {title}',
  'handover.notDetermined': 'Not determined: evidence to obtain',
  'handover.noGaps': 'No gap.',
  'handover.waveEnds': 'ends {date}',
  'handover.raise': 'Raise an action…',
  'handover.raiseSubmit': 'Raise the action',
  'handover.basisDefault': '{priority} by rule maturity.v1',
  'handover.basisHint': 'Proposed from the gap\'s priority; say why if you change the severity.',
  'handover.dueHint': 'Yours to set; the tracker also measures the action against the end of its wave.',
  'handover.applied': 'Added to the annual cycle.',
  'handover.cycle': 'Recurring activities the practices name',
  'handover.cycleIntro': 'Items of the annual cycle that carry out a practice with a gap and are not yet applied for this client. A lead applies them; each series records the analysis it came from.',
  'handover.noCycle': 'Nothing to apply.',
  'handover.forPractices': 'For {practices} · service {service}',
  'handover.apply': 'Apply',
  'handover.leadOnly': 'A lead of the client applies these.',
  'handover.cycleApplied': 'Already in the annual cycle: {list}',
  'handover.cycleNotOffered': 'Not offered, because the client has no service of their kind: {list}',
  'handover.models': 'Models of the inventory',
  'handover.modelsIntro': 'Each model is validated before use and after a significant change, in Valentin or by another party; the practice of model validation is assessed for each entry. A lead applies a revalidation series for each model.',
  'handover.noModels': 'The inventory holds no model.',
  'handover.seriesApplied': 'Series {code}',
  'handover.applyRevalidation': 'Apply the revalidation series',
  'handover.revalidationNoService': 'The client has no service of the kind this series needs.',
  'handover.kris': 'KRIs the practices suggest',
  'handover.krisIntro': 'A KRI for each practice in scope whose levels name one. It is created in the KRI register and linked to the practice.',
  'handover.noKris': 'Nothing to create.',
  'handover.createKri': 'Create the KRI…',
  'handover.krisLinked': 'Linked already: {list}',
  'handover.reassessment': 'Re-assessment',
  'handover.reassessmentIntro': 'The yearly re-assessment of the current state, by default on 30 September, a month before the draft of the business-wide risk assessment. A lead may choose another day on the series.',
  'handover.noReassessment': 'No service in this analysis\'s scope has a re-assessment item in the client\'s template.',
  'handover.applyReassessment': 'Apply the re-assessment series',
  'roadmap.title': 'Roadmap tracker',
  'roadmap.notFrozen': 'The roadmap is followed once the analysis is frozen.',
  'roadmap.summary': 'How far the roadmap has come',
  'roadmap.since': 'Baseline {code}, frozen {date} · today {today}',
  'roadmap.withAction': 'Gaps with an action: {n} of {of}',
  'roadmap.done': 'Actions verified: {n} of {of}',
  'roadmap.overdueAction': 'Overdue against the action\'s due date: {n} of {of} open actions',
  'roadmap.overdueWave': 'Overdue against the wave: {n} of {of} gaps',
  'roadmap.quickWins': 'Quick wins done: {n} of {of}',
  'roadmap.noAction': 'Gaps with no action yet: {n} of {of}',
  'roadmap.escalations': 'Escalation notices sent: {n} of {of} · their actions done: {d} of {dof}',
  'roadmap.intro': 'Counted from the frozen gaps and the actions as they stand today. A gap whose action is verified stays a gap until the next re-assessment confirms the level.',
  'roadmap.waveEnds': 'ends {date}',
  'roadmap.waveEnded': 'ended',
  'roadmap.waveCounts': '{gaps} gaps · with an action: {with} of {gaps} · done: {done} of {gaps}',
  'roadmap.waveEmpty': 'No gap in this wave.',
  'roadmap.capOnly': 'Independent assessment needed',
  'roadmap.capOnlyCounts': 'With an action: {with} of {of} · overdue against the wave: {overdue} of {of}',
  'roadmap.noActionTitle': 'Gaps with no action yet',
  'roadmap.noneWithoutAction': 'Every gap has an action.',
  'roadmap.col.practice': 'Practice',
  'roadmap.col.action': 'Action',
  'roadmap.col.state': 'Where it stands',
  'roadmap.due': 'due {date}',
  'roadmap.noActionYet': 'no action yet',
  'roadmap.overdueActionChip': 'overdue',
  'roadmap.overdueWaveChip': 'past the wave\'s end',
  'roadmap.dueAfterWave': 'due after the wave\'s end',
  'roadmap.verified': 'action verified; the level is confirmed at the next re-assessment',
  'roadmap.kris': 'KRIs since the freeze',
  'roadmap.krisCount': 'KRIs with a reading since the freeze: {n} of {of}',
  'roadmap.noKris': 'No KRI is linked to a practice of this analysis.',
  'roadmap.noReading': 'no confirmed reading since the freeze',
  'roadmap.threshold': 'threshold {value}',
  'standing.title': 'Where the client stands',
  'standing.byRule': 'computed by rule, no AI',
  'standing.none': 'No current-state analysis is frozen yet.',
  'standing.baseline': 'Baseline {code}, frozen {date}',
  'standing.function': '{fn}: {n} of {of} capabilities at or above target',
  'standing.function.aml': 'AML',
  'standing.function.governance': 'Governance',
  'standing.breaches': 'Possible breaches: {n} of {of} conclusions on rules in force',
  'standing.escalated': 'Escalated: {n}, notices sent {s} of {of}',
  'standing.overdueWave': 'Overdue against their wave: {n} of {of} gaps',
  'kriKind.training_completion': 'Training completion',
  'kriKind.customers_without_risk_profile': 'Customers without a risk profile',
  'kriKind.reviews_overdue': 'Customer reviews overdue',
  'kriKind.alert_backlog': 'Alert backlog',
  'kriKind.alert_handling_days': 'Days to handle an alert',
  'kriKind.detection_to_report_days': 'Days from detection to report',
  'kriKind.screening_runs_missed': 'Screening runs missed',
  'kriUnit.percent': 'per cent',
  'kriUnit.days': 'days',
  'kriUnit.count': 'number',
  'auditField.originRef': 'applied from',
  'auditAction.handover.gap_action': 'Raised an action from a gap',
  'auditAction.handover.apply_cycle_item': 'Applied a recurring activity from the hand-over',
  'auditAction.handover.apply_revalidation': 'Applied a model\'s revalidation series',
  'auditAction.handover.apply_reassessment': 'Applied the re-assessment series',
  'activity.current_state_analysis': 'current-state analysis',
  'activity.maturity_reassessment': 're-assessment of the current state',
  'errors.HANDOVER_NOT_FROZEN': 'The hand-over starts once the analysis is frozen.',
  'errors.HANDOVER_NOT_PROPOSED': 'The hand-over of this analysis does not propose that, or it is done already.',
  'errors.ACTION_NOT_FROM_GAP': 'An action from the current state is raised from a gap of a frozen analysis.',
  'errors.GAP_HAS_ACTION': 'This gap has an action already; cancel it first to raise another.',
  'errors.REVALIDATION_EXISTS': 'This model has its revalidation series already.',
  'errors.REVALIDATION_MODEL_RETIRED': 'A revalidation series is for a model of this client that is in use.',
  'errors.REVALIDATION_PER_MODEL': 'A revalidation series is applied for one model of the inventory, from the hand-over of a frozen analysis.',
  'nav.discover': 'Discover your client',
  'nav.discover.answers': 'Where does the client stand in the journey, and what do we still need?',
  'library.file.missing': 'Rule for what is still needed',
  'auditAction.missing.request_add': 'Added lines from what we still need',
  'auditAction.missing.interview_add': 'Added a question from what we still need to an interview',
  'errors.INQUIRY_NOT_DRAFT': 'Questions are added to an interview before it is held.',
  'errors.QUESTION_UNKNOWN': 'The interview guides have no such question.',
  'discover.title': 'Discover your client',
  'discover.intro': 'One place for the whole journey, from accepting the client to following up. Every figure is a count with its denominator.',
  'discover.unverifiable': 'The frozen analysis cannot be checked against its snapshot, so what we still need is not shown.',
  'discover.analysis': 'Current-state analysis {code} · {status} · as of {date}',
  'discover.noAnalysis': 'No current-state analysis is planned yet.',
  'discover.others': 'Other analyses of this client',
  'discover.ai.off': 'AI proposals: not switched on for this client',
  'discover.ai.on': 'AI for this client: on since {date} · this month {spent} of {cap}',
  'discover.ai.onNoCap': 'AI for this client: on since {date} · this month {spent}, with no monthly cap',
  'discover.stage.accept': 'Accept',
  'discover.stage.plan': 'Plan',
  'discover.stage.gather': 'Gather',
  'discover.stage.analyse': 'Analyse',
  'discover.stage.picture': 'Picture',
  'discover.stage.conclude': 'Conclude',
  'discover.stage.next': 'Next steps',
  'discover.stage.follow': 'Follow up',
  'discover.state.not_started': 'not started',
  'discover.state.in_progress': 'in progress',
  'discover.state.done': 'done',
  'discover.state.waiting': 'waits for the freeze',
  'discover.state.provisional': 'provisional',
  'discover.state.review': 'in review',
  'discover.state.closed': 'closed: the analysis is frozen',
  'discover.accept.checks': 'Start checks done or not applicable: {n} of {of} · exceptions confirmed: {e} · still open: {open}',
  'discover.plan.map': '{map}: {n} of {of} practices in scope · not assessed: {na}',
  'discover.plan.mapNa': '{map}: {n} of {of} practices in scope · not assessed: {na} · not applicable: {nap}',
  'discover.plan.bands': 'Inherent risk levels confirmed: {n} of {of} (assumed, as no rule could rate them: {a})',
  'discover.plan.scope': 'Practices left out, confirmed by a reviewer: {n} of {of}',
  'discover.plan.notUsed': 'Not in this analysis: {map} ({why})',
  'discover.plan.why.not_served': 'none of our services covers it',
  'discover.plan.why.types': 'not written for this kind of client',
  'discover.plan.why.jurisdiction': 'not written for the client’s home jurisdiction',
  'discover.plan.why.not_planned': 'not taken into this analysis',
  'discover.gather.requests': 'Requests: {n} (sent: {sent} · draft: {draft})',
  'discover.gather.requestsClosed': 'Requests: {n} (sent: {sent} · draft: {draft} · closed: {closed})',
  'discover.gather.lines': 'Lines answered: {n} of {of} (core lines: {c} of {cof}) · not held: {nh}',
  'discover.gather.overdue': 'Overdue: {n} requests, with {lines} lines still asked',
  'discover.gather.overdue.one': 'Overdue: {n} request, with {lines} lines still asked',
  'discover.gather.overdueNone': 'Overdue: none',
  'discover.gather.documents': 'Documents received on the lines: {n}',
  'discover.gather.evidence': 'Evidence items reviewed by a second member: {n} of {of}',
  'discover.gather.interviews': 'Interviews held: {n} of {of} planned',
  'discover.gather.statements': 'Statements confirmed by the participant: {n} of {of}',
  'discover.analyse.withFacts': 'Practices with facts: {n} of {of} in scope',
  'discover.analyse.states': 'Assessed: {a} · not determined: {nd} · not assessed: {nas} · not applicable: {nap} (of {of})',
  'discover.analyse.facts': 'Facts confirmed by a second member: {n} of {of}',
  'discover.analyse.inconsistencies': 'Open inconsistencies: {n} of {of}',
  'discover.picture.provisional': 'Provisional: the analysis is not frozen, so every picture here may still change.',
  'discover.picture.frozen': 'The baseline as frozen on {date}.',
  'discover.picture.internal': 'For internal use only while the library is not adopted.',
  'discover.picture.organisation': 'The organisation on one page',
  'discover.conclude.status': 'Status: {status}, sent by {name}',
  'discover.conclude.statusPlain': 'Status: {status}',
  'discover.conclude.validation': 'Validation meetings held: {n} of {of} planned',
  'discover.conclude.reportDrafted': 'Report: drafted',
  'discover.conclude.reportNone': 'Report: not drafted yet',
  'discover.next.gaps': 'Gaps: {n} (provisional)',
  'discover.next.gapsFrozen': 'Gaps: {n}',
  'discover.next.afterFreeze': 'Actions are raised from the gaps once the analysis is frozen.',
  'discover.follow.waits': 'Starts when the analysis is frozen.',
  'discover.follow.roadmap': 'Gaps with an action: {n} of {of} · waves overdue: {o} of {oof}',
  'discover.later.title': 'Comes later ({n})',
  'discover.later.inbox': 'Not built yet: an inbox for many files at once.',
  'discover.later.proposed_statements': 'Not built yet: statements proposed from a transcript.',
  'discover.later.ai_facts': 'Not built yet: facts proposed from the documents.',
  'discover.later.findings': 'Not built yet: findings and the mitigation plan.',
  'discover.later.suggestions': 'Not built yet: suggested next steps for each gap.',
  'discover.later.handoffs': 'Not built yet: hand-offs to the other experts.',
  'discover.later.reassessment': 'Not built yet: a re-assessment against this baseline.',
  'discover.missing.top': 'The first {n} of {total}',
  'discover.missing.all': 'All {total} items',
  'discover.act.close_checks': 'Close the open start checks',
  'discover.act.plan_analysis': 'Plan the current-state analysis',
  'discover.act.finish_plan': 'Finish the plan and start gathering',
  'discover.act.gather': 'Send the request and hold the interviews',
  'discover.act.see_missing': 'See what we still need ({n})',
  'discover.act.record_facts': 'Record the facts',
  'discover.act.open_picture': 'Open the organisation on one page',
  'discover.act.review_freeze': 'Review and freeze the analysis',
  'discover.act.draft_report': 'Draft the report',
  'discover.act.hand_over': 'Hand the gaps over to the work',
  'discover.act.open_roadmap': 'Open the roadmap tracker',
  'discover.act.none': 'nothing to do here now',
  'discover.act.lead_plans': 'The client lead plans the analysis.',
  'discover.upload': 'Upload documents',
  'discover.planInterview': 'Plan an interview',
  'discover.export': 'Export what we still need (DOCX)',
  'discover.work.open': 'Open the hub',
  'missing.title': 'What we still need',
  'missing.intro': 'Worked out by a rule, not by a model: for each practice in scope, what the analysis does not know yet at the practice’s next level (never past its target), and the catalogue item that would settle it, with an interview question on the practice. Nothing is added to a request or an interview until you add it.',
  'missing.provisional': 'Provisional: worked out from what is recorded now.',
  'missing.frozen': 'This analysis is frozen: the list shows what it did not know when it was frozen.',
  'missing.count': '{n} items',
  'missing.count.one': '{n} item',
  'missing.empty': 'Nothing: every practice in scope is settled at its next level, or meets its target.',
  'missing.col.practice': 'Practice',
  'missing.col.unknown': 'Still unknown',
  'missing.col.request': 'Settled by a request',
  'missing.col.interview': 'A question on this practice',
  'missing.level': 'Level {n}',
  'missing.fact': '{what}: {why}',
  'missing.element': '{code} {words}: {why}',
  'missing.reason.not_recorded': 'not recorded',
  'missing.reason.recorded_unknown': 'recorded as unknown',
  'missing.reason.claimed_not_evidenced': 'claimed, not evidenced',
  'missing.reason.derived_unknown': 'cannot be worked out yet',
  'missing.reason.not_checked': 'not checked',
  'missing.reason.not_quoted': 'no quote found yet',
  'missing.line': 'asked in request {request}, line {no} ({status})',
  'missing.lineNone': 'not asked yet',
  'missing.noItem': 'No catalogue item settles this: ask by hand.',
  'missing.noQuestion': 'The interview guides hold no question on this practice.',
  'missing.askedIn': 'asked in inquiry {inquiry} ({status})',
  'missing.askedNone': 'not in any interview yet',
  'missing.settles': 'settles {n} items of this list',
  'missing.settles.one': 'settles {n} item of this list',
  'missing.add.request': 'Add to the request',
  'missing.add.target': 'Add request lines to',
  'missing.add.new': 'a new request',
  'missing.add.interview': 'Add to the interview',
  'missing.add.inquiry': 'Add questions to',
  'missing.add.noDraft': 'Plan an interview first: questions are added to an interview before it is held.',
  'missing.add.done': 'Added: {items}.',
  'missing.add.none': 'Nothing was added.',
  'missing.add.skipped': 'Not added: {items}.',
  'missing.add.questionDone': 'The question is in the interview now.',
  'missing.add.questionThere': 'The question was in the interview already.',
  'missing.skipped.on_open_request': 'already asked on an open request',
  'missing.skipped.withdrawn_here': 'withdrawn from this request; add it to another',
  'missing.newRequestTitle': '{title}: what we still need',
  'missing.readOnly': 'Adding is for the working members of the client, while the analysis is open.',
  'missing.export': 'Export as a request letter (DOCX)',
  'engine.nav.missing': 'What we still need',
  'picture.title': 'The organisation on one page',
  'picture.internal': 'Internal use only: for the firm, not for the client.',
  'picture.internalChip': 'internal',
  'picture.asOf': 'Figures as of {date}',
  'picture.section.business': 'The business in figures',
  'picture.section.offers': 'What the client offers',
  'picture.section.bands': 'Inherent risk',
  'picture.section.who': 'Roles in the mandate, and who holds them',
  'picture.noRoles': 'No roles in the mandate recorded.',
  'picture.section.contacts': 'People at the client',
  'picture.section.systems': 'Systems and models',
  'picture.section.inherited': 'What we took over',
  'picture.section.services': 'Our services',
  'picture.value': '{value} (to {date})',
  'picture.later': 'later: {value} to {date}, not confirmed',
  'picture.confirmed': 'confirmed',
  'picture.notConfirmed': 'not confirmed',
  'picture.noFigures': 'No figure is recorded at or before this date.',
  'picture.noneRecorded': 'None recorded.',
  'picture.role': '{role}: {mode} · our person {person}',
  'picture.roleHolder': '{role}: {mode} · our person {person} · the client’s holder {holder}',
  'picture.neverValidated': 'never validated',
  'picture.validatedOn': 'last validated {date}',
  'picture.inherited': '{service}: alerts not handled {alerts} · reviews overdue {reviews} · customers not screened {screened} · open audit findings {findings}',
  'picture.unknownCount': 'not known',
  'picture.acknowledged': 'acknowledged by the client',
  'picture.notAcknowledged': 'not yet acknowledged by the client',
  'picture.cycleItems': '{n} annual-cycle items this year',
  'picture.cycleItems.one': '{n} annual-cycle item this year',
  'picture.export': 'Export (DOCX, internal)',
  'picture.fte': '{n} FTE',
  'picture.eur': 'EUR {n}',
  'picture.pct': '{n} %',
  'engine.nav.heatmap': 'Heat map',
  'engine.nav.role': 'Our role',
  'heat.title': 'Capability heat map',
  'heat.intro': 'Worked out by rules from what is recorded, never by a model and never as an average: each cell is a capability’s level against its target, with the gap in words. Select a cell to see why.',
  'heat.provisional': 'Provisional: hatched cells rest on facts or duty conclusions not yet confirmed. Not yet confirmed: {u} of {n} facts, {d} of {m} duty conclusions.',
  'heat.confirmedOnly': 'Not frozen: worked out from what is recorded now, and every fact and duty conclusion it rests on is confirmed.',
  'heat.frozen': 'This analysis is frozen: the cells are its stored results.',
  'heat.rule': 'Worked out by the heat map rule, version {heat}, from the maturity rule, version {maturity}: Olivia’s proposals, not yet adopted by the regulatory desk.',
  'heat.legend': 'Legend',
  'heat.hatched': 'Hatched: provisional',
  'heat.dashed': 'Dashed border: a possible breach',
  'heat.filter': 'Show',
  'heat.filter.all': 'All capabilities',
  'heat.filter.legal': 'Legal duties only',
  'heat.filter.changed': 'Not built yet: the capabilities changed since the last assessment.',
  'heat.filter.hidden': '{n} of {total} capabilities shown',
  'heat.view': 'View',
  'heat.view.grid': 'Grid',
  'heat.view.table': 'Table',
  'heat.view.radar': 'Radar and bars',
  'heat.overlays': 'Overlays: outlines and counts only, never a value',
  'heat.overlay.needed': 'Still needed: count per cell',
  'heat.overlay.role': 'Our role: outline',
  'heat.needed': 'still needed: {n}',
  'heat.assessedOf': 'assessed: {n} of {of} practices',
  'heat.roleOnCell': 'our role: {groups}',
  'heat.col.capability': 'Capability',
  'heat.col.cell': 'Cell',
  'heat.col.words': 'In words',
  'heat.radar.title': 'Radar: level and target per capability',
  'heat.radar.note': 'The table is the authority: a radar changes shape with the order of its axes, so it sits beside the table, never alone. A capability not determined has no point.',
  'heat.radar.desc': '{name}: the target of each capability as a dashed line, its level as a dot, on a scale from 0 at the centre to 4 at the edge.',
  'heat.radar.few': 'A radar needs at least three capabilities: the bars show them.',
  'heat.bars.title': 'Level and target per capability',
  'heat.bar.level': 'level {n}',
  'heat.bar.target': 'target {n}',
  'heat.bar.none': 'level not determined',
  'heat.empty': 'No capability in this analysis yet: plan its scope first.',
  'heat.why.title': 'Why: {cap}',
  'heat.why.close': 'Close',
  'heat.why.practices': 'The practices that decide this cell',
  'heat.why.pick': 'Select a practice to see its level and why.',
  'heat.why.level': 'Level {level}, target {target}: {gap} below',
  'heat.why.levelMet': 'Level {level}, target {target}: at or above target',
  'heat.why.priority': 'priority {p}',
  'heat.why.notDetermined': 'Not determined: whether it is performed is not known yet.',
  'heat.why.notAssessed': 'Not assessed: {reason}',
  'heat.why.notApplicable': 'Not applicable: {reason}',
  'heat.why.rule': 'Computed by the maturity rule, version {version}',
  'heat.why.gate.met': 'Level {n} met',
  'heat.why.gate.not_met': 'Level {n} not met',
  'heat.why.gate.unknown': 'Level {n} not known yet',
  'heat.why.facts': 'Facts',
  'heat.why.noFacts': 'No fact is recorded.',
  'heat.why.fact': '{fact}: {value}',
  'heat.why.recorded': 'Recorded by {name}',
  'heat.why.confirmed': 'confirmed by {name}',
  'heat.why.unconfirmed': 'not confirmed by a second member',
  'heat.why.quote': '“{quote}” ({locator}) · {check}',
  'heat.why.quoteNoPlace': '“{quote}” · {check}',
  'heat.quote.found': 'quote found',
  'heat.quote.not_found': 'quote not found',
  'heat.quote.not_checkable': 'quote cannot be checked',
  'heat.why.elements': 'Required elements',
  'heat.why.element': '{element}: {state}',
  'heat.element.present': 'present',
  'heat.element.absent': 'absent',
  'heat.element.not_applicable': 'not applicable',
  'heat.why.duty': 'Duty conclusions in force',
  'heat.why.noDuty': 'No duty conclusion in force.',
  'heat.why.proposed': 'proposed: {value}',
  'heat.why.decided': 'decided by {name}: {value}',
  'heat.why.notDecided': 'not decided yet',
  'heat.why.dutyConfirmed': 'confirmed by {name}',
  'heat.why.dutyUnconfirmed': 'not confirmed yet',
  'heat.why.role': 'Our role',
  'heat.why.notAGap': 'Not a gap, so it has no group here.',
  'heat.why.noGroup.not_assessed': 'Not assessed in this analysis, so it is in no group.',
  'heat.why.noGroup.not_determined': 'Not determined, so it is in no group until its level is known.',
  'heat.why.noGroup.not_applicable': 'Not applicable to this client, so it is in no group.',
  'heat.why.next': 'Next steps',
  'heat.why.equal': 'Recomputed from the snapshot: equal to the stored result.',
  'heat.why.differs': 'Recomputed from the snapshot: differs from the stored result ({fields}).',
  'heat.why.live': 'Not frozen: the level is computed on every read from what is recorded now, so there is nothing stored to compare it with.',
  'heat.why.open': 'Open the practice',
  'heat.field.final_level': 'final level',
  'heat.field.computed_level': 'computed level',
  'heat.field.target_level': 'target',
  'heat.field.gap': 'gap',
  'heat.field.priority': 'priority',
  'heat.field.wave': 'wave',
  'heat.field.quick_win': 'quick win',
  'heat.field.cap_only': 'gap from the cap only',
  'heat.field.cap_reason': 'the cap',
  'heat.field.performer': 'who performs it',
  'heat.field.state': 'state',
  'heat.field.reasons': 'the level gates',
  'heat.field.evidence_ids': 'the evidence',
  'heat.field.missing': 'a stored row missing',
  'heat.field.extra': 'a stored row too many',
  'heat.field.snapshot': 'the snapshot cannot be read again',
  'role.title': 'Our role',
  'role.intro': 'Each gap of the analysis in exactly one group, decided by rule from the services, who performs the practice, the tasks the client keeps and the client’s own acts. Never by a model.',
  'role.count': '{group}: {n}',
  'role.none': 'No gap in this group.',
  'role.offers': 'Possible offers: {list}',
  'role.noOffer': 'Possible offers: none, since the client holds every service of this map.',
  'role.proposal': 'Draft an engagement proposal for the gaps outside what the client bought (module)',
  'role.independence': 'Independence: {words}',
  'role.capOnly': 'Gaps caused only by the self-assessment cap, in no group: level 4 needs an independent assessment, which is no gap of the client.',
  'role.day': 'The services as they stand on {date}.',
  'role.rule': 'Worked out by the role rule, version {version}: Olivia’s proposal, not yet adopted by the regulatory desk.',
  'role.noGaps': 'No gap in this analysis yet.',
  'role.levelLine': 'level {level}, target {target}, priority {p}',
  'discover.picture.heatmap': 'Capability heat map',
  'discover.picture.role': 'Our role',
  'library.file.heat': 'Heat map rule',
  'library.file.role': 'Rule for our role',
  'heat.item.met': 'met',
  'heat.item.not_met': 'not met',
  'heat.item.unknown': 'not known yet',
  // IP3a: the regulation view, the regime screening, the calendar and the overlay of the changes on the heat map.
  'engine.nav.regulation': 'By regulation',
  'engine.nav.calendar': 'Going forward',
  'regulation.title': 'By regulation',
  'regulation.intro': 'Where the client stands under each regime on the chosen day. A cell counts the duty conclusions in force; a rule that is still coming shows readiness, never a breach; a directive addressed to the Member States is a note, never a cell.',
  'regulation.on': 'On',
  'regulation.today': 'Today, {date}',
  'regulation.otherDay': 'Another day',
  'regulation.caption': 'Domains by regime on {date}',
  'regulation.domain': 'Domain',
  'regulation.empty': 'No practice in scope cites this regime in this domain',
  'regulation.legend': 'Legend',
  'regulation.legend.A': 'every conclusion met',
  'regulation.legend.C': 'the worst conclusion is partly met',
  'regulation.legend.D': 'a duty not met: a possible breach',
  'regulation.legend.unknown': 'not determined, or not yet decided by a person',
  'regulation.legend.readiness': 'readiness, or applies but not yet concluded: never a breach',
  'regulation.legend.hatched': 'Hatched: a conclusion not yet decided, or not yet confirmed by a second person',
  'regulation.state.in_force': 'in force',
  'regulation.state.coming': 'coming: applies from {date}',
  'regulation.state.end_expected': 'last day in force expected {date}, not enacted',
  'regulation.state.ended': 'no longer in force',
  'regulation.count.met': '{n} met',
  'regulation.count.partly_met': '{n} partly met',
  'regulation.count.not_met': '{n} not met',
  'regulation.count.not_determined': '{n} not determined',
  'regulation.count.undecided': '{n} awaiting a decision',
  'regulation.unconfirmed': '({n} decided, not confirmed)',
  'regulation.pending': 'applies from {date}: not yet concluded; readiness {ready} of {total}',
  'regulation.readiness': 'readiness: {ready} ready · {not_yet} not yet',
  'regulation.applies.yes': 'applies',
  'regulation.applies.no': 'does not apply',
  'regulation.applies.not_determined': 'not determined',
  'regulation.why': 'Why: {reason}',
  'regulation.parts': 'Parts in play: {list}',
  'regulation.partsNone': 'Parts in play: none named',
  'regulation.evidence': 'Evidence held: {list}',
  'regulation.evidenceNone': 'Evidence held: none',
  'regulation.lookedAfter': 'Looked after by: {who}',
  'regulation.who.our_service': 'our service',
  'regulation.who.client': 'the client',
  'regulation.who.other_firm': 'another firm',
  'regulation.who.unknown': 'not known',
  'regulation.note': 'Note: {note}',
  'regulation.recorded': 'Recorded by {name} on {date}',
  'regulation.notScreened': 'not yet screened',
  'regulation.record': 'Record the screening',
  'regulation.change': 'Change the screening',
  'regulation.cancel': 'Cancel',
  'regulation.formTitle': 'The screening of a regime',
  'regulation.form.applies': 'Does it apply to the client?',
  'regulation.form.reason': 'Why',
  'regulation.form.parts': 'Parts in play',
  'regulation.form.evidence': 'Evidence held: document codes, separated by commas',
  'regulation.form.who': 'Who looks after it',
  'regulation.form.note': 'Note (optional)',
  'regulation.adopted': 'Regimes rule, version {v}: adopted by your regulatory desk.',
  'regulation.unadopted': 'Regimes rule, version {v}: Olivia’s proposal, not adopted by your regulatory desk.',
  'regulation.noColumns': 'No legal duty in scope cites a regime that has a column.',
  'regulation.screenedHeading': 'Screened, not rated',
  'regulation.screenedIntro': 'Regimes that reach the client and that no map rates yet: whether each applies, the parts in play, the evidence held and who looks after it. A screening is a fact for a person to read; it never makes a cell, a level or a conclusion.',
  'regulation.saved': 'The screening is recorded.',
  'regulation.noScreening': 'No regime to screen reaches this client.',
  'regulation.notesHeading': 'What affects you',
  'regulation.notesIntro': 'Directives addressed to the Member States, and the acts behind today’s rules: notes, never cells, because they set no duty of the client’s own.',
  'regulation.source': 'Source: {citation}',
  'regulation.touches': 'Touches the domains: {list}',
  'regulation.readinessHeading': 'Readiness for rules not yet in force',
  'regulation.noReadiness': 'No rule of this analysis comes into force after its as-of date.',
  'regulation.readinessLine': '{regime} applies from {date}: {ready} of {total} legal duties met already',
  'calendar.title': 'What matters going forward',
  'calendar.intro': 'Dated milestones of the regimes that reach the client, the legal duties of this analysis that start or stop applying, rule changes for the client’s roles and its own analysis items. Each date is as precise as its source.',
  'calendar.from': 'From {date}',
  'calendar.empty': 'Nothing is dated from this day.',
  'calendar.when': 'When',
  'calendar.what': 'What',
  'calendar.source': 'Source',
  'calendar.starts': '{regime}: legal duties of this analysis start to apply',
  'calendar.stops': '{regime}: legal duties of this analysis apply for the last time',
  'calendar.role': 'A rule for the role “{role}” changes',
  'calendar.expected': 'expected, not enacted',
  'calendar.readiness': 'readiness: {ready} of {total} met already',
  'calendar.ends': '{n} legal duties of this analysis apply for the last time this day',
  'calendar.ends.one': '1 legal duty of this analysis applies for the last time this day',
  'calendar.source.roles': 'The rules of the roles',
  'calendar.source.cycle': 'The client’s annual cycle',
  'calendar.source.maps': 'The references of the maps',
  'calendar.quarter': 'Q{q} {y}',
  'calendar.note': 'Every date is read from its source; none is worked out by a model.',
  'heat.overlay.changes': 'Changes on {date}',
  'heat.changes.cell': 'changes on {date}: {parts}',
  'heat.changes.starts': '{n} rules start to apply ({ready} of {n} met already)',
  'heat.changes.starts.one': '1 rule starts to apply ({ready} of 1 met already)',
  'heat.changes.stops': '{n} stop applying',
  'heat.changes.stops.one': '1 stops applying',
  'discover.plan.screening': 'Regime screening: {n} of {of} regimes screened',
  'discover.plan.screeningParts': '{state}, parts in play: {n} of {of}',
  'discover.picture.regulation': 'By regulation',
  'discover.picture.calendar': 'What matters going forward',
  'errors.SCREENING_REGIME_NOT_SCREENED': 'Only a regime that no map rates yet is screened.',
  'errors.SCREENING_REGIME_RATED': 'This analysis rates the regime from a map, so it is not screened.',
  'errors.SCREENING_PART_UNKNOWN': 'The regime has no part “{part}”.',
  'errors.SCREENING_NOT_APPLIES_PARTS': 'A regime that does not apply has no parts in play and no one looking after it.',
  'errors.SCREENING_DOCUMENT_UNKNOWN': 'The evidence names a document this client does not hold: {document}.',
  'errors.INVALID_DAY': 'A day is written YYYY-MM-DD, and a window ends after it starts.',
  'record.regimeScreening': 'Regime screening',
  'auditAction.regime.screen': 'Recorded the screening of a regime',
  'auditField.regime': 'regime',
  'auditField.screeningApplies': 'applies',
  'auditField.screeningParts': 'parts in play',
  'auditField.lookedAfterBy': 'looked after by',
  'regimeName.se_aml': 'Swedish AML rules today',
  'regimeName.amlr': 'AMLR',
  'regimeName.dora': 'DORA',
  'regimeName.gdpr': 'GDPR',
  'regimeName.safeguarding': 'safeguarding of users’ funds',
  'regimeName.complaints': 'complaints handling',
  'regimeName.incident_reporting': 'incident and fraud reporting',
  'regimeName.se_psa': 'the Payment Services Act',
  'regimeName.se_emoney': 'the E-Money Act',
  'engine.nav.affects': 'What affects us',
  'discover.picture.affects': 'What affects us',
  'discover.picture.summary': 'Discover summary (DOCX, internal, two pages)',
  'affects.title': 'What affects us',
  'affects.intro': 'What this client’s work asks of us as its provider: the agreement’s points under EBA/GL/2019/02 paragraph 75, the client’s yearly review of us, rule changes for the roles we hold or support, the capacity the lead states, roles per person and what we took over.',
  'affects.items.notAdopted': 'The points of paragraph 75 are Olivia’s proposal, quoted from the EBA’s texts; your regulatory desk has not adopted them yet.',
  'affects.summary.title': 'Discover summary',
  'affects.summary.note': 'Internal and provisional: for the kick-off conversation, not for the client. Every figure in it is checked against the records before the file is made.',
  'affects.summary.docx': 'Discover summary (DOCX, internal, two pages)',
  'affects.summary.pdf': 'Discover summary (PDF)',
  'affects.agreement.title': 'The agreement (EBA/GL/2019/02 paragraph 75)',
  'affects.agreement.line': '{service} {name}: {found} of {of} points found',
  'affects.agreement.missing': ' · missing: {list}',
  'affects.agreement.na': ' · not applicable: {list}',
  'affects.agreement.unchecked': ' · not yet checked: {n}',
  'affects.agreement.point': '({letter}) {short}',
  'affects.agreement.state.done': ' · the check is done',
  'affects.agreement.state.not_applicable': ' · the check does not apply',
  'affects.agreement.state.exception': ' · started on an exception',
  'affects.agreement.noCheck': '{service} {name}: the start checks are not made yet',
  'affects.agreement.none': 'No service of ours calls for this check.',
  'affects.agreement.points': 'The points, one by one, as paragraph 75 words them',
  'affects.agreement.record': 'Record the points on the mandate page',
  'affects.reviews.title': 'The client’s yearly review of us',
  'affects.review.due': '{service} {name}: the client’s review ({series}), next due {date}',
  'affects.review.none': '{service} {name}: the client’s review ({series}), no occurrence ahead',
  'affects.review.noSeries': '{service} {name}: not in the annual cycle yet',
  'affects.reviews.none': 'No outsourced function of ours calls for the client’s yearly review.',
  'affects.changes.title': 'Rule changes for the roles we hold or support',
  'affects.change.tasks': '{role}, supported by {person} ({service}): from {date} the rule no longer lets us perform {n} of the delegated tasks',
  'affects.change.held': '{role}, held by {person} ({service}): from {date} the rule no longer lets us hold it',
  'affects.change.interim': '{role}, covered ad interim by {person} ({service}): from {date} the rule no longer lets us cover it ad interim',
  'affects.changes.none': 'No rule ahead changes a role we hold or support.',
  'affects.capacity.title': 'Capacity, as the lead states it',
  'affects.capacity.line': '“{statement}” Stated by {by} on {date}.',
  'affects.capacity.none': 'Not stated yet.',
  'affects.capacity.never': 'Olivia computes no capacity: it shows what the lead states, dated and named.',
  'affects.capacity.state': 'State the capacity',
  'affects.capacity.label': 'The capacity, in your words',
  'affects.capacity.hint': 'For example who covers the work in the coming quarter. At most 500 characters.',
  'affects.capacity.save': 'Record the statement',
  'affects.capacity.saved': 'The statement is recorded.',
  'affects.people.title': 'Roles per person',
  'affects.person': '{person}: roles here {here} · over the clients you can see {visible}',
  'affects.personAbove': ' · above the firm’s threshold of {max}',
  'affects.people.none': 'No one holds, covers or supports a role here.',
  'affects.people.note': 'Roles held, covered or supported, counted over the clients you can see, as on the people page.',
  'affects.inherited.title': 'What we took over',
  'affects.inherited.line': '{service} {name}, inherited position as of {date}, {ack}: {list}',
  'affects.inherited.ack.yes': 'acknowledged',
  'affects.inherited.ack.no': 'not yet acknowledged',
  'affects.inherited.nothing': 'nothing open',
  'affects.inherited.notKnown': 'not known: {list}',
  'affects.inherited.none': 'No inherited position recorded.',
  'mandate.item.foundNeeds': 'A point found needs where it is in the document and a quote from it.',
  'errors.ITEM_FOUND_NEEDS_QUOTE': 'A point found needs the document, where it is in it, and a quote from it.',
  'errors.ITEM_QUOTE_NOT_CHECKABLE': 'The quote cannot be checked: {document} has no text to read yet. Record the point once its text is read, or quote a document with text.',
  'errors.SUMMARY_INCONSISTENT': 'The summary does not agree with the records it was made from, so no file was made. Try again; if it stays so, tell your administrator.',
  'errors.SUMMARY_TOO_LONG': 'The summary does not fit on {max} pages, so no file was made.',
  'record.capacityStatement': 'Capacity statement',
  'record.drift': 'Condition in the inbox',
  'auditAction.capacity.state': 'Stated the capacity',
  'auditField.capacityStatement': 'the capacity, as stated',
  'auditField.statedBy': 'stated by',
  'auditField.statedAt': 'stated at',
  'exportKind.discover_summary': 'the Discover summary',
  'auditAction.export.discover_summary': 'Exported the Discover summary',
  'exportKind.gap_log': 'the gap log of an analysis',
  'auditAction.export.gap_log': 'Exported the gap log of an analysis',
  // IP4a: accepting the client, the interview consent record and transcripts by hand (redaction as an option).
  'onboarding.step.accept': 'Accept the client',
  'acceptance.title': 'Accept the client',
  'acceptance.intro': 'Before the firm takes the client on, a lead or a partner answers the checklist and decides; a second member with the lead, reviewer or partner role confirms. A refusal is a decision too.',
  'acceptance.notAdopted': 'The checklist is Olivia’s proposal, not yet adopted by your regulatory desk.',
  'acceptance.current.accepted': '{code}: accepted on {on} by {by}, confirmed on {confirmedOn} by {confirmedBy}.',
  'acceptance.current.declined': '{code}: declined on {on} by {by}, confirmed on {confirmedOn} by {confirmedBy}.',
  'acceptance.waiting.accepted': '{code}: accepted by {by} on {on}, waiting for a second person to confirm.',
  'acceptance.waiting.declined': '{code}: declined by {by} on {on}, waiting for a second person to confirm.',
  'acceptance.review': 'Review by {date}.',
  'acceptance.reviewDue': 'review due',
  'acceptance.reviewOverdue': 'review overdue',
  'acceptance.none': 'No decision on accepting the client yet.',
  'acceptance.blocked.ACCEPTANCE_OPEN': 'The client starts once a decision to accept it is confirmed, or on an exception two people record.',
  'acceptance.blocked.CLIENT_DECLINED': 'The client was declined, so it does not start.',
  'acceptance.waitingTitle': 'The decision waiting for its confirmation',
  'acceptance.reasonIs': 'Reason: {reason}',
  'acceptance.sourceIs': 'source: {source}',
  'acceptance.choose': 'Choose an answer',
  'transcript.chooseParticipant': 'Choose a participant',
  'acceptance.notAnswered': 'not answered',
  'acceptance.documents': 'Documents: {list}',
  'acceptance.confirm': 'Confirm the decision',
  'acceptance.withdraw': 'Withdraw the decision',
  'acceptance.confirmWho': 'A second member with the lead, reviewer or partner role confirms; the person who decided does not.',
  'acceptance.warningsTitle': 'Conflicts and capacity, as the firm’s records show them',
  'acceptance.noWarnings': 'No warning: no one on two lines of defence, no internal audit beside our other work, and no one above the firm’s threshold of roles.',
  'acceptance.serviceLead': 'led by {name}',
  'acceptance.noLead': 'no lead named',
  'acceptance.formTitle': 'The checklist and the decision',
  'acceptance.formIntro': 'Answer each point. To accept, every point rests on an answer an acceptance may rest on; to decline, the reason is enough.',
  'acceptance.source': 'Where the answer comes from',
  'acceptance.sourceHint': 'The list, register or archive searched, and when.',
  'acceptance.words': 'In your words',
  'acceptance.capacityWords': 'The capacity, in your words',
  'acceptance.capacityHint': 'Who does the work, and from when. Olivia computes no hours.',
  'acceptance.why': 'Why',
  'acceptance.note': 'What was found',
  'acceptance.refusedOption': 'A client is not accepted on this answer; it may be declined on it.',
  'acceptance.addDocument': 'A document the decision rests on',
  'acceptance.attach': 'Add the document',
  'acceptance.decisionReason': 'The reason for the decision',
  'acceptance.reasonNeeded': 'Give the reason, at least three characters.',
  'acceptance.missing': 'To accept, answer these points in full: {list}',
  'acceptance.decide.accept': 'Accept the client',
  'acceptance.decide.decline': 'Decline the client',
  'acceptance.secondPerson': 'The decision waits for a second person’s confirmation before the client can start.',
  'acceptance.exceptionTitle': 'Starting before the acceptance',
  'acceptance.exceptionIntro': 'A lead may ask to start the client before the acceptance is decided; a second person confirms. A declined client starts on no exception.',
  'acceptance.exceptionAsked': 'Asked for by {by} on {on}, not yet confirmed. Reason: {reason}',
  'acceptance.exceptionConfirmed': 'Asked for by {by}, confirmed by {confirmedBy}. Reason: {reason}',
  'acceptance.askException': 'Ask to start on an exception',
  'acceptance.confirmException': 'Confirm the exception',
  'acceptance.historyTitle': 'Earlier decisions',
  'acceptance.withdrawnOn': 'Withdrawn on {date} by {by}.',
  'acceptance.basisTitle': 'Why these points are asked',
  'acceptanceDecision.accepted': 'accepted',
  'acceptanceDecision.declined': 'declined',
  'discover.accept.accepted': 'Accepted: {code} by {by} on {on}, confirmed by {confirmedBy} · review by {review}',
  'discover.accept.declined': 'Declined: {code} by {by} on {on}, confirmed by {confirmedBy}',
  'discover.accept.waiting': 'Waiting for confirmation: {code}, decided by {by} on {on}',
  'discover.accept.none': 'Not accepted yet: no decision on accepting the client',
  'discover.accept.exception': 'Not accepted yet: started on an exception two people confirmed',
  'discover.accept.reviewDue': 'the review is due',
  'discover.accept.reviewOverdue': 'the review is overdue',
  'discover.act.accept_client': 'Accept the client',
  'discover.gather.consent': 'Participants’ consent: yes {yes} of {of} · no {no} · withdrawn {withdrawn} · not recorded {missing}',
  'discover.gather.transcripts': 'Transcripts: {n} (consent withdrawn: {withdrawn})',
  'consent.title': 'Consent',
  'consent.intro': 'Each participant is given the notice of what is recorded and why, and says yes or no to the tool the transcript is made with.',
  'consent.noParticipants': 'No participant yet: add the participants first.',
  'consent.line.none': 'no consent recorded',
  'consent.line.yes': 'yes to “{tool}”',
  'consent.line.no': 'no to “{tool}”',
  'consent.line.notice': ' · notice given on {date}',
  'consent.line.withdrawn': 'consent withdrawn on {date} (was yes to “{tool}”)',
  'consent.record': 'Record the consent',
  'consent.change': 'Change the answer',
  'consent.withdraw': 'Record a withdrawal',
  'consent.withdrawn': 'The withdrawal is recorded: the statements drawn from the transcript no longer count as evidence.',
  'consent.withdrawNote': 'A participant can withdraw their consent at any time. Once the transcript is attached, a consent can only be withdrawn, not changed.',
  'consent.tool': 'The tool the transcript is made with',
  'consent.toolHint': 'For example “typed by the interviewer” or the name of the transcription tool.',
  'consent.answer': 'The participant’s answer',
  'consent.noticeOn': 'Notice given on',
  'consent.noticeNeeded': 'A participant from the client is given the notice before consent is recorded.',
  'consent.noticeDocument': 'The notice, as a document (if any)',
  'consent.save': 'Save the consent',
  'consentAnswer.yes': 'yes',
  'consentAnswer.no': 'no',
  'transcript.title': 'Transcript',
  'transcript.noAudio': 'No audio is stored: a transcript is text, typed here or from a document.',
  'transcript.consentRule': 'A transcript is attached only once every participant has said yes to its tool.',
  'transcript.none': 'No transcript yet.',
  'transcript.line': '{code} · tool: {tool} · speakers: {speakers}',
  'transcript.attached': 'Attached by {by} on {on}.',
  'transcript.withdrawn': 'Consent to this transcript is withdrawn: the statements drawn from it no longer count as evidence.',
  'transcript.text.pending': 'the text is being read',
  'transcript.text.not_checkable': 'no text could be read: quotes cannot be checked',
  'transcript.text.failed': 'the text could not be read',
  'transcript.redactedCounts': 'Redacted before saving: {counts}',
  'transcript.nothingRedacted': 'nothing',
  'redaction.count.name': 'names {n}',
  'redaction.count.phone': 'phone numbers {n}',
  'redaction.count.email': 'e-mail addresses {n}',
  'redaction.count.id': 'identity numbers {n}',
  'redaction.count.account': 'account or card numbers {n}',
  'redaction.kind.name': 'a name',
  'redaction.kind.phone': 'a phone number',
  'redaction.kind.email': 'an e-mail address',
  'redaction.kind.id': 'an identity number',
  'redaction.kind.account': 'an account or card number',
  'transcript.how': 'How the transcript arrives',
  'transcript.typed': 'Type or paste the transcript',
  'transcript.toolHint': 'The same tool every participant said yes to.',
  'transcript.speakers': 'Speakers',
  'transcript.speakersHint': 'Map each label in the transcript (S1, a name, an initial) to a participant: what they said is attributed by their role.',
  'transcript.label': 'Speaker in the transcript',
  'transcript.speakerIs': 'Participant',
  'transcript.addSpeaker': 'Add a speaker',
  'transcript.titleField': 'Title',
  'transcript.text': 'The transcript',
  'transcript.textHint': 'One line per turn, each beginning with the speaker’s label.',
  'transcript.loadFile': 'Or load it from a text file',
  'transcript.redact': 'Redact names and numbers before saving',
  'transcript.redactHint': 'Code proposes the names, phone numbers, e-mail addresses, identity and account numbers it sees; you decide each. It does not find a first name alone or a name in lower case: read the text yourself.',
  'transcript.findSpans': 'Find names and numbers',
  'transcript.noSpans': 'Code found nothing to redact.',
  'transcript.notAName': 'not a person’s name: keep it',
  'transcript.replacedBy': 'replaced by {word}',
  'transcript.phrase': 'Redact another word or phrase',
  'transcript.addPhrase': 'Redact it',
  'transcript.readYourself': 'This is a proposal, never a clearance: read the result before you save.',
  'transcript.preview': 'What will be saved',
  'transcript.save': 'Save the transcript',
  'transcript.saved': 'The transcript is attached.',
  'transcript.statementFrom': 'from the transcript',
  'transcript.statementWithdrawn': 'consent withdrawn',
  'transcript.quoteIs': 'Quote: “{quote}”',
  'transcript.quoteField': 'A quote from the transcript (optional)',
  'transcript.quoteHint': 'Copy the words exactly as they stand in the transcript; they are checked against it.',
  'transcript.noLabel': 'No label',
  'docKind.transcript': 'transcript',
  'errors.ACCEPTANCE_DECIDER_ROLE': 'A lead or a partner decides on accepting a client.',
  'errors.ACCEPTANCE_PENDING': 'A decision on accepting the client waits for its confirmation already.',
  'errors.ACCEPTANCE_INCOMPLETE': 'Some points of the checklist are not answered in full for an acceptance.',
  'errors.ACCEPTANCE_DECIDED': 'This decision is confirmed or withdrawn already.',
  'errors.ACCEPTANCE_CONFIRMER_ROLE': 'A lead, a reviewer or a partner confirms.',
  'errors.FOUR_EYES_ACCEPTANCE': 'You decided this, so a second person confirms it.',
  'errors.EXCEPTION_REQUESTER_ROLE': 'A lead asks for an exception to the acceptance.',
  'errors.EXCEPTION_PENDING': 'An exception to the acceptance is asked for already.',
  'errors.EXCEPTION_DECIDED': 'This exception is confirmed or withdrawn already.',
  'errors.CLIENT_DECLINED': 'The client was declined, so it does not start, on an exception or otherwise.',
  'errors.ACCEPTANCE_OPEN': 'The client is not accepted yet: a confirmed decision, or an exception two people record, comes first.',
  'errors.CONSENT_MISSING': 'Every participant says yes to the transcript’s tool before it is attached: {yes} of {of} have.{tools}',
  'errors.CONSENT_FIXED': 'Once the transcript is attached, a consent can only be withdrawn, not changed.',
  'errors.CONSENT_NOT_YES': 'Only a yes is withdrawn.',
  'errors.CONSENT_WITHDRAWN': 'Consent to the transcript is withdrawn: nothing more is drawn from it.',
  'errors.NOTICE_NEEDED': 'A participant from the client is given the notice before consent is recorded.',
  'errors.TRANSCRIPT_ATTACHED': 'The inquiry has its transcript already.',
  'errors.TRANSCRIPT_NOT_FOR_QUESTIONNAIRE': 'A questionnaire has no transcript.',
  'errors.SPEAKER_LABEL_UNKNOWN': 'The speaker label is not one of the transcript’s.',
  'errors.NO_TRANSCRIPT': 'The inquiry has no transcript to quote.',
  'errors.TRANSCRIPT_NOT_READ': 'The transcript’s text is not read yet. Try again in a moment.',
  'errors.TRANSCRIPT_NOT_CHECKABLE': 'The transcript has no text a quote can be checked against.',
  'errors.REDACTION_INCOMPLETE': 'Something that identifies a person is still in the text: find the names and numbers again and decide each.',
  'errors.DOCUMENT_EMPTY': 'The transcript has no text.',
  'record.acceptance': 'Decision on accepting the client',
  'record.acceptanceException': 'Exception to the acceptance',
  'record.consent': 'Consent of a participant',
  'auditAction.acceptance.accept': 'Decided to accept the client',
  'auditAction.acceptance.decline': 'Decided to decline the client',
  'auditAction.acceptance.confirm': 'Confirmed the decision on accepting the client',
  'auditAction.acceptance.withdraw': 'Withdrew the decision on accepting the client',
  'auditAction.acceptance.exception': 'Asked to start before the acceptance',
  'auditAction.acceptance.exception_confirm': 'Confirmed the start before the acceptance',
  'auditAction.acceptance.exception_withdraw': 'Withdrew the start before the acceptance',
  'auditAction.consent.record': 'Recorded a participant’s consent',
  'auditAction.consent.withdraw': 'Recorded the withdrawal of a consent',
  'auditAction.transcript.attach': 'Attached the transcript',
  'auditField.decision': 'decision',
  'auditField.checklistVersion': 'version of the checklist',
  'auditField.withdrawnBy': 'withdrawn by',
  'auditField.withdrawnAt': 'withdrawn',
  'auditField.consent': 'consent',
  'auditField.tool': 'tool',
  'auditField.noticeGivenOn': 'notice given on',
  'auditField.transcriptDocument': 'transcript',
  'auditField.transcriptTool': 'tool of the transcript',
  'auditField.transcriptRedaction': 'what was redacted',
  'auditField.transcriptAttachedBy': 'transcript attached by',
  'auditField.transcriptAttachedAt': 'transcript attached',
  'auditField.consentNoticeDocument': 'notice, as a document',
  'auditField.speakerLabel': 'speaker in the transcript',
  // The review round of wave 2 (findings on IP3a to IP4b).
  'consent.withdrawConfirm': 'Record the withdrawal? The statements drawn from the transcript stop counting as evidence. This cannot be undone.',
  'consent.withdrawHow': 'How the withdrawal came in (for example: by e-mail to the interviewer)',
  'consent.withdrawSubmit': 'Record the withdrawal',
  'documents.removePick': 'Remove',
  'inquiry.chooseLevel': 'Choose a level',
  'inquiry.chooseSpeaker': 'Choose who said it',
  'inquiry.confidentialNoTranscript': 'A confidential source’s statement is not drawn from the transcript: its quote and speaker would name the source.',
  'inquiry.noParticipantAfterTranscript': 'The transcript is attached, so no participant is added: a new person has not consented to it.',
  'regulation.form.addEvidence': 'Add',
  'transcript.chooseTool': 'Choose the tool',
  'transcript.toolFromConsent': 'The tool every participant said yes to.',
  'transcript.toolChoose': 'The participants said yes to more than one tool: every participant must have said yes to the one chosen.',
  'transcript.file': 'The transcript file (WebVTT, SubRip, text, Word or PDF)',
  'transcript.fileHint': 'The file goes to this interview only: it is stored once every participant has said yes, never before.',
  'transcript.fromFile': 'Send the file from the transcription tool',
  'transcript.heldOn': 'The day the interview was held',
  'transcript.heldOnHint': 'A transcript belongs to an interview that has been held.',
  'transcript.statement': 'What the transcript holds',
  'transcript.statementNone': 'The transcript holds no customer-level data (no data about the client’s own customers, one by one).',
  'calendar.endsExpected': '{n} legal duties of this analysis apply for the last time this day (expected, not enacted)',
  'calendar.endsExpected.one': '1 legal duty of this analysis applies for the last time this day (expected, not enacted)',
  'heat.changes.stopsExpected': '{n} expected to stop applying (not enacted)',
  'heat.changes.stopsExpected.one': '1 expected to stop applying (not enacted)',
  'heat.changes.stopsSomeExpected': '{n} stop ({e} of them expected, not enacted)',
  'affects.agreement.allUnchecked': '{service} {name}: the {of} points are not checked yet',
  'affects.review.open': 'Open the series {series}',
  'acceptance.reviewTitle': 'Review the acceptance',
  'acceptance.reviewIntro': 'A decision is in force. A new one replaces it once a second person confirms it.',
  'acceptance.declinedActive': 'The client was declined ({code}, confirmed on {date}) while its work goes on. End its services, or review the acceptance again.',
  'acceptance.declinedActiveServices': 'Go to the services',
  'mediaType.text': 'text file',
  'mediaType.csv': 'CSV file',
  'mediaType.markdown': 'Markdown file',
  'mediaType.pdf': 'PDF',
  'mediaType.xlsx': 'Excel workbook',
  'mediaType.docx': 'Word document',
  'mediaType.pptx': 'PowerPoint deck',
  'mediaType.doc': 'Word document (older format)',
  'mediaType.xls': 'Excel workbook (older format)',
  'mediaType.ppt': 'PowerPoint deck (older format)',
  'mediaType.eml': 'e-mail',
  'mediaType.vtt': 'WebVTT transcript',
  'mediaType.srt': 'SubRip transcript',
  'mediaType.image': 'image',
  'mediaType.unknown': 'file of unknown type',
  'mediaType.zip': 'zip archive',
  'mediaType.other': 'file ({type})',
  'textStatus.slides.one': 'text read: 1 slide, {notes} of it with speaker notes',
  'errors.consentTools': ' They said yes to: {tools}.',
  'errors.TRANSCRIPT_THROUGH_INQUIRY': 'A transcript is sent to its interview, where every participant’s consent is checked first; it is not uploaded as an ordinary document.',
  'errors.PARTICIPANT_AFTER_TRANSCRIPT': 'The transcript is attached already, so no participant is added: they have not consented to it.',
  'errors.TRANSCRIPT_NOT_HELD': 'Record the day the interview was held before its transcript.',
  'errors.CONFIDENTIAL_NOT_FROM_TRANSCRIPT': 'A confidential source’s statement is not drawn from a transcript: the quote and the speaker would name the source.',
  'errors.WITHDRAWAL_NOTE_NEEDED': 'Say how the withdrawal came in.',
  // IP5a: the findings, what matters most, the definition of done and the mitigation plan.
  'engine.nav.mitigation': 'What matters most and the plan',
  'plan.title': 'What matters most and the mitigation plan',
  'plan.notFrozen': 'The findings and the mitigation plan start once the analysis is frozen.',
  'plan.matters.title': 'What matters most',
  'plan.matters.p1': 'Gaps of priority P1: {n}',
  'plan.matters.breaches': 'Possible breaches of rules in force: {n} of {of} conclusions on rules in force',
  'plan.matters.escalations': 'Escalations made: {n}',
  'plan.matters.escalationSent': 'Escalation {code} · {practice} {title} · notice sent on {date}',
  'plan.matters.escalationUnsent': 'Escalation {code} · {practice} {title} · notice not yet sent',
  'plan.board.title': 'Decisions we need from the board',
  'plan.board.none': 'No gap of this analysis needs a decision from the board.',
  'plan.board.ownerRole': 'its owner role is the board',
  'plan.board.clientAct': 'the client’s own act: {acts}',
  'plan.findings.missing': '{n} gaps have no finding yet. Writing the findings writes their condition, criteria and definition of done by rule.',
  'plan.findings.missing.one': '1 gap has no finding yet. Writing the findings writes its condition, criteria and definition of done by rule.',
  'plan.findings.write': 'Write the findings',
  'plan.findings.written': 'The findings are written.',
  'plan.plan.title': 'Mitigation plan',
  'plan.plan.counts': '{gaps} gaps of the client · actions raised for {with} of {gaps}',
  'plan.wave.now': 'Now · the wave ends on {date}',
  'plan.wave.mid': 'Within six months · the wave ends on {date}',
  'plan.wave.later': 'Within eighteen months · the wave ends on {date}',
  'plan.wave.empty': 'No gap in this wave.',
  'plan.noAction': 'No action raised yet',
  'plan.raiseAction': 'Raise an action in the hand-over',
  'plan.owner': 'owner: {owner}',
  'plan.ownerClient': '{role} (client)',
  'plan.ownerNone': 'no owner named',
  'plan.due': 'due {date}',
  'plan.dueAfterWave': 'due {date}, after the wave’s end',
  'plan.quickWin': 'quick win',
  'plan.doneMeans': 'Done means',
  'plan.doneEdited': 'Changed by {name}, because: {reason}',
  'plan.doneGenerated': 'As the rule wrote it',
  'plan.proof': 'Proof',
  'plan.proofNone': 'No item of the request catalogue settles this; name the proof with the client.',
  'plan.editDone': 'Change the definition of done…',
  'plan.editDone.en': 'In English',
  'plan.editDone.sv': 'In Swedish',
  'plan.editDone.reason': 'Why it changes',
  'plan.editDone.save': 'Save the definition',
  'plan.editDone.hint': 'Give a reason of at least 3 characters, and both texts.',
  'plan.editDone.saved': 'The definition of done is changed.',
  'plan.editDone.keepsRule': 'The text the rule wrote, and the proof, are kept beside your words.',
  'plan.cancel': 'Cancel',
  'plan.findings.title': 'Findings',
  'plan.finding.line': 'Priority {priority} · level {level} of target {target}',
  'plan.finding.pending': 'The finding of this gap is not written yet.',
  'plan.byRule': 'Condition, criteria and the definition of done are written by rule from the frozen analysis and the library, and never edited.',
  'plan.condition': 'Condition',
  'plan.criteria': 'Criteria',
  'plan.cause': 'Cause',
  'plan.effect': 'Effect',
  'plan.recommendation': 'Recommendation',
  'plan.notWritten': 'Not written yet.',
  'plan.writtenBy': 'written by {name}',
  'plan.prose.edit': 'Write cause, effect and recommendation…',
  'plan.prose.save': 'Save',
  'plan.prose.saved': 'Saved.',
  'plan.prose.rootCause': 'A root cause is a person’s judgement, or agreed at the validation meeting; never a model’s.',
  'plan.ownerRole': 'Owner role',
  'plan.ownerRole.none': 'not named',
  'plan.ownerRole.proposed': 'proposed by rule',
  'plan.ownerRole.set': 'changed by {name}, because: {reason}',
  'plan.ownerRole.change': 'Change the owner role…',
  'plan.ownerRole.choose': 'Choose a role',
  'plan.ownerRole.reason': 'Why it changes',
  'plan.ownerRole.save': 'Save the owner role',
  'plan.ownerRole.hint': 'Choose another role, and give a reason of at least 3 characters.',
  'plan.ownerRole.saved': 'The owner role is changed.',
  'plan.capOnly.title': 'Level 4 needs an independent assessment',
  'plan.capOnly.intro': 'The firm performs these practices, so their level is assessed by the provider and stops at level 3. They are no gap of the client’s own.',
  'plan.prepare.title': 'The mitigation plan as a document',
  'plan.prepare.intro': 'The plan is rendered from the records as a Word file and a PDF: the findings, the actions as they stand and what matters most. Nothing in it is written by an AI model. It reaches the client only through its checkpoint.',
  'plan.prepare': 'Prepare the plan',
  'plan.prepared': 'The plan is prepared as a draft.',
  'plan.listTitle': 'Plans',
  'plan.none': 'No plan has been prepared yet.',
  'plan.standing': 'Can the plan be published?',
  'plan.internalWhy': 'Built on content not yet adopted by the regulatory desk: this plan can be used inside the firm but cannot be published.',
  'plan.publishable': 'Every library file the plan rests on is adopted by the regulatory desk: a plan can be published once a reviewer has approved it.',
  'plan.renderExpires': 'Rendering it again returns the plan to draft and expires its checkpoint.',
  'plan.whyNotPublish': 'You prepared this plan or asked for its review, so another reviewer publishes it.',
  'ownerRole.board': 'the board (the management body)',
  'ownerRole.ceo': 'the CEO',
  'ownerRole.aml_compliance_manager': 'the AML compliance manager',
  'ownerRole.appointed_aml_officer': 'the AML compliance officer',
  'ownerRole.compliance_officer': 'the head of compliance',
  'ownerRole.risk_officer': 'the risk officer',
  'ownerRole.internal_audit_head': 'the head of internal audit',
  'ownerRole.cfo': 'the CFO',
  'ownerRole.dpo': 'the data protection officer',
  'ownerRole.security_officer': 'the information security officer',
  'ownerRole.data_owner': 'the data owner',
  'ownerRole.firm': 'the firm',
  'ownerRole.other': 'another role at the client',
  'rootCauseSource.person': 'a person',
  'rootCauseSource.validation_meeting': 'the validation meeting',
  'auditField.doneEditedBy': 'definition of done changed by',
  'auditField.titleBy': 'title changed by',
  'auditField.doneEditedAt': 'definition of done changed',
  'auditField.proofItems': 'proof',
  'auditField.ownerRoleProposed': 'owner role proposed by rule',
  'auditField.ownerRole': 'owner role',
  'auditField.ownerRoleSetBy': 'owner role changed by',
  'auditField.ownerRoleSetAt': 'owner role changed',
  'auditField.causeBy': 'cause written by',
  'auditField.causeAt': 'cause written',
  'auditField.rootCauseSource': 'root cause from',
  'auditField.effectBy': 'effect written by',
  'auditField.effectAt': 'effect written',
  'auditField.recommendationBy': 'recommendation written by',
  'auditField.recommendationAt': 'recommendation written',
  'auditField.writtenBy': 'written by rule, at the act of',
  'auditField.writtenAt': 'written',
  'auditAction.plan.write_findings': 'Wrote the findings of the gaps by rule',
  'auditAction.plan.finding_prose': 'Wrote a finding’s cause, effect or recommendation',
  'auditAction.plan.owner_role': 'Changed a finding’s owner role',
  'auditAction.plan.edit_done': 'Changed an action’s definition of done',
  'auditAction.plan.prepare': 'Prepared a mitigation plan',
  'record.finding': 'Finding',
  'library.file.finding': 'Rule for findings and the definition of done',
  'errors.PLAN_NOT_FROZEN': 'The findings and the mitigation plan start once the analysis is frozen.',
  'errors.FINDING_NOT_FOR_GAP': 'A finding is written for a gap of a frozen analysis.',
  'errors.FINDING_WRITTEN_BY_RULE': 'A finding’s condition, criteria, definition of done and proof are written by rule and never change.',
  'errors.OWNER_ROLE_REASON_NEEDED': 'Say why the owner role changes.',
  'errors.ROOT_CAUSE_NEEDS_MEETING': 'A root cause from the validation meeting is recorded with the meeting, which comes later.',
  'errors.ACTION_NEEDS_FINDING': 'Write the gap’s finding first: an action from a gap carries its definition of done.',
  'errors.DONE_ONLY_FROM_GAP': 'Only an action raised from a gap has a definition of done and proof.',
  'errors.DONE_REASON_NEEDED': 'Say why the definition of done changes.',
  'engine.nav.packs': 'Meeting and board pack',
  'packs.title': 'The validation meeting and the board pack',
  'packs.notFrozen': 'The validation meeting and the packs start once the analysis is frozen.',
  'packs.standing': 'Can the packs be published?',
  'packs.internalWhy': 'Built on content not yet adopted by the regulatory desk: the packs can be used inside the firm but cannot be published.',
  'packs.publishable': 'Every library file the packs rest on is adopted by the regulatory desk: a pack can be published once a reviewer has approved it at its checkpoint.',
  'packs.renderExpires': 'Rendering it again returns the pack to draft and expires its checkpoint.',
  'packs.whyNotPublish': 'You prepared this pack or asked for its review, so another reviewer publishes it.',
  'packs.language': 'Language',
  'packs.validation.title': 'The validation pack',
  'packs.validation.intro': 'The draft findings, the gaps and the evidence each rests on, shared with the client before the report is final, so that nothing in it comes as a surprise.',
  'packs.validation.prepare': 'Prepare the validation pack',
  'packs.validation.prepared': 'The validation pack is prepared as a draft.',
  'packs.validation.none': 'No validation pack has been prepared yet.',
  'packs.log.title': 'The meeting’s log: no surprises',
  'packs.log.intro': 'Each comment is recorded with the speaker’s role, never a name. A comment that corrects a fact becomes a proposed fact that another member of the firm decides; the frozen analysis never changes, and an accepted correction is carried into the re-assessment.',
  'packs.log.none': 'No comment has been recorded yet.',
  'packs.log.counts': 'Comments: {n} · factual corrections: {fc}, of which {open} to decide · root causes: {rc} · disputed: {disputed}',
  'packs.log.line': '{code} · {practice} {title} · {kind} · {role}, {date}',
  'packs.log.response': 'Our response: {text}',
  'packs.log.proposed': 'Proposed fact: {fact} is “{value}”',
  'packs.log.decided': '{state} by {name}: {reason}',
  'packs.log.recordedBy': 'Recorded by {name}',
  'packs.log.accept': 'Accept the proposed fact',
  'packs.log.decline': 'Decline the proposed fact',
  'packs.log.reason': 'Why',
  'packs.log.acceptSubmit': 'Accept',
  'packs.log.declineSubmit': 'Decline',
  'packs.log.decidedDone': 'The proposed fact is decided.',
  'packs.log.notYours': 'You recorded this comment, so another member decides its proposed fact.',
  'packs.log.rootCause': 'Make it the gap’s root cause',
  'packs.log.rootCauseDone': 'The root cause is recorded on the gap’s finding.',
  'packs.log.isRootCause': 'This is the gap’s root cause.',
  'packs.log.needsFinding': 'Write the gap’s finding first, on the page of the mitigation plan.',
  'proposalStatus.proposed': 'To be decided',
  'proposalStatus.accepted': 'Accepted for the re-assessment',
  'proposalStatus.declined': 'Declined',
  'commentKind.factual_correction': 'Factual correction',
  'commentKind.root_cause': 'Root cause',
  'commentKind.management_response': 'Management response',
  'commentKind.disputed': 'Disputed',
  'packs.record.title': 'Record a comment from the meeting',
  'packs.record.needsPack': 'Prepare a validation pack first: the meeting is held on it.',
  'packs.record.pack': 'Pack discussed',
  'packs.record.heldOn': 'Meeting held on',
  'packs.record.gap': 'Gap',
  'packs.record.role': 'Speaker’s role (never a name)',
  'packs.record.kind': 'Kind of comment',
  'packs.record.comment': 'What was said',
  'packs.record.response': 'Our response (optional)',
  'packs.record.fact': 'Fact it corrects',
  'packs.record.value': 'Proposed value',
  'packs.record.document': 'Document that shows it (optional)',
  'packs.record.choose': 'Choose…',
  'packs.record.noDocument': 'No document',
  'packs.record.hint': 'Give the speaker’s role, the comment and, for a factual correction, the fact and its value.',
  'packs.record.save': 'Record the comment',
  'packs.record.done': 'The comment is recorded.',
  'packs.board.title': 'The board pack',
  'packs.board.intro': 'A body of at most two pages for the client’s board, with the annex after it. The figures are written by code; the introduction and the observations are a person’s, and the prose check reads them before approval.',
  'packs.board.introduction': 'Introduction',
  'packs.board.observations': 'Observations',
  'packs.board.prepare': 'Prepare the board pack',
  'packs.board.prepared': 'The board pack is prepared as a draft.',
  'packs.board.none': 'No board pack has been prepared yet.',
  'packs.board.editProse': 'Edit the introduction and observations',
  'packs.board.saveProse': 'Save and render again',
  'packs.board.proseSaved': 'Saved; the pack is rendered again as a draft.',
  'packs.board.proseBy': 'Introduction and observations written by {name}',
  'prose.title': 'Prose check',
  'prose.summary': 'Statements about the record read: {statements} · {flags}, {open}',
  'prose.flags.one': '{n} flag',
  'prose.flags': '{n} flags',
  'prose.openCount.one': '{n} open',
  'prose.openCount': '{n} open',
  'prose.nothingToCheck': 'No person-written text to check.',
  'prose.clean': 'The prose agrees with the record: no flag.',
  'prose.nothingRead': 'The check read no statement about the record in the prose.',
  'prose.check.consistency': 'A figure differs from the record',
  'prose.check.verdict': 'A verdict with no matching conclusion',
  'prose.source.introduction': 'Introduction',
  'prose.source.observations': 'Observations',
  'prose.source.cause': 'Cause, {code}',
  'prose.source.effect': 'Effect, {code}',
  'prose.source.recommendation': 'Recommendation, {code}',
  'prose.source.title': 'Action title, {code}',
  'prose.source.done': 'Definition of done, {code}',
  'prose.source.comment': 'Meeting comment, {code}',
  'prose.source.response': 'Response to {code}',
  'prose.says': 'The text says {stated}; the record holds {register}.',
  'prose.value.met': 'met',
  'prose.value.partly_met': 'partly met',
  'prose.value.not_met': 'not met',
  'prose.value.none': 'no conclusion decided and confirmed by people',
  'prose.value.level': 'level {n}',
  'prose.answered': 'Stands, as {name} recorded: {answer}',
  'prose.answer': 'Record why it stands',
  'prose.answerReason': 'Why the sentence stands',
  'prose.answerSubmit': 'Record',
  'prose.answerDone': 'The answer is recorded.',
  'prose.open': 'Approval waits until every flag is corrected in the text, which is then rendered again, or answered by the reviewer with a reason.',
  'errors.PROSE_FLAGS_OPEN': 'A flag of the prose check is open: correct the text, or record why it stands.',
  'errors.PROSE_NOT_CHECKED': 'The prose of this rendering was not checked; render it again.',
  'errors.FLAG_ANSWERED': 'This flag has its answer already.',
  'errors.FLAG_NOT_AT_CHECKPOINT': 'A flag is answered while its rendering waits at its checkpoint.',
  'errors.FOUR_EYES_FLAG': 'A flag is answered by a reviewer who neither prepared, rendered nor asked for review.',
  'errors.FOUR_EYES_FLAG_AUTHOR': 'You wrote the flagged sentence, so another reviewer records why it stands.',
  'errors.FOUR_EYES_PROSE_AUTHOR': 'You wrote text this rendering prints, so another reviewer approves it.',
  'errors.DONE_AFTER_REMEDIATION': 'The action is remediated, so its definition of done no longer changes.',
  'errors.FOUR_EYES_DONE': 'You changed this action’s definition of done, so another reviewer verifies it.',
  'errors.FLAG_REASON_NEEDED': 'Say why the sentence stands.',
  'errors.COMMENT_NEEDS_VALIDATION_PACK': 'A comment of the validation meeting is recorded on a validation pack of this analysis.',
  'errors.MEETING_DAY': 'The meeting is held after its pack was prepared, and no later than today.',
  'errors.COMMENT_NOT_GAP': 'A comment names a gap of this analysis.',
  'errors.COMMENT_DOCUMENT': 'A proposed fact names a document of this client that is not retired.',
  'errors.COMMENT_PROPOSED_FACT': 'A factual correction names the fact and its value; another comment names none.',
  'errors.PROPOSAL_DECIDED': 'A comment is never edited; a proposed fact in it is decided once.',
  'errors.PROPOSAL_REASON_NEEDED': 'Say why the proposed fact is accepted or declined.',
  'errors.FOUR_EYES_PROPOSAL': 'You recorded this comment, so another member decides its proposed fact.',
  'errors.ROOT_CAUSE_NOT_FROM_MEETING': 'Only a root-cause comment recorded in the meeting on this gap becomes its root cause.',
  'errors.ROOT_CAUSE_NEEDS_FINDING': 'Write the gap’s finding first; the root cause is recorded on it.',
  'errors.BOARD_PACK_TOO_LONG': 'The board pack’s body does not fit on two pages; shorten the introduction or the observations.',
  'errors.PROSE_BOARD_PACK_ONLY': 'Only a board pack carries an introduction and observations.',
  'auditField.proseBy': 'Introduction and observations written by',
  'auditField.proseAt': 'Introduction and observations written at',
  'auditField.packDeliverable': 'Pack discussed',
  'auditField.commentKind': 'Kind of comment',
  'auditField.proposalStatus': 'Proposed fact',
  'auditField.proseTexts': 'Pieces of prose read',
  'auditField.proseStatements': 'Statements about the record read',
  'auditField.checkedBy': 'Checked by',
  'auditField.flagSeq': 'Flag',
  'auditField.flagSource': 'Where the sentence stands',
  'auditField.checkKind': 'Kind of flag',
  'auditField.factId': 'What the record holds',
  'auditField.stated': 'What the text says',
  'auditField.register': 'In the record',
  'auditField.answeredBy': 'Answered by',
  'auditField.answeredAt': 'Answered at',
  'auditField.rootCauseComment': 'Root cause from the meeting',
  'record.validationComment': 'Comment of the validation meeting',
  'record.proseCheck': 'Prose check',
  'record.proseFlag': 'Flag of the prose check',
  'library.file.verdictWords': 'The words of the prose check (verdicts and counts)',
  'auditAction.pack.prepare_validation': 'Prepared a validation pack',
  'auditAction.pack.prepare_board': 'Prepared a board pack',
  'auditAction.pack.prose': 'Changed a board pack’s introduction or observations',
  'auditAction.pack.answer_flag': 'Recorded why a flagged sentence stands',
  'auditAction.pack.record_comment': 'Recorded a comment of the validation meeting',
  'auditAction.pack.decide_proposal': 'Decided a proposed fact',
  'auditAction.pack.root_cause': 'Took the meeting’s root cause onto a finding',
  // IP6: the inbox and the drift rules.
  'auditAction.job.drift.check': 'Checked what needs attention',
  'auditAction.drift.acknowledge': 'Acknowledged a condition in the inbox',
  'auditField.driftRule': 'condition',
  'auditField.raisedAt': 'found at',
  'auditField.resolvedAt': 'cleared at',
  'auditField.acknowledgedAt': 'acknowledged at',
  'nav.inbox': 'Inbox, all clients',
  'nav.inbox.answers': 'What needs attention in my clients?',
  'inbox.intro': 'What the daily check found in your clients. Each line stays here until its condition clears; acknowledging it says you have seen it and closes nothing.',
  'inbox.none': 'Nothing needs attention in any of your clients.',
  'inbox.counts': 'Counted: the conditions the daily check finds open in clients you are a member of: a baseline not frozen 90 days after a second-line service started, a start check past its due date, an escalation notice not sent one business day after it opened, a model to validate, a frozen analysis that no longer recomputes the same, and an acceptance to review.',
  'inbox.showing': 'showing {n} of {total}',
  'inbox.rule.baseline_due': 'Baseline due',
  'inbox.rule.mandate_check_open': 'Start check open',
  'inbox.rule.escalation_notice_unsent': 'Notice not sent',
  'inbox.rule.model_validation_due': 'Model validation due',
  'inbox.rule.baseline_recompute_mismatch': 'Analysis does not recompute',
  'inbox.rule.acceptance_review_due': 'Acceptance review due',
  'inbox.open': 'Open',
  'inbox.raised': 'found {date}',
  'inbox.acknowledge': 'Acknowledge',
  'inbox.acknowledged': 'seen by {name}, {date}',
  'inbox.acknowledgedNote': 'Acknowledged. It stays here until the condition clears.',
  'inbox.client': '{name}: {n} open',
  'portfolio.q1.firm': 'The firm',
  'errors.DRIFT_ACKNOWLEDGED': 'This is acknowledged already.',
  'errors.DRIFT_RESOLVED': 'This has cleared; there is nothing to acknowledge.',
  // IP7: re-assessment, carry-forward, before and after, H5 and the what-if.
  'nav.progress': 'Progress',
  'nav.progress.answers': 'How has the client moved since the baseline?',
  'nav.portfolioHeat': 'Portfolio map',
  'nav.portfolioHeat.answers': 'Where are my clients below target?',
  'progress.beforeAfter': 'Before and after',
  'progress.none': 'No frozen assessment yet: progress is read from frozen assessments only.',
  'progress.grid': 'Capabilities before and after',
  'progress.capability': 'Capability',
  'progress.target': 'Target',
  'progress.level': 'level {n}',
  'progress.noTarget': 'no target',
  'progress.partial': 'partial re-assessment: {n} practices',
  'progress.partial.one': 'partial re-assessment: {n} practice',
  'progress.band.a': 'at or above target',
  'progress.band.b': '1 level below target',
  'progress.band.c': '2 levels below target',
  'progress.band.d': '3 or more levels below target',
  'progress.band.nd': 'not determined: evidence to obtain',
  'progress.band.none': 'not assessed',
  'progress.band.na': 'not applicable',
  'progress.change.improved': 'improved',
  'progress.change.worsened': 'worsened',
  'progress.change.unchanged': 'unchanged',
  'progress.change.newly_in_scope': 'newly in scope',
  'progress.change.left_scope': 'left scope',
  'progress.change.not_comparable': 'not comparable',
  'progress.change.undetermined': 'cannot compare: not determined in one',
  'progress.change.not_reassessed': 'not re-assessed: baseline value',
  'progress.state.assessed': 'assessed',
  'progress.state.not_determined': 'not determined',
  'progress.state.not_assessed': 'not assessed',
  'progress.state.not_applicable': 'not applicable',
  'progress.notComparableMeans': 'Not comparable: a practice of the capability changed what decides its level (its gates, facts, elements or clauses) in a new map version that does not say which practice it replaces. A new label or translation keeps a practice comparable.',
  'progress.counts': 'Each cell is that assessment’s band against the target; the change is against the column before it. Counted: the capabilities of the client’s frozen assessments, in date order.',
  'progress.flags': 'Lapsed since the freeze',
  'progress.flag.lapsed': 'Evidence lapsed',
  'progress.flag.line': '{practice}: {evidence} was valid until {date}. The level frozen in {assessment} stays as it was; the next re-assessment re-tests it.',
  'progress.openIt': 'Open the assessment',
  'progress.followsLine': 'follows {code}',
  'progress.notComparableList': 'Not comparable with the assessment it follows: {list}',
  'progress.carry': 'Carry forward',
  'progress.noValue': 'no value',
  'progress.retest': 'to re-test: {reason}',
  'progress.carryNote': 'A carried fact is offered, not recorded: record it in the new assessment, under the same evidence rules.',
  'progress.startTitle': 'Start a re-assessment',
  'progress.follows': 'From the frozen assessment',
  'progress.kind': 'Kind',
  'progress.kind.reassessment': 'Yearly: the whole scope',
  'progress.kind.triggered': 'Triggered: chosen practices only',
  'progress.choose': 'The practices to re-assess',
  'progress.reason': 'Why now',
  'progress.start': 'Start the re-assessment',
  'progress.started': 'Started. What can be carried forward and what must be re-tested is listed above.',
  'progress.whatIf': 'What if',
  'progress.whatIf.intro': 'Choose a fact and a value to see what the rule would compute. This is a scenario: nothing is recorded, nothing is frozen.',
  'progress.whatIf.on': 'On the assessment',
  'progress.whatIf.run': 'Compute the scenario',
  'progress.whatIf.label': 'Scenario: not a fact, never frozen',
  'progress.whatIf.never': 'Computed on a copy of the assessment’s facts with your change. Nothing was stored.',
  'progress.whatIf.recorded': 'As recorded',
  'progress.whatIf.scenario': 'In the scenario',
  'carry.disposition.carried': 'carried forward',
  'carry.disposition.retest': 'to re-test',
  'carry.reason.corrected_at_validation': 'corrected at the validation meeting',
  'carry.reason.never_tested': 'never tested',
  'carry.reason.remediation_planned': 'remediation raised on it',
  'carry.reason.no_evidence': 'no evidence',
  'carry.reason.evidence_unverified': 'evidence not verified',
  'carry.reason.evidence_lapsed': 'evidence lapsed',
  'h5.intro': 'For planning only: one row per client you are a member of, from its last whole frozen assessment. Never shown to a client or put in a deliverable.',
  'h5.none': 'None of your clients has a frozen assessment yet.',
  'h5.client': 'Client',
  'h5.asOf': '{code}, as of {date}',
  'h5.cell': '{k} of {n} capabilities below target',
  'h5.notDetermined': '{n} not determined',
  'h5.notAssessed': 'not assessed',
  'h5.counts': 'Counted: the capabilities assessed or not determined in each client’s last whole frozen assessment; below target means a gap above zero. Counts only, never an average, and only for clients you are a member of.',
  'auditField.follows': 'follows',
  'auditField.chosenPractices': 'chosen practices',
  'auditField.triggerReason': 'why now',
  'auditField.retestReason': 'reason to re-test',
  'auditField.evidenceCodes': 'evidence',
  'auditField.correction': 'correction',
  'record.carryForward': 'Carry-forward',
  'auditAction.assessment.reassess': 'Started a re-assessment',
  'errors.REASSESS_NOT_FROZEN': 'A re-assessment starts from a frozen assessment.',
  'errors.REASSESS_PRACTICE_UNKNOWN': 'A triggered re-assessment chooses practices of the assessment it follows: {practices}.',
  'errors.WHAT_IF_FACT_UNKNOWN': 'That fact is not one a person records.',
  'errors.WHAT_IF_EVIDENCE_UNKNOWN': 'That evidence item is not in this assessment.',
  'inbox.rule.proof_overdue': 'Proof overdue',
  'inbox.rule.reported_done_unchecked': 'Reported done, proof not asked for',
  'record.remediationPack': 'Remediation pack',
  'auditField.reportedDoneOn': 'reported done by the client on',
  'auditField.reportedDocument': 'what the client sent',
  'auditField.reportedRecordedBy': 'client’s report recorded by',
  'auditField.reportedAt': 'client’s report recorded on',
  'auditField.remediationPack': 'remediation pack',
  'auditField.proofRequest': 'proof request',
  'auditAction.followup.report_done': 'Recorded that the client reports an action done',
  'auditAction.followup.report_pack': 'Recorded the client’s remediation pack as done for the chosen actions',
  'auditAction.followup.reassess_verified': 'Started a re-assessment of the practices whose actions are all verified',
  'auditAction.followup.prepare_report': 'Prepared a follow-up report',
  'errors.ACTION_NOT_REPORTABLE': 'Only an open action, or one in progress, is reported done by the client.',
  'errors.NO_VERIFIED_PRACTICES': 'No practice of this analysis has all its actions verified yet.',
  'errors.FOLLOWUP_NEEDS_REASSESSMENT': 'A follow-up report is made from a frozen re-assessment and the frozen assessment it follows.',
  'errors.REPORTED_ON_FUTURE': 'The client reports an action done on a day that has come.',
  'errors.PACK_REPORTED_ON_FUTURE': 'The client reports a pack done on a day that has come.',
  'errors.PACK_DOCUMENT_NOT_LIVE': 'The remediation pack is a document of this client that is not retired.',
  'errors.REPORTED_DOCUMENT_NOT_LIVE': 'What the client sent is a document of this client that is not retired.',
  'followup.reportDone': 'The client reports this done',
  'followup.reportedNote': 'Recorded: the client reports this done. Its proof request is drafted; send it when ready.',
  'followup.reportedExplain': 'The client says this is done. Nothing moves until the proof is checked: record the remediation once the proof is in, and a reviewer who did not record it verifies it.',
  'followup.title': 'The client says it is done',
  'followup.line': 'Reported done by the client {reported_done} · proof requested {proof_requested} · proof received {proof_received} · verified {verified} · proof overdue {proof_overdue}',
  'followup.intro': 'Counted over the actions raised from this analysis’s gaps. A report from the client changes no level, verification or wave: only a verified action and the next re-assessment do.',
  'followup.none': 'No action has been raised from this analysis’s gaps yet.',
  'followup.col.reported': 'Reported done by the client',
  'followup.col.proof': 'Proof',
  'followup.state.verified': 'verified',
  'followup.state.overdue': 'proof overdue',
  'followup.state.received': 'proof received',
  'followup.state.requested': 'proof requested',
  'followup.state.drafted': 'proof request drafted, not sent',
  'followup.state.reported': 'reported done, no proof items',
  'followup.state.notReported': 'not reported done',
  'followup.packTitle': 'The client reports a pack done',
  'followup.packIntro': 'Upload the client’s remediation pack once (Documents), then choose the actions it covers. Each takes the pack and the day in one act, and each with proof items gets its proof request as a draft.',
  'followup.packDocument': 'The remediation pack',
  'followup.chooseDocument': 'Choose the document',
  'followup.reportedOn': 'Reported done on',
  'followup.packActions': 'The actions the pack covers',
  'followup.packSubmit': 'Record the pack as done for {n} actions',
  'followup.packDone': 'Recorded: {n} actions reported done with the pack.',
  'followup.reassessTitle': 'Every action on {n} practices is verified',
  'followup.reassessSubmit': 'Start a re-assessment of the {n} practices whose actions are all verified',
  'followup.standing': 'Can the follow-up report be published?',
  'followup.internalWhy': 'Built on content not yet adopted by the regulatory desk: this report can be used inside the firm but cannot be published.',
  'followup.publishable': 'Every library file both assessments rest on is adopted by the regulatory desk: the report can be published once a reviewer has approved it.',
  'followup.renderExpires': 'Rendering it again returns the report to draft and expires its checkpoint.',
  'followup.whyNotPublish': 'You prepared this report or asked for its review, so another reviewer publishes it.',
  'followup.reportsTitle': 'Follow-up reports of {code}',
  'followup.reportsIntro': 'Rendered from this re-assessment’s snapshot and the one it follows: the roadmap’s actions and the before and after, every figure with its denominator. No model writes any of it.',
  'followup.prepareEn': 'Prepare in English',
  'followup.prepareSv': 'Prepare in Swedish',
  'followup.prepared': 'The follow-up report is prepared as a draft.',
  'followup.noReports': 'No follow-up report yet.',
  // IP9a: "What went in", the preview, the prompt library and the synthetic class.
  'explain.whatWentIn': "What went in",
  'explain.howComputed': "How was this computed?",
  'explain.howComputedText': "The prompt is rebuilt from the record of the call ({version}): each template from the archive by its SHA-256, each client record at the version and the characters the call used, fenced again with a placeholder for the fence nonces, which are never stored. \"Equals\" is said only when both hashes match what was recorded when the call was sent.",
  'explain.check.matches': "Rebuilt prompt equals what was sent",
  'explain.check.differs': "Cannot show exactly what was sent",
  'explain.check.cannot_rebuild': "Cannot be fully rebuilt: {n} part erased",
  'explain.check.not_recorded': "What went in was not recorded for this call",
  'explain.chars': "{n} characters",
  'explain.notRecorded.archive_refused': "The archive refused a template of this call because it equals a text of the client, so its parts could not be recorded. The call itself is recorded.",
  'explain.notRecorded.error': "The parts of this call could not be recorded because of an error. The call itself is recorded.",
  'explain.notRecorded.none': "It was made before Olivia kept this record, or it was refused before a prompt was put together.",
  'explain.problem.archive_missing': "A template is missing from the archive.",
  'explain.problem.archive_altered': "A template in the archive no longer matches its checksum.",
  'explain.problem.document_unavailable': "A document the call used is not available to you, or is gone.",
  'explain.problem.document_changed': "A document no longer holds the text the call used.",
  'explain.problem.record_unavailable': "A record the call used is not available to you, or is gone.",
  'explain.problem.record_changed': "A record no longer holds the text the call used.",
  'explain.problem.erased': "A part the call used was erased, so it is not shown.",
  'explain.part.date_block': "Today's date",
  'explain.part.fence_rules': "How to read fenced text",
  'explain.part.foundation': "Foundation",
  'explain.part.task': "Task",
  'explain.part.output_rules': "Output rules",
  'explain.part.practice': "The practice",
  'explain.part.instructions': "Closing instruction",
  'explain.part.nudge': "Reply again as JSON",
  'explain.part.document_text': "Evidence (fenced as data)",
  'explain.part.proposal_request': "A member's request (fenced as data)",
  'explain.part.module_config': "The module's inputs",
  'explain.range': "characters {from} to {to}",
  'explain.fencedAsData': "Sent between fences, as data; shown here by reference only.",
  'explain.newer': "A newer version exists; this run used v{used}.",
  'explain.newerBy': "(The newest by {name}, {date}.)",
  'explain.erased': "Erased; not shown.",
  'explain.subtitle': "{purpose} · {date} · started by {name}",
  'explain.engine': "Engine",
  'explain.engine.anthropic': "Anthropic API",
  'explain.engine.openrouter': "OpenRouter (the provider fixed in code, zero data retention)",
  'explain.asked': "asked {model}",
  'explain.served': "served {model}",
  'explain.notServed': "nothing was served",
  'explain.effort': "effort {effort}",
  'explain.dataClass': "Data class",
  'explain.class.public': "public",
  'explain.class.synthetic': "synthetic",
  'explain.class.client': "client: Claude only",
  'explain.class.customer': "customer level",
  'explain.status': "What happened",
  'explain.status.sent': "sent",
  'explain.status.refused': "refused",
  'explain.status.failed': "failed",
  'explain.refused': "refused before anything was sent ({code}); this is what would have been sent",
  'explain.cost': "Cost",
  'explain.template': "Task template",
  'explain.notAdopted': "Not adopted by two people of the firm when it ran: {list}. Allowed only because the data is synthetic.",
  'explain.layers': "The layers, in the order sent",
  'explain.col.layer': "Layer",
  'explain.col.source': "Source and version",
  'explain.col.hash': "Hash",
  'explain.col.size': "Size",
  'explain.packs': "Knowledge packs",
  'explain.noPack': "No knowledge pack used",
  'explain.injection': "Injection lint",
  'explain.injection.none': "nothing found in the fenced parts",
  'explain.injection.found': "{source} line {line}: {rule}",
  'explain.injectionRule.ignore_instructions': "asks the model to ignore its instructions",
  'explain.injectionRule.role_change': "tries to give the model another role",
  'explain.injectionRule.system_prompt': "mentions the system prompt",
  'explain.injectionRule.forged_fence': "holds a fence marker, which was removed",
  'explain.outcome': "What people did with it",
  'explain.outcome.none': "No proposal is made from a call yet (that comes with the evidence inbox).",
  'explain.replayNot': "Replay: not offered",
  'explain.showSystem': "Show the system prompt",
  'explain.showFull': "Show the full prompt as sent",
  'synthetic.label': "synthetic",
  'synthetic.labelGlm': "synthetic, GLM",
  'synthetic.title': "Synthetic data",
  'synthetic.state.declared': "declared synthetic: waiting for a second person",
  'synthetic.state.in_force': "synthetic: confirmed",
  'synthetic.state.ended': "synthetic: ended; AI for this client is Claude only",
  'synthetic.routeOff': "This installation does not run the synthetic route (OLIVIA_SYNTHETIC_WORKSPACES is off), so this client is handled as a real one.",
  'synthetic.explain': "Declared at creation, with the attestation. Once a second person confirms it, AI steps may run on the pinned OpenRouter models (Mistral Large 4, GLM or Kimi). A document in which personal identifiers are found is never sent to them. Switching it off sends every later AI step to Claude only, and it cannot be switched on again.",
  'synthetic.otherConfirms': "You declared it, so another working member of this client confirms it.",
  'synthetic.confirm': "Confirm: this client holds only synthetic or test data",
  'synthetic.declare': "Declare it synthetic (only now, when it is created)",
  'synthetic.declareNote': "A second working member confirms it. It cannot be declared later. \"Declared synthetic\" rests on your word: never put real client data in it.",
  'synthetic.attestation': "This client is fictional. Its workspace holds only synthetic or test data, nothing about a real customer or a real person.",
  'aiRun.subtitle': "Every model call made for this client, each with what went in. Runs start with the evidence inbox; here you can preview what would be sent.",
  'aiRun.calls': "Model calls",
  'aiRun.noCalls': "No model call has been made for this client. Counted: every call, sent or refused.",
  'aiRun.col.code': "Call",
  'aiRun.col.when': "When",
  'aiRun.col.purpose': "Task",
  'aiRun.col.status': "What happened",
  'aiRun.preview': "Preview what would be sent",
  'aiRun.task': "Task",
  'aiRun.taskName.b3.fact': "Fact proposal",
  'aiRun.taskName.b3.proof_match': "Proof matching",
  'aiRun.taskName.b3.evidence_map': "Evidence map",
  'aiRun.practice': "Practice",
  'aiRun.practiceHint': "A practice code, for example AML.A2.1",
  'aiRun.documents': "Documents",
  'aiRun.documentsHint': "Document codes, for example DOC-001, DOC-002",
  'aiRun.engine': "Engine",
  'aiRun.language': "Answer in",
  'aiRun.language.en': "English",
  'aiRun.language.sv': "Swedish",
  'aiRun.nothingSent': "Nothing was sent: this is the prompt as it would be composed now, with the fence nonces as placeholders.",
  'aiRun.worstCase': "Worst-case cost",
  'aiRun.wouldRun': "If it ran now",
  'aiRun.wouldRefuse': "it would be refused ({code})",
  'aiRun.wouldGo': "it would be sent",
  'prompts.title': "Prompts",
  'prompts.counts': "{n} templates · {adopted} adopted · {waiting} waiting",
  'prompts.adopted': "adopted",
  'prompts.notAdopted': "not adopted",
  'prompts.layers': "Layers: {list}",
  'prompts.note': "Every prompt is a file in the repository, locked by its SHA-256. A real client’s call uses a template only after one desk member has checked it and a second has adopted that exact version; on synthetic data an unadopted template only labels the output. Nobody edits a prompt on screen.",
  'prompts.drafts.title': 'Drafts, not sent',
  'prompts.drafts.note': 'A draft is a newer version kept beside the one in use. It is checked and adopted like any template, but no call sends it until a reviewed change of the library offers it in place of the version it replaces.',
  'prompts.draft': 'draft, not sent',
  'prompts.replaces': 'Would replace {path}',
  'library.tabs': "Library sections",
  'library.tab.library': "Templates and rules",
  'library.tab.prompts': "Prompts",
  'errors.SYNTHETIC_ATTESTATION': "A client is declared synthetic only when it is fictional, with the agreed attestation, word for word.",
  'errors.SYNTHETIC_SET_AT_CREATION': "A client is declared fictional only when it is created; it cannot be switched on later.",
  'errors.SYNTHETIC_STATE': "This synthetic declaration is confirmed already, or has ended.",
  'errors.ALLOWANCE_FOUR_EYES': 'You decided this allowance, so a second member of the client confirms it.',
  'errors.ALLOWANCE_NOTHING_TO_CONFIRM': 'AI is off for this client, so there is nothing to confirm.',
  'errors.SYNTHETIC_FOUR_EYES': "You declared this client fictional, so a second person confirms it.",
  'errors.AI_DOCUMENT_NO_TEXT': "This document has no extracted text to send.",
  'auditAction.proposal_request.add': "Added a request for an AI task",
  'auditAction.proposal_request.erase': "Erased a request for an AI task",
  'auditAction.prompt_text.archive': "Archived a prompt template",
  'auditAction.workspace.synthetic.confirm': "Confirmed that the client holds only synthetic data",
  'record.proposalRequest': "Request for an AI task",
  'record.moduleConfig': "Module inputs",
  'record.modelCallInput': "Part of a model call",
  'record.identifierCheck': "Identifier check",
  'auditField.version': "version",
  'auditField.erasedAt': "erased",
  'auditField.erasedBy': "erased by",
  'auditField.module': "module",
  'auditField.template': "prompt template",
  'auditField.modelServed': "model served",
  // IP10a: the modules taken from ANTON, the catalogue, a module's page, a run's draft, the library's Modules tab.
  'nav.modules': "Modules",
  'nav.modules.answers': "Which modules can help with this client, and what did they draft?",
  'modules.subtitle': "Modules taken from ANTON, run for this client with Olivia's rules: web search off, every claim about the client quoted, no score, rating or number the client must decide. A module is offered once two people of the regulatory desk have confirmed it.",
  'modules.forClient': "For this client",
  'modules.aiForClient': "AI for this client:",
  'modules.noServices': "No live service is recorded for this client, so no module is marked as fitting its services.",
  'modules.services': "Your services: {list}",
  'modules.catalogue': "The catalogue",
  'modules.filter': "Show",
  'modules.filter.all': "All ({n})",
  'modules.filter.fits': "Your services ({n})",
  'modules.noneFits': "None of the {n} modules fits this client's services.",
  'modules.none': "No module is loaded in the library yet. Counted: every module of the library.",
  'modules.col.module': "Module",
  'modules.col.desk': "The regulatory desk",
  'modules.col.output': "Output",
  'modules.idLine': "{id} · v{version} (ANTON {commit})",
  'modules.webSearchOff': "web search off",
  'modules.catalogueNote': "Each module is a copy of an ANTON module at a fixed version, with Olivia's overlay sent after it. A run goes to Claude through the Anthropic API under this client's AI allowance, or, for a client declared synthetic, also to the pinned OpenRouter models (Mistral Large 4, GLM or Kimi). Its answer is a draft until a person makes it a deliverable and a reviewer approves it.",
  'modules.confirmed': "confirmed by the desk",
  'modules.waitingDesk': "waiting for the desk ({n} of 2 acts)",
  'modules.needsSource': "needs a grounded source",
  'modules.needsSourceMissing': "needs a grounded source: no document with text yet",
  'modules.ai.synthetic': "synthetic data: Claude or GLM",
  'modules.ai.on': "on",
  'modules.ai.off': "off",
  'modules.ai.waiting': "waiting for a second member",
  'modules.ai.review_passed': "due for review",
  'modules.blocked.ALLOWANCE_OFF': "AI is not switched on for this client, so a module cannot run. The lead switches it on in Members and AI.",
  'modules.blocked.ALLOWANCE_UNCONFIRMED': "AI for this client waits for a second member to confirm it, so a module cannot run yet.",
  'modules.blocked.ALLOWANCE_REVIEW_PASSED': "The AI allowance for this client is past its review date, so a module cannot run until it is reviewed.",
  'modules.blocked.ENGINE_NOT_CONFIGURED': "This installation has no model a module may use for this client (Claude through the Anthropic API needs its key), so a module cannot run. The preview still shows what would be sent.",
  'modules.blocked.NOT_OFFERED': "This module is not offered until two people of the regulatory desk have confirmed it.",
  'modules.texts': "The prompt, the overlay and the licence",
  'modules.readTexts': "Read the prompt ({k}k characters), the overlay and the licence",
  'modules.readPrompt': "Read the prompt ({path})",
  'modules.promptFrom': "A byte copy of ANTON's {source} at {commit}, SHA-256 {sha}…; it is never edited.",
  'modules.readOverlay': "Read the overlay (Olivia's rules, sent after the module's text)",
  'modules.licence': "Licence and changes (Apache-2.0)",
  'modules.licenceNote': "ANTON is licensed under the Apache License 2.0. The list below names every file taken from ANTON, the byte copies and the files changed, with each change.",
  'modules.provenance': "Provenance of the copied files",
  'modules.notice': "Notice",
  'modules.run': "Fill in, preview and run",
  'modules.required': "(required)",
  'modules.choose': "Choose",
  'modules.chooseAnalysis': "Choose an analysis",
  'modules.noAnalysis': "This client has no analysis yet. Counted: analyses not withdrawn.",
  'modules.analysisDerived': "What the request asks for is not typed here: the run takes the items of \"What we still need\" for this analysis, in that list's order, with the request each is already on, and the model words them.",
  'modules.chooseMany': "Choose any that apply",
  'modules.coverage': "Asked for: {n} items from \"What we still need\" ({codes}).",
  'modules.coverageAll': "The draft names every item by its code.",
  'modules.coverageMissing': "Not named in the draft:",
  'modules.derivedActions': "The actions are not typed here: the run takes every action raised from this frozen analysis's gaps, with its status and proof as Olivia records them. Olivia prints the statuses in the report; the model writes the words around them.",
  'modules.derivedRegister': "The figures are not typed here: the run takes the counts of this frozen analysis's register (gaps, possible breaches, board decisions, actions, escalations). Olivia prints them as a table; the model writes words only, and every count it states is checked.",
  'modules.derivedFinding': "The finding is not typed here: the run takes what Olivia's rules wrote for this gap of a frozen analysis (condition, criteria, what shows it is done) and what people recorded on it. No grade is sent or asked for.",
  'modules.derivedAgreement': "The agreement's points are not typed here: the run takes the item records of this service's agreement check (EBA/GL/2019/02 paragraph 75). Choose the agreement itself among the sources below.",
  'modules.noFrozenAnalysis': "This client has no frozen analysis yet. Counted: analyses frozen.",
  'modules.chooseGap': "Choose a gap",
  'modules.noGap': "No gap of a frozen analysis has its finding written yet. Counted: findings of frozen analyses.",
  'modules.chooseAgreement': "Choose a service's agreement",
  'modules.noAgreement': "No service of this client has an agreement check yet (Mandate). Counted: services with an agreement check.",
  'modules.agreementLine': "{code} · {kind} · {n} of {of} points recorded",
  'modules.codeTableNote': "Printed by Olivia from its records, above the draft's words in the deliverable; the model did not write it.",
  'modules.proseChecked': "Every count and verdict the draft states was checked against Olivia's records: nothing to flag.",
  'modules.proseFlags': "Statements in the draft that Olivia's records do not support:",
  'modules.proseFlag': "\"{excerpt}\": states {stated}, the records hold {register}",
  'modules.valuesEmpty': "The column the client fills is empty in every row.",
  'modules.valueFilled': "Filled in by the draft, where the client sets the value:",
  'modules.coverageActions': "Reported on: {n} actions raised from the analysis ({codes}).",
  'modules.coverageActionsAll': "The draft names every action by its code.",
  'modules.derivedOffers': "The offers are not typed here: the run takes the gaps of this frozen analysis that lie outside what the client bought (Olivia's rule role.v1) and the services that could close them, after the independence check. Only the services it lets through may be proposed.",
  'modules.derivedAgents': "The count of agents is not typed here: the run takes the client's confirmed business figure at the day chosen. Olivia prints it in the report; the model writes the words around it.",
  'modules.chooseFigure': "Choose a confirmed count of agents",
  'modules.noFigure': "No confirmed count of agents is recorded for this client yet (Business). Counted: confirmed counts of agents.",
  'modules.figureLine': "{value} agents at {day}",
  'modules.groundingAll': "The draft names every source document by its code.",
  'modules.groundingMissing': "Source documents the draft does not name:",
  'modules.independenceNone': "Olivia's independence check withheld no service.",
  'modules.independenceWithheld': "Withheld by Olivia's independence check: {list}.",
  'modules.withheldNamed': "Named in the draft although the independence check does not let the firm offer it:",
  'modules.agentsMatch': "Every count of agents the draft states is the confirmed one.",
  'modules.agentsStated': "Counts of agents in the draft that differ from the record ({recorded}):",
  'modules.sources': "Sources: the client's documents (up to {max})",
  'modules.noDocuments': "No document of this client has extracted text yet.",
  'modules.groundedRule': "This module relied on web search in ANTON. Here web search is off, so it runs only with a grounded source: a document of the client. A knowledge pack is a summary, not a source, and does not count.",
  'modules.boundary.client': "A run sends this client's chosen documents and your inputs to Claude through the Anthropic API, under the client's AI allowance, with no tools and no web search. The output is a draft for a person to read.",
  'modules.boundary.synthetic': "This client is declared synthetic: a run may go to Claude or to the pinned OpenRouter models (Mistral Large 4, GLM or Kimi). A document in which personal identifiers are found is never sent to them.",
  'modules.runIt': "Run",
  'modules.aiDraft': "AI draft, not checked by a person",
  'modules.running': "Running…",
  'modules.openDraft': "Open the draft {code}",
  'modules.refused.ALLOWANCE_OFF': "Refused: AI is not switched on for this client.",
  'modules.refused.ALLOWANCE_UNCONFIRMED': "Refused: AI for this client waits for a second member to confirm it.",
  'modules.refused.ALLOWANCE_REVIEW_PASSED': "Refused: the AI allowance for this client is past its review date.",
  'modules.refused.PROMPT_NOT_ADOPTED': "Refused: a library file this run sends is not adopted by two people of the regulatory desk.",
  'modules.refused.CLIENT_TO_PUBLIC_ENGINE': "Refused: client data goes only to Claude through the Anthropic API.",
  'modules.refused.CLIENT_CEILING': "Refused: this installation sends client data to no model.",
  'modules.refused.AI_OFF': "Refused: AI is switched off on this server.",
  'modules.refused.UNPRICED': "Refused: the model has no known price while a daily AI budget is set.",
  'modules.refused.DAILY_SPEND_CAP': "Refused: today's AI budget is used up.",
  'modules.refused.MONTHLY_SPEND_CAP': "Refused: this client's monthly AI budget is used up.",
  'modules.refused.SYNTHETIC_IDENTIFIERS': "Refused: a document looks as if it holds personal identifiers, so it is not sent to an OpenRouter model.",
  'modules.refused.SYNTHETIC_UNCHECKED': "Refused: a document has not been checked for personal identifiers yet, so it is not sent to an OpenRouter model.",
  'modules.refused.NOT_CONFIGURED': "Refused: Claude through the API is not configured on this server.",
  'modules.refused.other': "Refused ({code}).",
  'modules.nothingSent': "Nothing was sent.",
  'modules.failed': "The run did not complete ({code}). Its record is in AI drafts.",
  'modules.back': "All modules",
  'modules.card': "The module",
  'modules.does': "What it does",
  'modules.never': "What it never does",
  'modules.output': "Output",
  'modules.packs': "Knowledge packs",
  'modules.prompt': "Its prompt",
  'modules.promptLine': "SHA-256 {sha}…, from ANTON at {commit}",
  'modules.notOffered': "Not offered: the regulatory desk has done {n} of its 2 acts on this module.",
  'modules.runs': "Runs for this client",
  'modules.noRuns': "No run of this module for this client yet. Counted: every run that sent a draft.",
  'modules.col.run': "Run",
  'modules.col.when': "When and by whom",
  'modules.col.status': "State",
  'modules.col.deliverable': "Deliverable",
  'modules.runStatus.draft': "draft",
  'modules.runStatus.made_deliverable': "made a deliverable",
  'modules.runStatus.discarded': "discarded",
  'modules.backToModule': "The module",
  'modules.draft': "The draft",
  'modules.madeBy': "Run by",
  'modules.call': "The model call",
  'modules.sourcesUsed': "Sources",
  'modules.noSourceUsed': "no document of the client",
  'modules.discardedWhy': "Discarded because",
  'modules.deliverableLanguage': "Deliverable in",
  'modules.made': "Made a deliverable. Review and approval follow on its card below.",
  'modules.makeDeliverable': "Make a deliverable",
  'modules.discard': "Discard the draft",
  'modules.discardWhy': "Why it is discarded",
  'modules.discardSubmit': "Discard",
  'modules.discarded': "Discarded.",
  'modules.draftNote': "A draft is made a deliverable once, or discarded; the text itself never changes. The deliverable's footer says it was prepared with AI assistance.",
  'modules.deliverable.standing': "Can the deliverable be published?",
  'modules.deliverable.internalWhy': "Built on a module or a library file the regulatory desk has not adopted: this deliverable can be used inside the firm but cannot be published.",
  'modules.deliverable.publishable': "The module and every library file its run used are adopted by the regulatory desk: the deliverable can be published once a reviewer has approved it.",
  'modules.deliverable.renderExpires': "Rendering it again returns the deliverable to draft and expires its checkpoint.",
  'modules.deliverable.whyNotPublish': "You prepared this deliverable or asked for its review, so another reviewer publishes it.",
  'moduleRunStatus.draft': "draft",
  'moduleRunStatus.made_deliverable': "made a deliverable",
  'moduleRunStatus.discarded': "discarded",
  'library.tab.modules': "Modules",
  'library.tab.packs': "Knowledge packs",
  'libraryPacks.title': "Knowledge packs from ANTON",
  'libraryPacks.counts': "{n} packs · {confirmed} confirmed by the desk · copied from ANTON at {commit}",
  'libraryPacks.counts.one': "{n} pack · {confirmed} confirmed by the desk · copied from ANTON at {commit}",
  'libraryPacks.rule': "Every entry of a pack is a summary written by ANTON's authors, not the text of the law. A module run sends it as context, labelled so, and never as a grounded source or the quote behind a claim. A run may use a pack only once two people of the regulatory desk have acted on its card.",
  'libraryPacks.none': "No knowledge pack is loaded.",
  'libraryPacks.confirmed': "confirmed by the desk",
  'libraryPacks.corrected': "corrected by Olivia",
  'libraryPacks.entries': "{n} entries",
  'libraryPacks.entries.one': "{n} entry",
  'libraryPacks.deferred': "Deferred: {n} packs, not copied",
  'libraryPacks.deferred.one': "Deferred: {n} pack, not copied",
  'libraryPacks.note': "A pack comes into Olivia as a copy of ANTON's files at a pinned commit, by script (pnpm packs:sync), byte for byte or with Olivia's recorded correction; nobody edits it on screen.",
  'packs.label': "Summary (ANTON pack), not a source",
  'explain.packSummary': "{id} {version}: {n} entries, {label}",
  'explain.packSummary.one': "{id} {version}: {n} entry, {label}",
  'explain.part.pack': "A knowledge pack (summary, not a source)",
  'modules.packs.legend': "Knowledge packs (context, not sources)",
  'modules.packs.none': "This module takes no knowledge pack.",
  'modules.packs.rule': "A pack adds ANTON's summaries as context. It is labelled a summary, never counts as a grounded source and is never quoted as the law.",
  'modules.packs.waiting': "waiting for the desk ({n} of 2 acts)",
  'modules.packsUsed': "Knowledge packs used",
  'modules.packUsedLine': "{title} v{version}: {n} entries, {label}",
  'modules.packUsedLine.one': "{title} v{version}: {n} entry, {label}",
  'errors.PACK_NOT_FOR_MODULE': "This module does not take that knowledge pack. Nothing was sent.",
  'errors.PACK_TOO_LARGE': "The knowledge packs chosen hold more than one run takes; choose fewer. Nothing was sent.",
  'libraryModules.title': "Modules from ANTON",
  'libraryModules.counts': "{n} modules · {confirmed} confirmed by the desk · copied from ANTON at {commit}",
  'libraryModules.screening': "The screening covers {n} modules of ANTON: {w1} in wave 1, {w2} in wave 2, {later} later and {out} left out, each with its reason.",
  'libraryModules.none': "No module is loaded. Counted: the module cards under data/modules.",
  'libraryModules.card': "The card ({path}): one desk member checks it, a second adopts it; then the module is offered",
  'libraryModules.parts': "The files a run sends with it ({adopted} of {n} adopted)",
  'libraryModules.note': "A module comes into Olivia as a byte copy of ANTON's files at a pinned commit, by script; nobody edits it on screen. A real client's run also needs every file it sends adopted, at its exact SHA-256.",
  'explain.part.module': "The module's prompt (ANTON)",
  'explain.part.persona': "An expert perspective (ANTON)",
  'explain.part.skill': "A skill (ANTON)",
  'explain.part.format': "Output format",
  'explain.part.overlay': "Olivia's overlay",
  'errors.NO_GROUNDED_SOURCE': "This module relied on web search in ANTON, which is off here. Choose a grounded source: a document of the client (a knowledge pack is a summary, not a source). Nothing was sent.",
  'errors.MODULE_RUN_STATE': "This draft was already made a deliverable or discarded.",
  'errors.MODULE_INPUTS': "Fill in the module's inputs as asked: every required one, and an option from the list.",
  'errors.MODULE_DRIFT': "This module's prompt changed after the library was loaded. Nothing was sent; the library is loaded again and the desk confirms the module again.",
  'errors.PACK_NOT_CONFIRMED': "This knowledge pack is not confirmed by two people of the regulatory desk yet. Nothing was sent.",
  'errors.MODULE_TOO_MANY_DOCUMENTS': "This module takes at most {max} documents.",
  'errors.MODULE_NOTHING_TO_REQUEST': "This analysis has nothing to ask the client for: it is closed, or nothing it still needs is settled by a document from the client. Nothing was sent.",
  'errors.MODULE_NOT_FROZEN': "This module works from a frozen analysis; this one is not frozen. Nothing was sent.",
  'errors.MODULE_NOT_A_GAP': "This practice is not a gap of the analysis with its finding written. Write the findings first, or choose another gap. Nothing was sent.",
  'errors.MODULE_NOTHING_TO_FOLLOW_UP': "No action has been raised from this analysis's gaps, so there is nothing to follow up. Nothing was sent.",
  'errors.MODULE_NO_AGREEMENT_ITEMS': "No item of this service's agreement has been recorded yet (Mandate, the agreement check). Nothing was sent.",
  'errors.MODULE_NOTHING_OUTSIDE': "No gap of this analysis lies outside what the client bought, so there is nothing to propose. Nothing was sent.",
  'errors.MODULE_INDEPENDENCE': "Every service that could close the gaps outside what the client bought fails the independence check: the firm would review its own work. Nothing was sent.",
  'errors.MODULE_NO_AGENTS': "No confirmed count of agents is recorded for the client at that day (Business). Nothing was sent.",
  'method.area.modules': "Modules from ANTON",
  'auditAction.module.compose': "Saved a module's inputs",
  'auditAction.module.run': "Ran a module (its draft)",
  'auditAction.module.make_deliverable': "Made a deliverable of a module's draft",
  'auditAction.module.discard': "Discarded a module's draft",
  'record.moduleRun': "Module draft",
  'auditField.moduleRun': "module draft",
  'auditField.modelCall': "model call",
  'auditField.groundedSources': "grounded sources",
  'auditField.draftChars': "length of the draft",
  'auditField.discardedBy': "discarded by",
  'auditField.discardedAt': "discarded",
  // IP11: the next step, the other experts and hand-offs.
  'nav.handoffs': "Hand-offs",
  'nav.handoffs.answers': "What went to the other experts, and what came back?",
  'auditAction.handoff.prepare': "Prepared a hand-off to another expert",
  'auditAction.handoff.request_review': "Asked for review of a hand-off's pack",
  'auditAction.handoff.approve': "Approved a hand-off's pack",
  'auditAction.handoff.reject': "Sent a hand-off back",
  'auditAction.handoff.sent': "Recorded that a hand-off was sent",
  'auditAction.handoff.return': "Recorded what came back from an expert",
  'auditAction.handoff.evidence': "Recorded an expert's report as evidence",
  'auditAction.handoff.withdraw': "Withdrew a hand-off",
  'next.chip': "Next steps: {n} ({offered} offered) · {priority}",
  'next.noPriority': "no priority",
  'next.priority': "Priority {priority}",
  'next.title': "Next steps for {practice}",
  'next.back': "← The findings and the plan",
  'next.gapLine': "{title} · level {level}, target {target}",
  'next.rule': "Chosen by rule nextstep.v1, version {version}",
  'next.adopted': "adopted by two people of the regulatory desk",
  'next.notAdopted': "not yet adopted by the regulatory desk: the suggestions are for internal use",
  'next.suggestions': "Suggested next steps",
  'next.noSuggestions': "No rule suggests a next step for this gap.",
  'next.kind.module': "Module",
  'next.kind.olivia': "Olivia",
  'next.kind.expert': "Expert",
  'next.state.offered': "offered",
  'next.state.not_yet_offered': "not yet offered",
  'next.state.not_yet_available': "not yet available",
  'next.state.done': "done",
  'next.act.raise_action': "Raise the action",
  'next.act.apply_series': "Apply the series of the annual cycle",
  'next.act.create_kri': "Create a key risk indicator",
  'next.act.add_request_lines': "Ask the client for what is missing",
  'next.why': "why: {reason}",
  'next.openModule': "Open, pre-filled",
  'next.go': "Go there",
  'next.prepare': "Prepare hand-off",
  'next.notSuggested': "Not suggested",
  'next.handoffs': "Hand-offs of this gap",
  'next.noHandoffs': "No hand-off yet.",
  'handoff.request.note': "The request file Valentin imports, with client data: the client, the activity it answers, the firm's services and the people on them, the gaps' baseline and the model. A reviewer who did not prepare it approves its exact bytes before anyone sends it.",
  'handoff.brief.note': "A brief with no client data: the practices, their public references and the task. This expert receives public material only.",
  'handoff.form.title': "Title",
  'handoff.form.occurrence': "The annual-cycle activity it answers (code)",
  'handoff.form.occurrenceHint': "For example CY-2027-001: a model validation, an audit or a review Valentin performs.",
  'handoff.form.validationType': "Kind of validation",
  'handoff.form.lead': "The lead you ask for at Valentin (e-mail)",
  'handoff.form.periodFrom': "Period from",
  'handoff.form.periodTo': "Period to",
  'handoff.form.objectives': "Objectives",
  'handoff.form.scope': "Scope",
  'handoff.form.submit': "Prepare the pack",
  'handoff.validationType.initial': "initial",
  'handoff.validationType.periodic': "periodic",
  'handoff.validationType.triggered_by_change': "after a change",
  'handoff.validationType.conditions_review': "a review of the conditions of use",
  'handoff.task.model_validation': "an independent validation of a model",
  'handoff.task.independent_audit': "a test by someone independent of the work",
  'handoff.task.bwra': "the business-wide risk assessment",
  'handoff.task.article_check': "an article-by-article check",
  'handoff.task.watch_rule': "watching a rule about to change",
  'handoff.task.continuous_support': "continuous support of the team",
  'handoff.task.other': "expert support",
  'handoff.task.crisis_simulation': "a crisis exercise",
  'handoff.task.project': "running the work as a project",
  'handoff.status.draft': "draft",
  'handoff.status.in_review': "in review",
  'handoff.status.approved': "approved, not sent",
  'handoff.status.sent': "sent, waiting for an answer",
  'handoff.status.answered': "answered",
  'handoff.status.withdrawn': "withdrawn",
  'handoff.packKind.valentin_request': "olivia.handoff-request.v1 (with client data)",
  'handoff.packKind.public_brief': "a brief (no client data)",
  'handoff.independence.computed': "request_id matches: independent",
  'handoff.independence.cannot_be_matched': "not independent: receipt cannot be matched",
  'handoff.match.matched': "the receipt carries this hand-off's request_id",
  'handoff.match.no_request_id': "the receipt carries no request_id: the validation was not started from this request",
  'handoff.match.other_request': "the receipt answers another request",
  'handoff.channelWord.client_data_allowed': "client data allowed, by file",
  'handoff.channelWord.public_only': "public material only",
  'handoff.channelWord.not_available': "no client data: no boundary recorded",
  'handoffs.title': "Hand-offs",
  'handoffs.empty': "No hand-off yet. A hand-off starts from a gap's next steps.",
  'handoffs.col.code': "Hand-off",
  'handoffs.col.expert': "Expert",
  'handoffs.col.gaps': "Gaps",
  'handoffs.col.status': "Status",
  'handoffs.col.prepared': "Prepared",
  'handoff.backToList': "← Hand-offs",
  'handoff.title': "Title",
  'handoff.gaps': "Gaps",
  'handoff.analysis': "Analysis",
  'handoff.channelLabel': "The expert's data channel",
  'handoff.prepared': "Prepared",
  'handoff.pack': "The pack",
  'handoff.packKindLabel': "Kind",
  'handoff.requestId': "request_id",
  'handoff.occurrence': "Activity of the annual cycle",
  'handoff.size': "Size",
  'handoff.bytes': "{n} bytes",
  'handoff.sha': "SHA-256",
  'handoff.download': "Download the pack",
  'handoff.checkpoint': "Checkpoint",
  'handoff.cp': "{code} · {status} · asked for by {by}",
  'handoff.decided': "decided by {by}",
  'handoff.noCheckpoint': "No review asked for yet.",
  'handoff.askReview': "Ask for review",
  'handoff.approve': "Approve the pack",
  'handoff.reject': "Send back",
  'handoff.rejectWhy': "Why it goes back",
  'handoff.reviewerDecides': "A reviewer who neither prepared the hand-off nor asked for its review decides it.",
  'handoff.sent': "Sent",
  'handoff.sentOn': "Day it was sent",
  'handoff.channel': "How it was sent",
  'handoff.markSent': "Record that it was sent",
  'handoff.notSent': "Not sent: the pack is approved at its checkpoint first.",
  'handoff.readyToSend': "Approved: a person sends the file and records the day and how.",
  'handoff.sentLine': "{day} by {by} · {channel}",
  'handoff.back': "What came back",
  'handoff.nothingBack': "Nothing has come back yet.",
  'handoff.returnHint': "Upload the report (and Valentin's receipt) under Documents first, then name them here.",
  'handoff.report': "Report (document code)",
  'handoff.receipt': "Valentin's receipt (document code)",
  'handoff.recordReturn': "Record what came back",
  'handoff.returned': "Recorded by",
  'handoff.evidence': "As evidence",
  'handoff.evidenceAssessment': "Open analysis",
  'handoff.evidenceTitle': "Title",
  'handoff.evidenceDated': "Dated",
  'handoff.recordEvidence': "Record as evidence",
  'handoff.evidenceNone': "No analysis is open: the report becomes evidence at the next re-assessment.",
  'handoff.evidenceRow': "{code} in {assessment}",
  'handoff.reviewed': "· reviewed by {by}",
  'handoff.unreviewed': "· not reviewed yet: a member who did not record the return reviews it",
  'handoff.withdraw': "Withdraw",
  'handoff.withdrawWhy': "Why it is withdrawn",
  'handoff.withdrawn': "Withdrawn by {by}: {reason}",
  'modules.prefilled': "Pre-filled from {gap}: {facts} facts missing at the target, {references} references, evidence {evidence}. Change anything before you run.",
  'inbox.rule.handoff_unanswered': "Hand-off not answered",
  'evidenceKind.expert_report': "Another expert's report",
  'record.handoff': "Hand-off",
  'auditField.handoff': "hand-off",
  'auditField.expert': "expert",
  'auditField.task': "task",
  'auditField.gaps': "gaps",
  'auditField.packKind': "pack",
  'auditField.clientParts': "with client data",
  'auditField.packDocument': "pack file",
  'auditField.requestId': "request id",
  'auditField.returnedBy': "answer recorded by",
  'auditField.returnedAt': "answer recorded",
  'auditField.reportDocument': "report",
  'auditField.independence': "independence",
  'auditField.matchReason': "receipt",
  'errors.NEXT_NOT_FROZEN': "The next steps start once the analysis is frozen.",
  'errors.HANDOFF_CHANNEL': "This expert receives public material only: a pack with client data is not prepared for it.",
  'errors.HANDOFF_EXPERTS_NOT_ADOPTED': "The experts and their data channels wait for two people of the regulatory desk to adopt them; no pack with client data is prepared until then.",
  'errors.HANDOFF_EXPERT_UNAVAILABLE': "This expert is not available for a hand-off.",
  'errors.HANDOFF_NOT_APPROVED': "A hand-off is sent once a reviewer who did not prepare it has approved its pack.",
  'errors.FOUR_EYES_HANDOFF': "You prepared this hand-off or asked for its review, so another reviewer decides it.",
  'errors.HANDOFF_SENT_DAY': "The day it was sent is between the day it was prepared and today.",
  'errors.HANDOFF_IN_REVIEW': "This hand-off waits for a decision at its checkpoint first.",
  'errors.HANDOFF_STATE': "This cannot be done to the hand-off as it stands.",
  'errors.HANDOFF_RETURN_DOCUMENTS': "What comes back is current documents of this client, other than the pack.",
  'errors.FOUR_EYES_RETURN': "You recorded what came back, so another member reviews it.",
  'errors.HANDOFF_EVIDENCE_MISMATCH': "A returned report is evidence of its own answered hand-off, with its documents.",
  'errors.HANDOFF_NOT_A_GAP': "Choose gaps of this frozen analysis.",
  'errors.HANDOFF_NEEDS_OCCURRENCE': "A hand-off to Valentin answers an activity of the annual cycle: choose the occurrence it is for.",
  'errors.HANDOFF_FINDING_NOT_WRITTEN': "Write the findings of the gaps first: the request carries each gap with its finding.",
  'errors.HANDOFF_KIND_NOT_PERFORMED': "Valentin does not perform this kind of activity.",
  'errors.HANDOFF_JURISDICTION': "Valentin takes clients in Sweden, Denmark, Norway and Finland.",
  'errors.HANDOFF_TOO_LONG': "The request is too long for Valentin.",
  'errors.HANDOFF_RECEIPT_NEEDED': "Valentin's report comes back with its receipt: choose both documents.",
  'errors.HANDOFF_NO_RECEIPT': "Only Valentin returns a receipt.",
  'errors.HANDOFF_RECEIPT_INVALID': "This document is not a receipt of Valentin.",
  'errors.HANDOFF_RECEIPT_HASH': "The receipt's own SHA-256 does not match its content: it was changed after Valentin wrote it.",
  'errors.HANDOFF_REPORT_HASH': "The report is not the one the receipt names: its SHA-256 differs.",
  // IP12: AI proposals I (the evidence inbox, the proposals, the second opinion, FB15).
  'engine.nav.inbox': "Evidence inbox",
  'evinbox.title': "Evidence inbox",
  'evinbox.counts': "In the inbox: {inbox} · filed: {filed} · set aside: {set_aside}",
  'evinbox.batch': "Batch {code}: {n} files, uploaded by {who}, {when}",
  'evinbox.note': "A document waits here until a member files it, or sets it aside with the reason. An AI proposal maps a document to the practices; a person accepts each link after opening the quoted page.",
  'evinbox.add': "Upload a batch",
  'evinbox.files': "Files",
  'evinbox.filesHint': "Choose many at once: PDF, Word, Excel, PowerPoint, e-mails and transcripts.",
  'evinbox.noCustomerData': "These files hold no customer-level data.",
  'evinbox.upload': "Upload {n} files into a new batch",
  'evinbox.uploaded': "{n} files uploaded into the inbox.",
  'evinbox.documents': "Documents",
  'evinbox.empty': "No document has been uploaded into the inbox of this analysis yet.",
  'evinbox.text.extracted': "text extracted ({pages} pages)",
  'evinbox.text.not_checkable': "no text (a scan or an image): quotes cannot be checked",
  'evinbox.text.failed': "the text could not be read",
  'evinbox.text.pending': "text not read yet",
  'evinbox.state.inbox': "in the inbox",
  'evinbox.state.filed': "filed",
  'evinbox.state.set_aside': "set aside",
  'evinbox.proposals': "proposals: {pending} waiting · {accepted} accepted · {refused} refused",
  'evinbox.review': "Review",
  'evinbox.hide': "Hide",
  'evinbox.notCheckable': "This document has no text to map: reading a scanned page waits for a decision of the owner.",
  'evinbox.file': "File",
  'evinbox.setAside': "Set aside",
  'proposals.allowanceOff': "AI is not switched on for this client: nothing is sent.",
  'proposals.boundary': "Before it runs: the documents and the practices are sent to {model}, for client data only through the Anthropic API; at most {cost}. Nothing is sent until you press the button.",
  'proposals.noPrice': "no price known",
  'proposals.ran': "Done: {pending} proposals wait for a person; {refused} were refused before anyone saw them.",
  'proposals.refused': "{n} proposals were refused before you saw them.",
  'proposals.refusedReason': "{reason}: {n}",
  'proposals.refusal.QUOTE_NOT_FOUND': "quote not found in the document",
  'proposals.refusal.QUOTE_MISSING': "no quote",
  'proposals.refusal.QUOTE_TOO_LONG': "quote longer than 300 characters",
  'proposals.refusal.NOT_IN_SCOPE': "practice not in the scope of this analysis",
  'proposals.refusal.UNKNOWN_FACT': "a fact or element the practice does not ask for",
  'proposals.refusal.UNKNOWN_DOCUMENT': "a document that was not sent",
  'proposals.refusal.UNKNOWN_ACTION': "an action not in this pack",
  'proposals.refusal.UNKNOWN_PROOF_ITEM': "a proof item the action did not ask for",
  'proposals.what.link': "{practice}: bears on {on}",
  'proposals.what.fact': "{practice}: {fact} · {value}",
  'proposals.what.proof': "{action}: {item}",
  'proposals.partly': "(partly found)",
  'proposals.value.yes': "yes",
  'proposals.value.no': "no",
  'proposals.value.unknown': "not shown",
  'proposals.status.pending': "waiting for a person",
  'proposals.status.accepted': "accepted",
  'proposals.status.rejected': "rejected",
  'proposals.status.stale': "no longer current",
  'proposals.whatWentIn': "What went in ({call})",
  'proposals.quoteAt': "({document}, page {page})",
  'proposals.quoteFound': "quote found",
  'proposals.why': "The model says: {why}",
  'proposals.decided': "Decided by {who} {when}.",
  'proposals.nobody': "no one",
  'proposals.because': "Why: {reason}",
  'proposals.became': "It became {record}.",
  'proposals.openPage': "Open page {page}",
  'proposals.accept': "Accept",
  'proposals.acceptAfterOpen': "Accept is enabled after the quoted page has been opened.",
  'proposals.replace': "Accept, replacing the recorded fact",
  'proposals.replaceReason': "Why the recorded fact is replaced",
  'proposals.reject': "Reject",
  'proposals.none': "No proposal is waiting.",
  'proposals.noneProof': "No match is proposed for this pack yet.",
  'proposals.map': "Propose mapping (AI)",
  'proposals.facts': "Propose facts (AI)",
  'proposals.factsTitle': "AI proposals for this practice",
  'proposals.showFacts': "Show the AI proposals",
  'proposals.secondOpinion': "Ask for a second opinion",
  'proposals.proof': "Match proof (AI)",
  'proposals.proofCard': "Proof matching",
  'proposals.proofTitle': "Remediation pack {pack}",
  'proposals.showProof': "Match proof in pack {pack} (AI)",
  'secondOpinion.title': "Second opinion",
  'secondOpinion.note': "A second Claude model answers the same questions without seeing the first answers. Each disagreement is resolved or set aside with a reason; an open one blocks nothing and is counted before the freeze.",
  'secondOpinion.line': "{code}: {first} and {second} · open disagreements: {open}",
  'secondOpinion.col.fact': "Fact",
  'secondOpinion.col.first': "First model",
  'secondOpinion.col.second': "Second model",
  'secondOpinion.col.agreement': "Agreement",
  'secondOpinion.col.decision': "Decision",
  'secondOpinion.answer.yes': "yes",
  'secondOpinion.answer.no': "no",
  'secondOpinion.answer.unknown': "not shown",
  'secondOpinion.answer.none': "no answer",
  'secondOpinion.agree': "agree",
  'secondOpinion.disagree': "disagree",
  'secondOpinion.do.resolved': "Resolve",
  'secondOpinion.do.set_aside': "Set aside",
  'secondOpinion.resolution.resolved': "Resolved by {who}: {reason}",
  'secondOpinion.resolution.set_aside': "Set aside by {who}: {reason}",
  'freeze.blocker.ai_unconfirmed': "AI-proposed yes or partial facts waiting for a second member",
  'freeze.secondOpinionsOpen': "Second-opinion disagreements left open: {n}. They block nothing; read them before you freeze.",
  'heat.aiShare': "{n} of {of} facts rest on AI proposals.",
  'discover.gather.inbox': "Evidence inbox: {inbox} waiting · {filed} filed · {set_aside} set aside",
  'discover.analyse.ai': "Facts from AI proposals: {n} of {of} (not yet confirmed: {u}) · proposals waiting: {p}",
  'errors.INBOX_CLOSED': "Documents are taken into the inbox while the analysis gathers or assesses.",
  'errors.INBOX_STATE': "This document is filed or set aside already.",
  'errors.PROPOSAL_STATE': "This proposal is decided already.",
  'errors.PROPOSAL_NOT_OPENED': "Open the quoted page before you accept it.",
  'errors.FOUR_EYES_PROOF_MATCH': "You own this action or recorded its remediation, so another member accepts the match.",
  'errors.SECOND_OPINION_STATE': "This disagreement is resolved or set aside already, or the reason is missing.",
  'errors.FREEZE_AI_UNCONFIRMED': "Some facts recorded from AI proposals wait for a second member to confirm them ({n}).",
  'errors.PROPOSAL_FIGURE': "The answer carried a figure that code and people decide, so all of it was refused before anyone saw it. Nothing was recorded.",
  'errors.PROPOSAL_SCHEMA': "The answer was not in the form asked for, so all of it was refused before anyone saw it. Nothing was recorded.",
  'errors.SECOND_OPINION_ENGINE': "A second opinion is asked of Claude only.",
  'errors.NO_INDEPENDENT_MODEL': "No independent model is offered for a second opinion on this run.",
  'errors.SECOND_OPINION_INPUT_CHANGED': "What the first run read has changed since, so a second opinion cannot read the same.",
  'errors.AI_INPUT_TOO_LARGE': "What this run would send is larger than one call takes. Choose fewer documents.",
  'errors.PROPOSAL_NOTHING_TO_SEND': "There is nothing for this run to read yet.",
  'errors.PROPOSAL_ANALYSIS_STATE': "Proposals are made while the analysis gathers or assesses.",
  'auditField.batch': "Batch",
  'auditField.inboxState': "In the inbox",
  'auditField.proposalKind': "Kind of proposal",
  'auditField.items': "Items in the answer",
  'auditField.proposalsPending': "Proposals for a person",
  'auditField.proposalsRefused': "Proposals refused by code",
  'auditField.proofItem': "Proof item",
  'auditField.matchAnswer': "Match",
  'auditField.page': "Page",
  'auditField.openedBy': "Opened by",
  'auditField.openedAt': "Opened at",
  'auditField.firstModel': "First model",
  'auditField.secondModel': "Second model",
  'auditField.firstAnswer': "First answer",
  'auditField.secondAnswer': "Second answer",
  'auditField.agreement': "Agreement",
  'auditField.resolution': "Resolution",
  'auditField.resolvedBy': "Resolved by",
  'auditField.proposal': "AI proposal",
  'auditField.action': "Action",
  'auditField.evidence': "Evidence item",
  'proposals.runKind.evidence_map': "evidence map",
  'proposals.runKind.fact': "fact proposals",
  'proposals.runKind.fact_second': "second opinion",
  'proposals.runKind.proof_match': "proof matching",
  'proposals.kind.evidence_map': "evidence link",
  'proposals.kind.fact': "fact",
  'proposals.kind.proof_match': "proof match",
  'proposals.kind.transcript_statement': "statement from a transcript",
  'proposals.kind.interview_summary': "statement from the interviewer's notes",
  'proposals.kind.interview_question': "tailored question",
  'proposals.kind.profile_figure': "business figure",
  'proposals.kind.role_holder': "holder of a role",
  'proposals.kind.inventory_item': "system or model",
  'proposals.kind.inherited_item': "inherited count",
  'proposals.kind.mandate': "service or role of the mandate",
  'proposals.runKind.transcript': "statements from a transcript",
  'proposals.runKind.interview_summary': "statements from the interviewer's notes",
  'proposals.runKind.interview_questions': "tailored questions",
  'proposals.runKind.profile': "business figures",
  'proposals.runKind.roles': "holders of the control functions",
  'proposals.runKind.inventory': "systems and models",
  'proposals.runKind.inherited': "the inherited position",
  'proposals.runKind.mandate': "the mandate",
  'record.questionRound': "Round of tailored questions",
  'record.roundQuestion': "Question in a round",
  'auditField.guideRole': "Role interviewed",
  'auditField.chosenBy': "Put in the round by",
  'auditField.chosenAt': "Put in the round at",
  'round.role.board_chair': "Chair of the board",
  'round.role.ceo': "CEO",
  'round.role.aml_compliance_officer': "AML compliance officer",
  'round.role.first_line_head': "Head of a first-line unit",
  'round.role.firm_supporter': "The firm member who supports a role",
  'round.status.open': "open",
  'round.status.closed': "closed",
  'proposals.refusal.UNKNOWN_SPEAKER': "a speaker the brief did not name",
  'proposals.refusal.UNKNOWN_QUESTION': "a question the brief did not give",
  'proposals.refusal.SUSPICION_SENSITIVE': "near suspicion reports, without the tick",
  'proposals.refusal.UNKNOWN_ITEM': "an item, practice or fact the round does not ask about",
  'proposals.refusal.UNKNOWN_FIELD': "a field or count Olivia does not record",
  'proposals.refusal.BAD_VALUE': "a value its unit does not allow",
  'proposals.refusal.BAD_PERIOD': "a day that is not valid or has not come",
  'proposals.refusal.NOT_IN_QUOTE': "the quote does not state it",
  'proposals.refusal.UNKNOWN_KIND': "a kind Olivia does not record",
  'proposals.refusal.UNKNOWN_ROLE': "a role Olivia does not record",
  'proposals.refusal.UNKNOWN_SERVICE': "a service kind Olivia does not offer",
  'proposals2.show': "Show the AI proposals",
  'proposals2.choose': "Choose…",
  'proposals2.notConfirmed': "Proposed by AI, not confirmed",
  'proposals2.drafted': "Drafted with AI, accepted by {name}",
  'proposals2.notTheirWords': "Not yet confirmed by the participant: these are not their words.",
  'proposals2.what.statement': "{speaker} ({kind}): {text}",
  'proposals2.what.question': "{item} · {practice} · {fact}: {question}",
  'proposals2.what.figure': "{field}: {value} at {day}",
  'proposals2.what.holder': "{name}: {role}",
  'proposals2.what.system': "System ({kind}): {name}",
  'proposals2.what.model': "Model ({kind}): {name}",
  'proposals2.what.count': "{count}: {value} at {day} ({service})",
  'proposals2.what.service': "Service {kind}, {model}, from {from}",
  'proposals2.what.role': "Role {kind} ({mode}) of {service}; tasks: {tasks}",
  'proposals2.tick.allegation': "It holds no allegation of misconduct against a named person.",
  'proposals2.tick.suspicion': "It refers to no individual suspicion assessment or report.",
  'proposals2.tick.suspicionRun': "The questions near suspicion reports may be sent: this interview touches no individual suspicion assessment or report.",
  'proposals2.startOn': "Start of the service",
  'proposals2.firmPerson': "Who at the firm",
  'proposals2.fromOn': "From",
  'proposals2.clientHolder': "Who holds the role at the client",
  'proposals2.putInRound': "Put in the round",
  'proposals2.document': "Document",
  'proposals2.letter': "Engagement letter",
  'proposals2.service': "Service taken over",
  'proposals2.interview.title': "AI proposals for this interview",
  'proposals2.interview.youAccept': "You are the interviewer: you accept each drafted statement and put the questions into a round.",
  'proposals2.interview.interviewerAccepts': "The interviewer accepts each drafted statement and puts the questions into a round.",
  'proposals2.runTranscript': "Propose statements from the transcript (AI)",
  'proposals2.runNotes': "Draft statements from the notes (AI)",
  'proposals2.runQuestions': "Word the questions (AI)",
  'proposals2.noStatements': "No statement is proposed for this interview yet.",
  'proposals2.noQuestions': "No question is worded for this round yet.",
  'proposals2.rounds.title': "Rounds of tailored questions",
  'proposals2.rounds.none': "No round is opened for this interview.",
  'proposals2.rounds.role': "Role to interview",
  'proposals2.rounds.open': "Open a round",
  'proposals2.rounds.close': "Close the round",
  'proposals2.rounds.item': "{ref}: {practice}, fact {fact}",
  'proposals2.rounds.contradiction': "{ref}: {practice}, what was said does not agree with the documents: “{said}”",
  'proposals2.rounds.leftOut': "{n} items near suspicion reports were left out.",
  'proposals2.picture.title': "Proposed by AI from the client's documents",
  'proposals2.picture.kind': "What to propose",
  'proposals2.picture.pending': "{n} proposals wait for a person; none of them counts until it is accepted and, for a figure, confirmed by a second member.",
  'proposals2.runPicture': "Propose from this document (AI)",
  'proposals2.mandate.title': "The mandate proposed by AI from the engagement letter",
  'proposals2.mandate.leadActs': "Nothing is created until a lead accepts it; the rules of each role apply then.",
  'proposals2.runMandate': "Propose services and roles (AI)",
  'errors.TRANSCRIPT_MISSING': "This interview has no transcript attached.",
  'errors.NOTES_MISSING': "This interview has no notes of the interviewer to read.",
  'errors.ROUND_OPEN': "This interview has a round of questions open; close it first.",
  'errors.ROUND_CLOSED': "This round is closed: it takes no more questions.",
  'errors.ROUND_NOTHING_TO_ASK': "Nothing is left to ask this role: no fact is missing and no contradiction is open on its practices.",
  'errors.PROPOSAL_NOT_INTERVIEWER': "The interviewer accepts what was drafted for the interview.",
  'errors.FIGURE_RECORDED': "A figure for this field and day is recorded already; change it on the business profile if it should change.",
  'errors.ROLE_HOLDER_RECORDED': "Someone at the client is recorded in this role already; change it on the client page if it has changed.",
  'errors.INHERITED_AS_OF': "The inherited position is recorded as of another day; change it by hand.",
  'errors.MANDATE_NO_SERVICE': "The client has no live service of this kind for the role: accept or add the service first.",
  'proposals.answer.found': "found",
  'proposals.answer.partly_found': "partly found",
  'proposals.status.refused': "refused by code",
  'proposals.quoteCheck.found': "found",
  'proposals.quoteCheck.not_found': "not found",
  'proposals.quoteCheck.not_checkable': "not checkable",
  'proposals.quoteCheck.too_long': "too long",
  'record.intakeBatch': "Intake batch",
  'record.proposalRun': "AI proposal run",
  'record.proposal': "AI proposal",
  'record.proposalQuote': "Quote of an AI proposal",
  'record.evidenceOpening': "Opening of a quoted page",
  'record.secondOpinion': "Second opinion",
  'record.secondOpinionItem': "Agreement of a second opinion",
  'auditAction.inbox.batch': "Opened an intake batch",
  'auditAction.inbox.file': "Filed a document of the inbox",
  'auditAction.inbox.set_aside': "Set a document of the inbox aside",
  'auditAction.proposal.evidence_map': "Recorded an AI mapping of a document",
  'auditAction.proposal.facts': "Recorded AI fact proposals",
  'auditAction.proposal.brief': "Wrote the brief of an AI proposal run",
  'auditAction.proposal.transcript': "Recorded AI statements from a transcript",
  'auditAction.proposal.interview_summary': "Recorded AI statements from the interviewer's notes",
  'auditAction.proposal.interview_questions': "Recorded AI-worded interview questions",
  'auditAction.proposal.profile': "Recorded AI-proposed business figures",
  'auditAction.proposal.roles': "Recorded AI-proposed holders of control functions",
  'auditAction.proposal.inventory': "Recorded AI-proposed systems and models",
  'auditAction.proposal.inherited': "Recorded AI-proposed counts of the inherited position",
  'auditAction.proposal.mandate': "Recorded AI-proposed services and roles",
  'auditAction.round.open': "Opened a round of tailored questions",
  'auditAction.round.close': "Closed a round of tailored questions",
  'auditAction.proposal.second_opinion': "Recorded a second opinion",
  'auditAction.proposal.proof_match_instruction': "Saved what a proof matching asks for",
  'auditAction.proposal.proof_match': "Recorded an AI proof matching",
  'auditAction.proposal.open': "Opened the quoted page of an AI proposal",
  'auditAction.proposal.accept': "Accepted an AI proposal",
  'auditAction.proposal.reject': "Rejected an AI proposal",
  'auditAction.second_opinion.decide': "Resolved or set aside a disagreement of a second opinion",
  // IP13b: AI proposals III (wording, prose, the second take, modules for a need).
  'proposals3.show': "Show the AI drafts",
  'proposals3.text': "Text",
  'proposals3.none': "No draft yet.",
  'proposals3.accept': "Accept as drafted",
  'proposals3.notes': "Notes for the reader (they hold nothing; the checkpoint checks the text again):",
  'proposals3.numbers': "Numbers the draft states that what it was drafted from does not: {list}",
  'proposals3.askSecondTake': "Ask for a second take (AI)",
  'proposals3.secondTakeRan': "Done: {flags} flags written; {refused} refused because their words are not in the draft.",
  'proposals3.secondTake': "Second take",
  'proposals3.secondTakeLine': "{code} by {model} on a draft of {draft}: {flags} flags, {refused} refused",
  'proposals3.neverEdits': "A second take only flags: it never changes the draft.",
  'proposals3.noFlags': "No flag.",
  'proposals3.acceptFlag': "Accept the flag",
  'proposals3.rejectFlag': "Reject the flag",
  'proposals3.flagDecided': "The flag is decided.",
  'proposals3.flagDecidedBy': "Decided by {who} {when}: {reason}",
  'proposals3.count': "Second take: {flags} flags, {open} open",
  'proposals3.count.noTake': "Prints {drafts} AI drafts; no second take was asked for.",
  'proposals3.count.open': "Decide every flag of the second take before the checkpoint is approved.",
  'proposals3.finding.title': "AI drafts of the effect and the recommendation",
  'proposals3.finding.never': "The model drafts the effect and the recommendation from the finding as code wrote it, never the cause. A draft goes into a finding with no effect or recommendation yet, exactly as drafted.",
  'proposals3.finding.run': "Draft the effect and the recommendation (AI)",
  'proposals3.board.title': "AI drafts of the introduction and the observations",
  'proposals3.board.checked': "The model drafts from the register of the frozen analysis. An accepted draft is rendered and checked like a person's words, and its flags hold the approval.",
  'proposals3.board.run': "Draft the introduction and the observations (AI)",
  'proposals3.request.title': "The covering letter and the lines' wording",
  'proposals3.request.intro': "AI drafts of the covering letter and of each line",
  'proposals3.request.letter': "Covering letter",
  'proposals3.request.runLetter': "Draft the covering letter (AI)",
  'proposals3.request.runWording': "Word the lines (AI)",
  'proposals3.request.sent': "The request is sent: its words no longer change.",
  'proposals3.module.title': "Second take",
  'proposals3.module.intro': "Claude's other model reads the draft and what it was drafted from, and only flags. Every flag is decided before the deliverable's checkpoint is approved.",
  'proposals3.suggest.title': "Describe what you need",
  'proposals3.suggest.intro': "The model orders the confirmed modules that fit a need you type. It gives an order only; nothing is stored but the call.",
  'proposals3.suggest.need': "What do you need?",
  'proposals3.suggest.prepare': "Prepare the suggestion",
  'proposals3.suggest.run': "Suggest modules (AI)",
  'proposals3.suggest.ran': "{n} modules suggested; {dropped} names left out because they are not confirmed modules.",
  'proposals3.suggest.orderOnly': "The order is the model's suggestion; it says nothing about priority.",
  'secondTake.kind.unsupported_claim': "Unsupported claim",
  'secondTake.kind.missing_quote': "Missing quote",
  'secondTake.kind.verdict_word': "Verdict word without a conclusion",
  'secondTake.kind.regulator_objection': "A supervisor would object",
  'secondTake.status.open': "open",
  'secondTake.status.accepted': "accepted",
  'secondTake.status.rejected': "rejected",
  'proposals.kind.request_wording': "wording of a request's line",
  'proposals.kind.letter': "covering letter",
  'proposals.kind.finding_text': "effect and recommendation of a finding",
  'proposals.kind.report_prose': "introduction and observations of a board pack",
  'proposals.runKind.request_wording': "the lines' wording",
  'proposals.runKind.letter': "the covering letter",
  'proposals.runKind.finding_text': "a finding's effect and recommendation",
  'proposals.runKind.report_prose': "a board pack's prose",
  'record.secondTake': "Second take",
  'record.secondTakeFlag': "Flag of a second take",
  'auditField.code': "code",
  'auditField.createdAt': "created at",
  'auditField.coverLetterBy': "covering letter by",
  'auditField.coverLetterAt': "covering letter at",
  'auditField.wordingBy': "wording by",
  'auditField.wordingAt': "wording at",
  'auditField.draftModel': "model of the draft",
  'auditField.secondTakeFlags': "flags written",
  'auditField.flagsRefused': "flags refused by code",
  'auditField.secondTakeBy': "second take asked for by",
  'auditField.flagKind': "kind of flag",
  'auditAction.proposal.finding_text': "Recorded an AI draft of a finding's effect and recommendation",
  'auditAction.proposal.report_prose': "Recorded an AI draft of a board pack's prose",
  'auditAction.proposal.letter': "Recorded an AI draft of a covering letter",
  'auditAction.proposal.request_wording': "Recorded AI drafts of a request's lines",
  'auditAction.second_take.brief': "Wrote the brief of a second take",
  'auditAction.second_take.run': "Recorded a second take and its flags",
  'auditAction.second_take.decide': "Decided a flag of a second take",
  'auditAction.request.cover_letter': "Wrote or cleared a request's covering letter",
  'auditAction.request.line_wording': "Wrote or cleared a line's wording",
  'auditAction.nextstep.rank_brief': "Wrote the brief of a module suggestion",
  'errors.AI_ROOT_CAUSE': "A cause is a person's words: text drafted with AI is never a root cause.",
  'errors.REQUEST_NOT_DRAFT': "Only a request not yet sent takes a covering letter or a line's wording.",
  'errors.DRAFT_CHANGED': "The draft changed while its second take was read; nothing was recorded. Ask again.",
  'errors.SECOND_TAKE_FLAG_DECIDED': "This flag is decided already.",
  'errors.SECOND_TAKE_FOUR_EYES': "You ran or accepted this draft, so another lead or reviewer decides its flags.",
  'errors.SECOND_TAKE_FLAGS_OPEN': "A flag of a second take on a draft this deliverable prints is open: decide it first.",
  'errors.SECOND_TAKE_NOT_A_DRAFT': "A second take reads an AI draft that stands: a finding text, a board pack's prose or a module's draft.",
  'errors.FINDING_TEXT_WRITTEN': "This finding has an effect or a recommendation already; clear them first if the draft should take their place.",
  'errors.BOARD_PROSE_WRITTEN': "This board pack has an introduction or observations already; clear them first if the draft should take their place.",
  'errors.COVER_LETTER_WRITTEN': "This request has a covering letter already; clear it first if the draft should take its place.",
  'errors.LINE_WORDING_WRITTEN': "This line has its wording already; clear it first if the draft should take its place.",
  // AI-C (2026-10-11): a client's data through OpenRouter, the client's permission, the routes, the statement v2.
  'aic.title': "AI on this client's data through OpenRouter",
  'aic.intro': "Besides Claude, this client's data may go to a model through OpenRouter only where the client's latest signed permission names the model, a lead has switched its route on, and a second person, a lead or a reviewer, has confirmed it. The AI allowance above is still needed: a route never switches AI on. Data about the client's customers never goes this way.",
  'aic.switchOff': "This installation does not send a client's data to OpenRouter (OLIVIA_CLIENT_DATA_OPENROUTER is off). The routes below stay off.",
  'aic.routes': "Routes, one per model",
  'aic.state.off': "Off",
  'aic.state.waiting': "Waiting for a second person to confirm",
  'aic.state.in_force': "In force",
  'aic.state.not_named': "Not in force: the client's latest permission does not name this model",
  'aic.state.ended': "Switched off",
  'aic.provider': "Provider: {provider}, fixed; zero data retention; no fallback to another provider.",
  'aic.proposedBy': "{code}: switched on by {name} on {date}.",
  'aic.confirmedBy': "Confirmed by {name} on {date}.",
  'aic.endedBy': "Switched off by {name} on {date}.",
  'aic.propose': "Switch the route on",
  'aic.confirm': "Confirm the route",
  'aic.end': "Switch the route off",
  'aic.endReason': "Why it is switched off (at least 20 characters)",
  'aic.block.not_named': "The client's latest permission does not name this model.",
  'aic.block.own_proposal': "You switched this on, so a second person confirms it.",
  'aic.block.not_lead_or_reviewer': "A lead or a reviewer of this client confirms it.",
  'aic.block.switch_off': "The installation does not send a client's data to OpenRouter, so this cannot be confirmed now.",
  'aic.receipt.title': "The client's permission",
  'aic.receipt.latest': "{code}, signed on {date} by {name} ({role}); the document {document}.",
  'aic.receipt.models': "Models the client permits: {models}.",
  'aic.receipt.noModels': "It permits no model through OpenRouter.",
  'aic.receipt.none': "No permission recorded yet.",
  'aic.receipt.record': "Record the permission",
  'aic.receipt.signedOn': "Signed on",
  'aic.receipt.signer': "Signed by, at the client",
  'aic.receipt.signerRole': "Signed as (the signer's role)",
  'aic.receipt.document': "The signed document",
  'aic.receipt.modelsLegend': "Models the client permits",
  'aic.receipt.modelBox': "We permit {model} via OpenRouter, EU ({provider}), zero retention",
  'aic.receipt.noneNote': "A permission that ticks none withdraws every route: the latest permission decides.",
  'aic.processors': "OpenRouter, Inc. (United States) is the intermediary and the model provider behind it the sub-processor, both under the data processing agreement (GDPR Article 28); the client keeps the arrangement in its register of information (DORA Article 28(3)).",
  'aiRun.model.openrouter': "{model} via OpenRouter ({provider}, zero data retention)",
  'modules.boundary.routes': "This client's permission and its routes in force also let a run go to {models} through OpenRouter, each fixed to its provider with zero data retention.",
  'explain.provider.h': "Provider",
  'explain.provider': "Provider: {provider}; zero data retention; data collection denied; no fallback to another provider.",
  'explain.provider.other': "Provider: {provider}.",
  'errors.CLIENT_ROUTE_PERMISSION': "The client's latest permission does not name this model, so its route cannot be switched on or confirmed.",
  'errors.CLIENT_ROUTE_STATE': "This route is already switched on, confirmed or switched off.",
  'errors.CLIENT_ROUTE_FOUR_EYES': "You switched this route on, so a second person confirms it.",
  'errors.CLIENT_ROUTES_OFF': "This installation does not send a client's data to OpenRouter (OLIVIA_CLIENT_DATA_OPENROUTER is off).",
  'errors.CLIENT_ROUTE_NOT_IN_FORCE': "This model may not read this client's data: no route for it is in force here. Nothing was sent.",
  'errors.PERMISSION_SIGNED_LATER': "The day the permission was signed is after today.",
  'errors.REASONING_ONLY': "The model used its whole answer on reasoning and gave no answer. Nothing was kept; try again, or choose another model.",
  'auditAction.ai_permission.record': "Recorded the client's permission for AI through OpenRouter",
  'auditAction.ai_route.propose': "Switched on a route to an AI model through OpenRouter",
  'auditAction.ai_route.confirm': "Confirmed a route to an AI model through OpenRouter",
  'auditAction.ai_route.end': "Switched off a route to an AI model through OpenRouter",
  'record.aiPermission': "AI permission",
  'record.aiRoute': "AI route",
  'auditField.signedOn': "signed on",
  'auditField.signer': "signed by, at the client",
  'auditField.signerRole': "signed as",
  'auditField.permittedModels': "models the client permits",
  'auditField.permission': "the client's permission",
  'auditField.provider': "provider",
  'auditField.switchedOnBy': "switched on by",
  'auditField.switchedOffBy': "switched off by",
  'auditField.switchedOffAt': "switched off on",
  'privacy.ai.v2': "A client's data, including its documents, goes to Anthropic (Claude, through its API) only where that client has allowed it. It may also go to one of three models through OpenRouter (Mistral Large 4, GLM 5.3 or GLM 5.3 Flash), but only where this installation allows it, the client's signed permission names the model, and two people of the firm have switched its route on and confirmed it. Data about the client's customers goes only to Claude, and only where, in addition, the client's recorded basis allows it. Nothing that reveals a suspicion goes to any model.",
  'privacy.processors.h': "Processors behind the AI",
  'privacy.processors': "For a model through OpenRouter, OpenRouter, Inc., in the United States, is the intermediary; behind it the model is run by Mistral AI's EU endpoint (Mistral Large 4) or by Inceptron (GLM 5.3 and GLM 5.3 Flash), each fixed in code with zero data retention, no data collection and no fallback to another provider. They act as sub-processors, authorised by the client and bound by the same obligations under a data processing agreement (GDPR Article 28(2) and (4)); the transfer to the United States rests on that agreement's safeguards (GDPR Chapter V).",
  'privacy.outsourcing.h': "Outsourcing and ICT third parties",
  'privacy.outsourcing': "For a client in the financial sector, the use of these services is part of its outsourcing and ICT third-party arrangements (the EBA Guidelines on outsourcing arrangements, EBA/GL/2019/02, and Finansinspektionen's rules); the client records it in its register of information on contractual arrangements with ICT third-party service providers (DORA, Regulation (EU) 2022/2554, Article 28(3)). We give the client what it needs for that register.",
  // ── H3b-6: Ask Olivia, the guide built into Olivia ───────────────────────────────────────────────────────────
  'help.bar': 'Ask Olivia…',
  'help.bar.input': 'Your question for Olivia',
  'help.title': 'Ask Olivia',
  'help.subtitle': 'The guide built into Olivia',
  'help.tabs': 'Ask Olivia: sections',
  'help.tab.ask': 'Ask',
  'help.tab.how': 'How it works',
  'help.tab.where': 'Where do I…',
  'help.size.toCompact': 'Smaller panel',
  'help.size.toLarge': 'Larger panel',
  'help.intro': 'I’m the guide built into Olivia. I answer from Olivia’s guide and say which parts I used. I don’t do anything for you: I change, approve and confirm nothing, and I read no client’s records.',
  'help.question': 'Your question',
  'help.questionHint': 'For example: “Why can I not approve the item I prepared?” Please don’t type personal data or a client’s details.',
  'help.send': 'Ask',
  'help.asking': 'Looking in the guide…',
  'help.notice.ai': 'Answers are written by {model} from Olivia’s guide. Your question is not sent to the model and not stored: I match it to the guide here, and only the matching parts of the guide are sent.',
  'help.notice.openrouter': 'OpenRouter is a company in the United States; it passes the text only to Inceptron, in the EU, with zero data retention.',
  'help.notice.anthropic': 'It goes through the Anthropic API, the route a client’s data takes.',
  'help.notice.none': 'AI answers are not set up on this server, so I answer with the guide’s own words, found by keyword match. Your question is not stored.',
  'help.answer.ai': 'Answer written by {model}',
  'help.answer.keywords': 'The guide’s own words',
  'help.from': 'From:',
  'help.seeAlso': 'See also:',
  'help.openScreen': 'Open {name}',
  'help.reason.not_configured': 'AI answers are not set up on this server, so this is what the guide says, found by keyword match.',
  'help.reason.rate_limited': 'You have reached this hour’s limit of AI answers, so this is what the guide says, found by keyword match.',
  'help.reason.unavailable': 'The AI answer could not be made just now, so this is what the guide says, found by keyword match.',
  'help.reason.spend_cap': 'The AI spending limit on this server has been reached, so this is what the guide says, found by keyword match.',
  'help.reason.uncited': 'I set the AI answer aside because it did not name the parts of the guide it rests on, so this is what the guide says.',
  'help.reason.figure': 'I set the AI answer aside because it stated a figure, which only a screen may state, so this is what the guide says.',
  'help.reason.overstated': 'I set the AI answer aside because it claimed more than the guide supports, so this is what the guide says.',
  'help.none': 'I could not match your question to the guide. Try other words, or open How it works.',
  'help.refused.personal_data': 'Your question looks as if it holds personal data, such as a personal identity number, an account number, an e-mail address or a phone number. I did not send or keep it. Please ask again without it.',
  'help.refused.credentials': 'Your question looks as if it holds a password, a key or a token. I did not send or keep it. Please ask again without it, and change that secret if it is real.',
  'help.cite.step': 'step {n}, {title}',
  'help.cite.faq': 'question: {title}',
  'help.cite.glossary': 'glossary: {title}',
  'help.cite.screen': 'screen: {title}',
  'help.cite.ai': 'what AI does and never does',
  'help.cite.brief': 'Olivia’s brief',
  'help.how.current': 'You are here',
  'help.how.step': 'Step {n}: {title}',
  'help.how.youDo': 'You do',
  'help.how.itDoes': 'Olivia does',
  'help.how.youDecide': 'You decide',
  'help.how.aiDoes': 'What AI does here',
  'help.how.aiNever': 'What AI never does here',
  'help.how.pick': 'Diagram',
  'help.how.diagramsIntro': 'How Olivia works, drawn. Choose a box to see what it is, where it is in Olivia and which rule card applies.',
  'help.how.asText': 'Olivia in the guide’s words',
  'help.how.diagramsUnavailable': 'The diagrams cannot be read on this server just now, so here is the guide as text.',
  'help.draft': 'This guide is a draft, version {version}, for review.',
  'help.diagram.start': 'Walk through the steps',
  'help.diagram.prev': 'Previous step',
  'help.diagram.next': 'Next step',
  'help.diagram.position': 'Step {n} of {total}: {label}',
  'help.diagram.count': '{n} steps, in order',
  'help.diagram.count.one': 'One step',
  'help.diagram.choose': 'Choose a box in the drawing to see what it is, where it is in Olivia and which rule card applies.',
  'help.diagram.where': 'Where it is in Olivia',
  'help.diagram.rules': 'Rule cards that apply',
  'help.diagram.noRule': 'No rule card applies to this box: people decide here, or the screen says what holds.',
  'help.diagram.noWhere': 'This box is not one screen: it holds wherever it applies.',
  'help.diagram.key': 'Key to the shapes',
  'help.diagram.list': 'The diagram as a list',
  'help.diagram.badge': 'step {n}',
  'help.diagram.kind.step': 'A step',
  'help.diagram.kind.decision': 'A person decides',
  'help.diagram.kind.person': 'A person or a role',
  'help.diagram.kind.ai': 'AI proposes',
  'help.diagram.kind.rule': 'Code or a rule decides',
  'help.diagram.kind.data': 'Data',
  'help.diagram.kind.external': 'Outside Olivia',
  'help.diagram.kind.output': 'What leaves Olivia',
  'help.needs.client': 'Open a client first.',
  'help.needs.assessment': 'Open one of the client’s analyses first.',
  'help.needs.practice': 'Open a practice of an analysis first.',
  'help.needs.record': 'Open the record from its list first.',
  'help.screen.newClient': 'New client',
  'help.screen.onboarding': 'Setting up the client',
  'help.screen.assessment': 'The analysis',
  'help.screen.practice': 'A practice of the analysis',
  'help.screen.nextSteps': 'Next steps for a gap',
  'help.screen.interview': 'An interview',
  'help.screen.request': 'A request to the client',
  'help.screen.action': 'An action',
  'help.screen.handoff': 'A hand-off',
  'help.where.intro': 'Every screen you can open, as the menu has them, with the question each answers.',
  'help.where.noClient': 'You are not in a client now: a client’s screens open once you choose a client.',
  'help.where.portfolio': 'Across your clients',
  'help.where.clientWork': 'A client, in Work',
  'help.where.clientExpert': 'A client, in Expert',
  'help.fault.missing': 'a file of the guide is missing',
  'help.fault.sums': 'a file of the guide does not match its locked checksum',
  'help.fault.not_json': 'a file of the guide cannot be read',
  'help.fault.format': 'a file of the guide is not in its format',
  'help.fault.topics': 'Olivia’s topics do not fit the guide',
  'help.fault.rule_card': 'a diagram names a rule card that does not exist',
  'errors.HELP_TOO_MANY': 'You have asked many questions this hour. Please try again later.',
  'errors.HELP_GUIDE_UNAVAILABLE': 'The guide cannot be read on this server just now: {reason}.',
  'errors.HELP_DIAGRAMS_UNAVAILABLE': 'The diagrams cannot be read on this server just now: {reason}.',
  // IP15f: questionnaires with self-ratings, and the perception gap.
  'engine.nav.questionnaires': 'Questionnaires',
  'questionnaires.title': 'Questionnaires and self-ratings',
  'questionnaires.intro': 'A questionnaire asks the client’s staff how far each practice in scope is in place, on the five levels. What comes back is recorded as self-ratings only: it is shown beside the evidenced levels and never changes a level, a fact or a gap.',
  'questionnaires.maps': 'Maps in this analysis',
  'questionnaires.noMaps': 'No map is in the scope of this analysis yet.',
  'questionnaires.inScope': '{n} statements for the practices in scope',
  'questionnaires.adopted': 'Questionnaire adopted by the regulatory desk',
  'questionnaires.notAdopted': 'Questionnaire not yet adopted by the regulatory desk: the file says it is a draft',
  'questionnaires.noneForMap': 'No questionnaire for this version of the map',
  'questionnaires.issueTitle': 'Issue a questionnaire',
  'questionnaires.issueIntro': 'One statement per practice in scope, in the language you choose. The file is an Excel workbook the client fills in and sends back.',
  'questionnaires.language': 'Language of the file',
  'questionnaires.issue': 'Issue the questionnaire',
  'questionnaires.issued': 'Questionnaire {code} is issued with {n} statements. Download it below.',
  'questionnaires.issueClosed': 'A questionnaire is issued while the analysis is gathering or assessing, by a lead, an analyst or a reviewer.',
  'questionnaires.noIssues': 'No questionnaire is issued for this analysis yet.',
  'questionnaires.issuesLabel': 'Issued questionnaires',
  'questionnaires.col.issue': 'Questionnaire',
  'questionnaires.col.language': 'Language',
  'questionnaires.col.statements': 'Statements',
  'questionnaires.col.issuedBy': 'Issued by',
  'questionnaires.col.file': 'File',
  'questionnaires.download': 'Download the workbook',
  'questionnaires.draftMarked': 'Marked as a draft in the file',
  'questionnaires.returnsTitle': 'Answered files',
  'questionnaires.uploadIntro': 'Upload the workbook as it came back. It is read strictly: a changed statement, another version, a formula or an answer off the scale refuses the whole file, and nothing of it is kept. The file itself is not stored; its answers are.',
  'questionnaires.file': 'Answered workbook (.xlsx, at most 2 MB)',
  'questionnaires.statementNone': 'This file holds no customer-level data.',
  'questionnaires.upload': 'Upload the answers',
  'questionnaires.recorded': 'Recorded as {code}: {n} answers kept as self-ratings, {m} statements left empty.',
  'questionnaires.noReturns': 'No answered file is uploaded yet.',
  'questionnaires.returnsLabel': 'Answered files',
  'questionnaires.col.return': 'Answered file',
  'questionnaires.col.answeredBy': 'Answered by, as the file states',
  'questionnaires.col.answered': 'Answers',
  'questionnaires.col.uploadedBy': 'Uploaded by',
  'questionnaires.col.state': 'State',
  'questionnaires.answeredOn': 'answered {day}',
  'questionnaires.noDay': 'no day stated',
  'questionnaires.answeredCount': '{n} self-ratings',
  'questionnaires.retired': 'Retired: {reason}',
  'questionnaires.retire': 'Retire this file’s answers',
  'questionnaires.retireReason': 'Why are they retired?',
  'questionnaires.current': 'Current',
  'perception.title': 'Self-ratings beside the evidence',
  'perception.intro': 'Each self-rating beside the level the evidence supports. Where there is no evidenced level yet, it says so: a self-rating is never compared with a blank, never averaged, and never changes a level.',
  'perception.provisional': 'The levels are computed from what is recorded now, and are provisional until the analysis is frozen.',
  'perception.frozen': 'The levels are the frozen analysis’s record.',
  'perception.empty': 'No self-rating is recorded yet.',
  'perception.col.practice': 'Practice',
  'perception.col.evidenced': 'Evidenced level',
  'perception.col.selfRated': 'Self-ratings',
  'perception.reading.no_evidence': 'No evidence yet',
  'perception.reading.not_in_scope': 'Not in this analysis now',
  'perception.reading.above': 'Rated {n} above the evidence',
  'perception.reading.same': 'Same as the evidence',
  'perception.reading.below': 'Rated {n} below the evidence',
  'errors.QUESTIONNAIRE_FILE_TOO_LARGE': 'A questionnaire file may be at most {mb} MB.',
  'errors.QUESTIONNAIRE_NOT_XLSX': 'This file is not an Excel workbook (.xlsx). Upload the questionnaire as it was sent, saved as .xlsx.',
  'errors.QUESTIONNAIRE_ZIP_REFUSED': 'This workbook holds something a questionnaire never does (macros, links to other files, embedded objects), or is built to be far larger than it looks. It was not read.',
  'errors.QUESTIONNAIRE_UNKNOWN': 'This file is not a questionnaire issued for this client. Upload the file that was sent from here.',
  'errors.QUESTIONNAIRE_OTHER_ANALYSIS': 'This questionnaire was issued for another analysis of this client. Upload it there.',
  'errors.QUESTIONNAIRE_VERSION': 'This file is another version of the questionnaire than the one issued. Send the client the issued file again.',
  'errors.QUESTIONNAIRE_EDITED': 'The questionnaire was changed: a statement, a sheet or a heading is not as issued ({where}). Only the answers, the comments and who answered may be filled in.',
  'errors.QUESTIONNAIRE_FORMULA': 'The file holds a formula (sheet {sheet}, cell {cell}). Answers are typed or chosen from the list, never computed: remove the formula and upload again.',
  'errors.QUESTIONNAIRE_OFF_SCALE': 'The answer to {practice} is not on the scale. Choose 0 to 4 from the list, or leave it empty.',
  'errors.QUESTIONNAIRE_COMMENT': 'The comment on {practice} cannot be kept: a comment is text of at most 1,000 characters, beside an answer.',
  'errors.QUESTIONNAIRE_TEXT_UNSAFE': 'A comment or who answered starts with =, +, -, @ or a tab, which a spreadsheet could run as a formula. Start the text with a letter or a digit.',
  'errors.QUESTIONNAIRE_RESPONDENT': 'Who answered is not filled in as asked: a role is needed, a name and a role are at most 200 characters, and the date is a day between the issue and today.',
  'errors.QUESTIONNAIRE_EMPTY': 'No statement is answered in this file.',
  'errors.QUESTIONNAIRE_RETURNED_ALREADY': 'This file is recorded already, as {code}.',
  'errors.QUESTIONNAIRE_NOTHING_TO_ASK': 'No practice in the scope of this analysis has a questionnaire yet.',
  'errors.QUESTIONNAIRE_RETURN_RETIRED': 'These answers are retired already.',
  'auditField.questionnaires': 'questionnaires',
  'auditField.questionnaireAdopted': 'adopted by the regulatory desk',
  'auditField.issuedBy': 'issued by',
  'auditField.issuedAt': 'issued',
  'auditField.respondentRole': 'role of who answered',
  'auditField.answeredOn': 'answered on',
  'exportKind.questionnaire': 'a questionnaire for the client',
  'auditAction.export.questionnaire': 'Downloaded a questionnaire for the client',
  'auditAction.questionnaire.issue': 'Issued a questionnaire',
  'auditAction.questionnaire.return': 'Recorded the answers of a questionnaire as self-ratings',
  'auditAction.questionnaire.retire_return': 'Retired the answers of a questionnaire',
  'record.questionnaireIssue': 'Questionnaire issued',
  'record.questionnaireReturn': 'Answered questionnaire',
  'record.selfRating': 'Self-rating',
} as const

export type MessageKey = keyof typeof en
